import React, { useState, useEffect, useRef, useCallback, memo, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import toast from '../components/common/toast';
import * as XLSX from 'xlsx';
import { outwardService, productService, companyService } from '../services/api';
import { IoMdArrowDropdown } from 'react-icons/io';
import { FiDownload, FiX } from "react-icons/fi";
import { parseISO, startOfMonth } from "date-fns";
import { DashboardDateRangePopup } from "./DashboardDemo";
import { Table, Badge, EditIcon, DeleteIcon, FormPopup, DeletePopUp, UploadPreviewPopUp, OutwardDetailsModal, } from '../components/common';
import SkeletonTable from '../components/SkeletonTable';
import { parseFileToPreview } from '../utils/uploadPreviewParser';
import { downloadOutwardUploadTemplate } from '../utils/downloadOutwardUploadTemplate';
import { downloadOutwardProductsTemplate } from '../utils/downloadOutwardProductsTemplate';
import { exportOutwardReportPdfFromExcelBlob } from '../utils/exportOutwardReportPdf';
import { useOutwardBackdatePermission } from '../hooks/useOutwardBackdatePermission';
import OutwardRequestAccessModal from '../components/outward/OutwardRequestAccessModal';
import {
  createPostingDateSelectableChecker,
  extractInvoiceDatesFromUploadRows,
  findDisallowedBulkInvoiceDates,
  formatApprovedPermissionsForDisplay,
  getInvoiceDatePickerBoundsIST,
  isPostingDateAllowed,
  normalizeYmdIST,
  requiresBackdatePermission,
  todayYmdIST,
} from '../utils/outwardBackdatePermission';

// Item-level status (items[].status in DB); transaction-level is computed from these
const OUTWARD_ITEM_STATUS_OPTIONS = ['PENDING', 'DISPATCHED', 'CANCELLED'];

const getStatusVariant = (status) => {
  switch (status) {
    case 'DISPATCHED':
      return 'success';
    case 'PARTIALLY_DISPATCHED':
      return 'info';
    case 'CANCELLED':
      return 'danger';
    case 'PENDING':
      return 'warning';
    default:
      return 'secondary';
  }
};

const fmtISTYMD = (v) => {
  if (!v) return '—';
  const d = typeof v === 'string' ? new Date(v) : new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
};

const fmtISTTimeHM = (v) => {
  if (!v) return '—';
  const d = typeof v === 'string' ? new Date(v) : new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    return d.toISOString().slice(11, 16);
  }
};

/** Outward qty inputs: digits only; null = reject (no rounding). */
function coerceOutwardWholeNumberQtyInput(value) {
  if (value === '' || value == null) return '';
  const s = String(value).trim();
  if (!/^\d+$/.test(s)) return null;
  return s;
}

function outwardQtyRawIsValidWholeNumber(raw, { allowZero = true } = {}) {
  if (raw == null || raw === '') return false;
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw) || !Number.isInteger(raw)) return false;
    return allowZero ? raw >= 0 : raw > 0;
  }
  const coerced = coerceOutwardWholeNumberQtyInput(String(raw).trim());
  if (coerced === null || coerced === '') return false;
  const n = Number(coerced);
  return allowZero ? n >= 0 : n > 0;
}

function parseOutwardWholeNumberQtyInput(value) {
  const coerced = coerceOutwardWholeNumberQtyInput(value);
  if (coerced === null) return null;
  if (coerced === '') return 0;
  return Number(coerced);
}

function preventOutwardQtyDecimalKeyDown(e) {
  if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) {
    e.preventDefault();
  }
}

function blockInvalidOutwardQtyPaste(e) {
  const text = e.clipboardData.getData('text') || '';
  const t = String(text).trim();
  if (t !== '' && !/^\d+$/.test(t)) {
    e.preventDefault();
  }
}

const dateInputToIsoUtc = (dateInput) => {
  const raw = String(dateInput || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return "";
  return `${raw}T00:00:00.000Z`;
};

/** Visible Outward form header fields — all mandatory (secondary company is conditional). */
const OUTWARD_REQUIRED_FIELDS = [
  { name: 'invoiceDate', label: 'Posting Date', message: 'Posting Date is required.' },
  { name: 'vehicleNumber', label: 'Vehicle Number', message: 'Please enter the vehicle number.' },
  { name: 'vehicleTemperature', label: 'Vehicle Temperature', message: 'Please enter the vehicle temperature.' },
  { name: 'reportingDate', label: 'Reporting Date', message: 'Please select the reporting date.' },
  { name: 'reportingTime', label: 'Reporting Time', message: 'Please select the reporting time.' },
  { name: 'dockNumber', label: 'Dock Number', message: 'Please enter the dock number.' },
  { name: 'dockInTime', label: 'Dock In Time', message: 'Please select the dock in time.' },
  { name: 'dockOutTime', label: 'Dock Out Time', message: 'Please select the dock out time.' },
  { name: 'productTemperature', label: 'Product Temperature', message: 'Please enter the product temperature.' },
  { name: 'companies', label: 'Company', message: 'Please select a company.' },
  { name: 'method', label: 'Method', message: 'Please select a method.' },
];

const isBlankOutwardField = (value) => {
  if (value == null) return true;
  if (Array.isArray(value)) {
    return (
      value.length === 0 ||
      value.every((v) => v == null || String(v).trim() === '')
    );
  }
  if (typeof value === 'number') return Number.isNaN(value);
  return String(value).trim() === '';
};

const validateOutwardRequiredFields = (data, { requireSecondary = false } = {}) => {
  const nextErrors = {};
  for (const { name, message } of OUTWARD_REQUIRED_FIELDS) {
    if (isBlankOutwardField(data?.[name])) {
      nextErrors[name] = message;
    }
  }
  if (requireSecondary && isBlankOutwardField(data?.secondaryCompanies)) {
    nextErrors.secondaryCompanies = 'Secondary Company is required.';
  }
  return nextErrors;
};

/** Single company id from form state (string or legacy array). */
const getSelectedCompanyId = (companies) => {
  if (Array.isArray(companies)) {
    const first = companies[0];
    const id = first?._id || first?.id || first?.value || first;
    return id != null && String(id).trim() !== '' ? String(id).trim() : '';
  }
  if (companies == null) return '';
  return String(companies).trim();
};

/** API still expects companies as an array of one id. */
const toCompaniesPayload = (companies) => {
  const id = getSelectedCompanyId(companies);
  return id ? [id] : [];
};

/** Collapse item-level API rows into one display row per outward transaction. */
function groupOutwardListingRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const groups = new Map();
  const order = [];

  for (const row of rows) {
    const key = String(row?.transactionId ?? row?._id ?? row?.id ?? '');
    if (!key) continue;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key).push(row);
  }

  return order.map((key) => {
    const lineItems = groups.get(key);
    if (lineItems.length === 1) {
      return { ...lineItems[0], _productCount: 1, _lineItems: lineItems };
    }
    return {
      ...lineItems[0],
      _productCount: lineItems.length,
      _lineItems: lineItems,
    };
  });
}

/** Transaction/list-row checks for pick list, DO, and dispatch actions. */
const validateOutwardActionRow = (row) => {
  if (!row || typeof row !== 'object') {
    return 'Transaction data is required.';
  }
  if (isBlankOutwardField(row.invoiceDate)) return 'Posting Date is required.';
  if (isBlankOutwardField(row.vehicleNumber)) return 'Vehicle Number is required.';
  if (isBlankOutwardField(row.companies)) return 'Company is required.';
  const sku =
    row?.productId?.sku ??
    row?.productSku ??
    row?.sku ??
    '';
  if (isBlankOutwardField(sku) && isBlankOutwardField(row.productId)) {
    return 'Item Code is required.';
  }
  const qty = Number(row.dispatchQuantity);
  if (!Number.isFinite(qty) || qty <= 0) return 'Quantity is required.';
  return null;
};

function toDateInputValue(value) {
  if (!value) return todayYmdIST();
  const ymd = normalizeYmdIST(value);
  return ymd || todayYmdIST();
}

function resolvePostingYmdForPermissionModal(value) {
  if (value == null || value === "") return "";
  return normalizeYmdIST(value);
}

const OUTWARD_ACTIONS_MENU_MIN_WIDTH = 160;

const OutwardRowActionsMenuIcon = ({ className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={`w-5 h-5 ${className}`}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
    />
  </svg>
);

function OutwardRowActionsMenu({ row, actions = [] }) {
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      if (menuRef.current && menuRef.current.contains(event.target)) return;
      setOpen(false);
      setAnchorRect(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleScroll = () => {
      setOpen(false);
      setAnchorRect(null);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [open]);

  if (!actions.length) return null;

  const toggleMenu = (e) => {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      setAnchorRect(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setAnchorRect(rect);
    setOpen(true);
  };

  const menuStyle = anchorRect
    ? (() => {
        const spaceBelow = window.innerHeight - anchorRect.bottom;
        const openAbove = spaceBelow < 220;
        return {
          position: 'fixed',
          left: Math.max(
            8,
            Math.min(
              anchorRect.right - OUTWARD_ACTIONS_MENU_MIN_WIDTH,
              window.innerWidth - OUTWARD_ACTIONS_MENU_MIN_WIDTH - 8
            )
          ),
          width: OUTWARD_ACTIONS_MENU_MIN_WIDTH,
          zIndex: 99999,
          ...(openAbove
            ? { bottom: window.innerHeight - anchorRect.top + 4 }
            : { top: anchorRect.bottom + 4 }),
        };
      })()
    : null;

  return (
    <div className="relative flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={toggleMenu}
        className="wms-table-action-btn flex h-9 w-9 items-center justify-center rounded-lg border-0 bg-transparent active:scale-[0.98] transition"
        aria-label="More actions"
      >
        <OutwardRowActionsMenuIcon />
      </button>
      {open && anchorRect &&
        createPortal(
          <div
            ref={menuRef}
            className="min-w-[160px] rounded-xl border border-gray-200 bg-white shadow-lg py-1"
            style={menuStyle}
          >
            {actions.map((action, actionIndex) => {
              const IconComponent = action.icon;
              const isDisabled = Boolean(action.disabled);
              return (
                <button
                  key={actionIndex}
                  type="button"
                  disabled={isDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isDisabled) return;
                    action.onClick(row);
                    setOpen(false);
                    setAnchorRect(null);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 transition hover:bg-gray-50 first:rounded-t-xl last:rounded-b-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {IconComponent && (
                    <span className="text-gray-600">
                      <IconComponent className="w-4 h-4" />
                    </span>
                  )}
                  <span>{action.label}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}

function OutwardStatusCell({ status, transactionId, itemId, onSelectNewStatus, getStatusVariant, onOpenModeSelection, currentStatus }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const wrapRef = useRef(null);
  const [pendingMode, setPendingMode] = useState(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setPendingMode(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  if (!status) return '—';
  const isFinal = status === 'DISPATCHED' || status === 'CANCELLED';
  // Only allow status changes from PENDING status
  const otherStatuses = isFinal ? [] : (status === 'PENDING' ? OUTWARD_ITEM_STATUS_OPTIONS.filter((s) => s !== status) : []);

  const handleStatusClick = (e) => {
    e.stopPropagation();
    if (onOpenModeSelection) {
      onOpenModeSelection(transactionId, itemId, currentStatus || status);
    } else {
      setDropdownOpen((o) => !o);
    }
  };

  const handleStatusSelect = (s) => {
    onSelectNewStatus(s);
    setDropdownOpen(false);
    setPendingMode(null);
  };

  return (
    <div className="relative inline-flex items-center gap-1" ref={wrapRef}>
      <Badge variant={getStatusVariant(status)} className="inline-flex items-center gap-1 pr-1">
        <span>{status.replace(/_/g, ' ')}</span>
        {!isFinal && (
        <button
          type="button"
          onClick={handleStatusClick}
          className="p-0.5 rounded hover:bg-black/10 focus:outline-none focus:ring-1 focus:ring-offset-0"
          aria-label="Change status"
        >
          <IoMdArrowDropdown className="w-4 h-4" />
        </button>
        )}
      </Badge>
      {dropdownOpen && otherStatuses.length > 0 && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-[200px] rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
          <ul className="py-1 list-none" role="listbox">
            {otherStatuses.map((s) => (
              <li key={s} role="option">
                <button
                  type="button"
                  onClick={() => handleStatusSelect(s)}
                  className="w-full block px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                >
                  {s.replace(/_/g, ' ')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function getBatchAvailableQty(batch) {
  const value =
    batch?.availableQty ??
    batch?.availableQuantity ??
    batch?.available ??
    batch?.remainingQty ??
    batch?.qtyAvailable;
  const num = Number(value);
  if (Number.isFinite(num)) return num;
  return Number(batch?.quantity ?? 0) || 0;
}

function getBatchDisplayAvailableQty(batch) {
  if (batch?.catalogAvailableQty != null) {
    const catalogQty = Number(batch.catalogAvailableQty);
    if (Number.isFinite(catalogQty)) return catalogQty;
  }
  return getBatchAvailableQty(batch);
}

function getBatchBinCode(batch, product) {
  const bid = String(batch?.binId?._id || batch?.binId || '');
  if (!bid) return batch?.binCode || '—';
  const bin = (product?.bins || []).find(
    (b) => String(b.binId || b._id || '') === bid
  );
  return bin?.binCode || batch?.binCode || '—';
}

function formatAllocationDisplayDate(value) {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : new Date(value);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function manualBatchIdentityKey(productId, batch) {
  return [
    String(productId ?? ''),
    String(batch?.batchNumber ?? ''),
    formatAllocationDisplayDate(batch?.manufacturingDate),
    formatAllocationDisplayDate(batch?.expiryDate),
  ].join('\u0000');
}

function groupIdenticalManualBatches(productId, batches) {
  const groups = [];
  const indexByKey = new Map();
  for (const batch of batches || []) {
    const key = manualBatchIdentityKey(productId, batch);
    const existing = indexByKey.get(key);
    if (existing == null) {
      indexByKey.set(key, groups.length);
      groups.push({ key, members: [batch] });
    } else {
      groups[existing].members.push(batch);
    }
  }
  return groups;
}

function distributeGroupedManualQty(members, rawValue, dispatchQty) {
  const parsed = parseOutwardWholeNumberQtyInput(rawValue);
  if (parsed === null) return {};
  const requested = Math.max(0, parsed);
  const capacity = (members || []).reduce((sum, member) => sum + getBatchAvailableQty(member), 0);
  let remaining = Math.min(requested, Math.min(capacity, Math.max(0, Number(dispatchQty) || 0)));
  const allocations = {};
  for (const member of members || []) {
    const memberId = String(member.batchId || member._id);
    const give = Math.min(getBatchAvailableQty(member), remaining);
    allocations[memberId] = give;
    remaining -= give;
  }
  return allocations;
}

function mapBatchesFromProductApi(batches) {
  return filterOutwardAvailableBatches((batches || []).map((b) => ({
    batchId: b._id,
    batchNumber: b.batchNumber,
    expiryDate: b.expiryDate,
    manufacturingDate: b.manufacturingDate,
    availableQty: b.quantity ?? 0,
    quantity: b.quantity ?? 0,
    binId: b.binId,
  })));
}

async function enrichBatchesWithCatalogAvailableQty(productId, batches) {
  const list = Array.isArray(batches) ? batches : [];
  try {
    const res = await outwardService.getBatchesByProduct(productId);
    const catalog = mapBatchesFromProductApi(Array.isArray(res?.data) ? res.data : []);
    const catalogById = new Map(
      catalog.map((b) => [String(b.batchId || b._id), getBatchAvailableQty(b)])
    );
    return list.map((b) => ({
      ...b,
      catalogAvailableQty: catalogById.get(String(b.batchId || b._id)) ?? 0,
    }));
  } catch {
    return list.map((b) => ({ ...b, catalogAvailableQty: 0 }));
  }
}

async function enrichFefoBatchesWithCatalogAvailableQty(productId, fefoApiBatches) {
  const mapped = mapFefoRecommendationBatches(fefoApiBatches);
  return enrichBatchesWithCatalogAvailableQty(productId, mapped);
}

function getEditBatchMaxAllocQty(batch) {
  const reserved = Number(batch?.originalReservedQty) || 0;
  return getBatchDisplayAvailableQty(batch) + reserved;
}

function buildOutwardEditProductFromItem(item, stockRow = null) {
  const product = item?.productId || {};
  const productId = product._id || item?.productId;
  const onHandStockQty = Number(stockRow?.totalQty) || 0;
  return {
    _id: productId,
    productId,
    sku: product.sku || product.productCode || '',
    productCode: product.sku || product.productCode || '',
    description: item?.productDescription || product.description || '',
    totalQty: onHandStockQty,
    onHandStockQty,
    bins: stockRow?.bins || [],
    companies: product.companies || [],
  };
}

async function buildEditModeBatches(productId, originalBatchDetails = []) {
  const originalMap = {};
  const prefilled = (originalBatchDetails || []).map((b) => {
    const batchId = String(b.batchId?._id || b.batchId || '');
    const qty = Number(b.quantity) || 0;
    if (batchId) originalMap[batchId] = qty;
    return {
      batchId,
      batchNumber: b.batchNumber,
      manufacturingDate: b.manufacturingDate,
      expiryDate: b.expiryDate,
      quantity: qty,
      binId: b.binId?._id || b.binId,
    };
  });

  let catalog = [];
  try {
    const res = await outwardService.getBatchesByProduct(productId);
    catalog = mapBatchesFromProductApi(Array.isArray(res?.data) ? res.data : []);
  } catch (_) {
    catalog = [];
  }

  const byId = new Map();
  for (const batch of catalog) {
    const id = String(batch.batchId || batch._id);
    byId.set(id, {
      ...batch,
      quantity: originalMap[id] ?? 0,
      originalReservedQty: originalMap[id] ?? 0,
    });
  }
  for (const batch of prefilled) {
    const id = String(batch.batchId);
    if (!id) continue;
    if (!byId.has(id)) {
      byId.set(id, {
        ...batch,
        availableQty: originalMap[id] || 0,
        catalogAvailableQty: originalMap[id] || 0,
        originalReservedQty: originalMap[id] || 0,
      });
    } else {
      const cur = byId.get(id);
      byId.set(id, {
        ...cur,
        quantity: batch.quantity,
        originalReservedQty: originalMap[id] || 0,
      });
    }
  }

  const enriched = await enrichBatchesWithCatalogAvailableQty(
    productId,
    Array.from(byId.values())
  );
  return enriched.map((b) => {
    const id = String(b.batchId || b._id);
    return {
      ...b,
      quantity: originalMap[id] ?? 0,
      originalReservedQty: originalMap[id] ?? 0,
    };
  });
}

function getProductRemainingQty(product) {
  return getProductOnHandStockQty(product);
}

function getProductOnHandStockQty(product) {
  if (product?.onHandStockQty != null) {
    return Math.max(0, Number(product.onHandStockQty) || 0);
  }
  return Math.max(0, Number(product?.totalQty) || 0);
}

function getDispatchAllowance(product, originalDispatchQuantity = 0) {
  const onHand = getProductOnHandStockQty(product);
  const originalQty = Number(originalDispatchQuantity) || 0;
  return {
    onHand,
    maxAllowed: originalQty > 0 ? onHand + originalQty : onHand,
  };
}

function formatDispatchStockShortageMessage({
  onHand,
  requested,
  maxAllowed,
  isEdit = false,
}) {
  const requestedQty = Number(requested) || 0;
  const available = Number(onHand) || 0;
  const max = Number(maxAllowed) || 0;
  if (requestedQty <= max) return '';
  const shortage = requestedQty - max;
  let msg = `Insufficient stock. Available Stock: ${available}, Requested Dispatch: ${requestedQty}, Shortage: ${shortage}.`;
  if (isEdit && max > available) {
    msg += ` Max editable for this line (including its reservation): ${max}.`;
  }
  return msg;
}

function getSavedBatchAllocationTotal(batches) {
  return (batches || []).reduce(
    (sum, b) => sum + (Number(b.quantity) || 0),
    0
  );
}

function getEditBatchAllocationValidationError(batches) {
  for (const b of batches || []) {
    const alloc = Number(b.quantity) || 0;
    if (alloc <= 0) continue;
    const max = getEditBatchMaxAllocQty(b);
    if (alloc > max) {
      return `Batch ${b.batchNumber || b.batchId}: allocated ${alloc} exceeds current availability (max ${max}).`;
    }
  }
  return '';
}

function replaceBinIdWithBinCodeInErrorMessage(message, productsList) {
  if (!message || typeof message !== 'string') return message;
  const match = message.match(/Failed to update bin ([a-f0-9]{24})/i);
  if (!match) return message;
  const binId = match[1];
  let binCode = null;
  for (const p of productsList || []) {
    const bin = (p.bins || []).find(
      (b) => String(b.binId || b._id || '') === binId
    );
    if (bin?.binCode) {
      binCode = bin.binCode;
      break;
    }
  }
  if (!binCode) return message;
  return message.replace(binId, binCode);
}

function getAllowedDispatchQty(product, originalDispatchQuantity = 0) {
  return getDispatchAllowance(product, originalDispatchQuantity).maxAllowed;
}

function isProductAvailableForOutward(product) {
  return getProductRemainingQty(product) > 0;
}

function isOutwardItemAllocationLocked(status) {
  const s = String(status || '').toUpperCase();
  return s === 'DISPATCHED' || s === 'PARTIALLY_DISPATCHED' || s === 'CANCELLED';
}

function getOutwardProductId(product) {
  return String(product?._id || product?.productId || "");
}

function getOutwardCompanyKey(companyIds) {
  return (Array.isArray(companyIds) ? companyIds : [])
    .map(String)
    .filter(Boolean)
    .sort()
    .join(",");
}

function zeroOutOutwardProduct(product) {
  return {
    ...product,
    totalQty: 0,
    bins: (product?.bins || []).map((b) => ({
      ...b,
      qty: 0,
      currentQuantity: 0,
    })),
  };
}

const knownOutwardProductsByCompanyRef = { current: new Map() };

function mergeOutwardProductsForCompany(companyKey, incomingProducts) {
  if (!companyKey) {
    return Array.isArray(incomingProducts) ? incomingProducts : [];
  }

  const incoming = Array.isArray(incomingProducts) ? incomingProducts : [];
  const knownMap =
    knownOutwardProductsByCompanyRef.current.get(companyKey) || new Map();

  for (const p of incoming) {
    const id = getOutwardProductId(p);
    if (id) knownMap.set(id, p);
  }

  const incomingById = new Map(
    incoming.map((p) => [getOutwardProductId(p), p])
  );
  const merged = [];

  for (const [id, p] of knownMap) {
    if (!id) continue;
    if (incomingById.has(id)) {
      merged.push(incomingById.get(id));
    } else {
      const zeroed = zeroOutOutwardProduct(p);
      knownMap.set(id, zeroed);
      merged.push(zeroed);
    }
  }

  knownOutwardProductsByCompanyRef.current.set(companyKey, knownMap);

  return merged.sort((a, b) =>
    String(a.sku || a.productCode || "").localeCompare(
      String(b.sku || b.productCode || "")
    )
  );
}

function filterOutwardAvailableBatches(batches) {
  return (batches || []).filter((b) => getBatchAvailableQty(b) > 0);
}

function mapFefoRecommendationBatches(batches) {
  return filterOutwardAvailableBatches((batches || []).map((b) => ({
    batchId: b.batchId || b._id,
    batchNumber: b.batchNumber,
    expiryDate: b.expiryDate,
    manufacturingDate: b.manufacturingDate,
    availableQty: b.quantity ?? 0,
    quantity: b.quantity ?? 0,
    binId: b.binId,
  })));
}

// ❌ OLD: Limited FEFO allocation preview to the first eligible batch only.
// function keepOnlyFirstEligibleFefoBatch(batches) {
//   return Array.isArray(batches) && batches.length > 0 ? [batches[0]] : [];
// }
function keepOnlyFirstEligibleFefoBatch(batches) {
  return filterOutwardAvailableBatches(Array.isArray(batches) ? batches : []);
}

function getFefoAllocatedQty(batches) {
  return (batches || []).reduce((sum, b) => sum + (Number(b.quantity) || 0), 0);
}

function formatOverallStockShortageMessage(requestedQty, availableQty) {
  const requested = Number(requestedQty) || 0;
  const available = Number(availableQty) || 0;
  if (requested <= available) return '';
  const shortage = requested - available;
  return `Insufficient stock. Available Stock: ${available}, Requested Dispatch: ${requested}, Shortage: ${shortage}.`;
}

function formatFefoBatchShortageMessage() {
  return 'Insufficient FEFO stock. Please reduce the dispatch quantity or switch to Manual.';
}

function isFefoSpecificValidationMessage(message) {
  const text = String(message || '').toLowerCase();
  return (
    text.includes('eligible fefo batch') ||
    text.includes('insufficient fefo stock') ||
    text.includes('fefo shortfall') ||
    text.includes('fefo fetch failed') ||
    (text.includes('fefo') && text.includes('switch to manual'))
  );
}

function resolveDispatchShortageMessage({
  method,
  requestedQty,
  totalProductQty,
  maxAllowedQty,
  originalDispatchQuantity = 0,
  isEdit = false,
  batches = [],
}) {
  const requested = Number(requestedQty) || 0;
  const onHand = Number(totalProductQty);
  const maxAllowed = Number(maxAllowedQty);
  if (!Number.isFinite(maxAllowed) || requested <= maxAllowed) return '';

  if (Number.isFinite(onHand)) {
    return formatDispatchStockShortageMessage({
      onHand,
      requested,
      maxAllowed,
      isEdit,
    });
  }

  return formatOverallStockShortageMessage(requested, maxAllowed);
}

function resolveFefoAllocationShortageMessage({
  requestedQty,
  totalProductQty,
  fefoAvailableQty,
  fefoTotalAvailable,
  batches = [],
  shortfall = 0,
}) {
  const requested = Number(requestedQty) || 0;
  const total = Number(totalProductQty) || 0;
  const fefoAvail = Math.max(
    Number(fefoTotalAvailable) || 0,
    Number(fefoAvailableQty) || 0
  );
  if (shortfall <= 0 && requested <= fefoAvail) return null;

  if (requested > total) {
    return formatOverallStockShortageMessage(requested, total);
  }

  return formatFefoBatchShortageMessage();
}

// Memoized batch table for FEFO (read-only)
const FEFOBatchTable = memo(function FEFOBatchTable({ batches }) {
  if (!batches?.length) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="px-2 py-1.5 text-left font-semibold text-gray-800">Batch Number</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-800">Expiry Date</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-800">Available</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-800">Alloc Qty</th>
          </tr>
        </thead>
        <tbody>
          {batches.map((b) => (
            <tr key={b.batchId || b._id} className="border-b border-gray-100">
              <td className="px-2 py-1.5 text-gray-700">{b.batchNumber || '—'}</td>
              <td className="px-2 py-1.5 text-gray-700">{b.expiryDate ? (typeof b.expiryDate === 'string' ? b.expiryDate : new Date(b.expiryDate).toISOString().slice(0, 10)) : '—'}</td>
              <td className="px-2 py-1.5 text-gray-700">{getBatchDisplayAvailableQty(b)}</td>
              <td className="px-2 py-1.5 text-gray-700">{b.quantity ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-gray-500 mt-1">Read-only FEFO allocation</p>
    </div>
  );
});

// Memoized batch table for MANUAL (with allocated qty inputs)
const ManualBatchTable = memo(function ManualBatchTable({ batches, manualSelections, dispatchQty, onQtyChange }) {
  if (!batches?.length) return null;
  const total = Object.values(manualSelections || {}).reduce((s, q) => s + (Number(q) || 0), 0);
  const match = total === dispatchQty;
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="px-2 py-1.5 text-left font-semibold text-gray-800">Batch Number</th>
              <th className="px-2 py-1.5 text-left font-semibold text-gray-800">Expiry Date</th>
              <th className="px-2 py-1.5 text-left font-semibold text-gray-800">Available Qty</th>
              <th className="px-2 py-1.5 text-left font-semibold text-gray-800">Input Allocated Qty</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => {
              const bid = b.batchId || b._id;
              const avail = getBatchAvailableQty(b);
              const currentSel = manualSelections?.[bid] ?? 0;
              const maxSelect = Math.min(avail, dispatchQty);
              return (
                <tr key={bid} className="border-b border-gray-100">
                  <td className="px-2 py-1.5 text-gray-700">{b.batchNumber || '—'}</td>
                  <td className="px-2 py-1.5 text-gray-700">{b.expiryDate ? (typeof b.expiryDate === 'string' ? b.expiryDate : new Date(b.expiryDate).toISOString().slice(0, 10)) : '—'}</td>
                  <td className="px-2 py-1.5 text-gray-700">{avail}</td>
                  <td className="px-2 py-1.5">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={currentSel || ''}
                      onKeyDown={preventOutwardQtyDecimalKeyDown}
                      onPaste={blockInvalidOutwardQtyPaste}
                      onChange={(e) => onQtyChange(bid, e.target.value, maxSelect)}
                      placeholder="0"
                      className="h-8 w-20 rounded border border-gray-200 px-2 text-sm focus:ring-2 focus:ring-emerald-200"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className={`text-sm mt-2 font-medium ${match ? 'text-emerald-600' : 'text-red-600'}`}>
        Allocated total: {total} / Dispatch: {dispatchQty} {match ? '' : ' — must match'}
      </p>
    </>
  );
});

// Memoized product row for create-from-table with expandable batch allocation panel
const ProductRowAllocation = memo(function ProductRowAllocation({
  rowNumber,
  product,
  state,
  globalMethod,
  onDispatchQtyChange,
  onSkuDraftChange,
  onGridPaste,
  onApplyFEFO,
  onApplyManual,
  onApplyManualBatch,
  onManualQtyChange,
  onEditBatchQtyChange,
  onRemoveBatch,
  dispatchQtyError,
}) {
  const id = product._id;
  const [binsExpanded, setBinsExpanded] = useState(false);
  const [allocationOpen, setAllocationOpen] = useState(state.expanded !== false);
  const isEditOutward = !!state.isEditOutward;
  const onHandStockQty = getProductOnHandStockQty(product);
  const outOfStock = !isEditOutward && onHandStockQty <= 0;
  const inputMaxQty = getAllowedDispatchQty(product, state.originalDispatchQuantity);
  const rowInactive = !state.selected;
  const dispatchInputDisabled = isEditOutward ? rowInactive : outOfStock;
  const dispatchQty = Number(state.dispatchQuantity) || 0;
  const method = String(globalMethod || state.allocationMethod || 'FEFO').toUpperCase();
  const isFEFO = method === 'FEFO';
  // Edit Outward switched FEFO → MANUAL uses the same Apply/Remove workflow as Create.
  const useManualCreateWorkflow = !isFEFO && (!isEditOutward || !!state.editManualCreateWorkflow);
  const useEditInlineAllocation = isEditOutward && !useManualCreateWorkflow;
  const showExpand = state.selected && dispatchQty > 0 && method;
  const isApplied = !!state.isApplied;
  const manualCatalogBatches = useManualCreateWorkflow
    ? (state.catalogBatches?.length
        ? state.catalogBatches
        : (!isApplied && (state.batches || []).length ? state.batches : (state.catalogBatches || [])))
    : [];
  const batches = isFEFO && !isEditOutward
    ? keepOnlyFirstEligibleFefoBatch(state.batches || [])
    : useManualCreateWorkflow
      ? manualCatalogBatches
      : isEditOutward
        ? (state.batches || [])
        : manualCatalogBatches;
  const showAllocationSection = showExpand;
  const uniqueBins = [...new Map(
    (product.bins || []).map((b) => [String(b.binId || b._id || b.binCode), b])
  ).values()];
  const binsList = uniqueBins.map((b) => `${b.binCode || b.binId}`);
  const firstBin = binsList[0] || '—';
  const remainingBinsCount = Math.max(0, binsList.length - 1);
  const appliedBatches = useEditInlineAllocation || isFEFO ? (state.batches || []) : (state.batches || []);
  const allocatedTotal = useEditInlineAllocation
    ? appliedBatches.reduce((sum, b) => sum + (Number(b.quantity) || 0), 0)
    : isFEFO
      ? getFefoAllocatedQty(batches)
      : appliedBatches.reduce((sum, b) => sum + (Number(b.quantity) || 0), 0);
  const allocationComplete = dispatchQty > 0 && allocatedTotal === dispatchQty;
  const remainingQty = Math.max(0, dispatchQty - allocatedTotal);
  const allocationRows = useManualCreateWorkflow
    ? groupIdenticalManualBatches(id, batches)
    : (batches || []).map((b) => ({
        key: `${id}-${b.batchId || b._id}`,
        members: [b],
      }));
  const batchCount = allocationRows.length;

  const getAppliedBatch = (batchId) =>
    appliedBatches.find((b) => String(b.batchId || b._id) === String(batchId));

  const getBatchAllocatedQty = (b) => {
    const bid = b.batchId || b._id;
    if (isFEFO && !isEditOutward) return Number(b.quantity || 0);
    if (useEditInlineAllocation) {
      return Number(b.quantity ?? state.manualSelections?.[bid] ?? 0);
    }
    const applied = getAppliedBatch(bid);
    if (applied) return Number(applied.quantity || 0);
    return Number(state.manualSelections?.[bid] ?? 0);
  };

  return (
    <>
      <tr
        className={`border-b border-gray-100 ${rowInactive ? "opacity-60 bg-gray-50/50" : ""} ${
          outOfStock ? "bg-gray-50/30" : ""
        }`}
      >
        <td className="px-2 py-1.5 text-center text-xs text-gray-500" style={{ border: '1px solid #E4E7EC', height: 36 }}>{rowNumber}</td>
        <td className="px-2 py-1.5 whitespace-nowrap" style={{ color: '#101828', border: '1px solid #E4E7EC', height: 36 }}>
          <input
            type="text"
            value={state.skuDraft ?? product.sku ?? ''}
            onChange={(e) => onSkuDraftChange(id, e.target.value)}
            onPaste={(e) => onGridPaste(e, rowNumber - 1, 0)}
            readOnly={isEditOutward}
            className="w-full border-0 bg-white px-2 text-sm focus:outline-none focus:ring-0"
            style={{ height: 34 }}
          />
        </td>
        <td className="px-2 py-1.5" style={{ border: '1px solid #E4E7EC', height: 36 }}>
          <input
            type="text"
            inputMode="numeric"
            value={
              state.dispatchQuantityInput != null
                ? state.dispatchQuantityInput
                : (state.dispatchQuantity === 0 ? '' : state.dispatchQuantity)
            }
            onChange={(e) => onDispatchQtyChange(id, e.target.value)}
            onKeyDown={preventOutwardQtyDecimalKeyDown}
            onPaste={(e) => {
              blockInvalidOutwardQtyPaste(e);
              onGridPaste(e, rowNumber - 1, 1);
            }}
            disabled={dispatchInputDisabled}
            title={
              outOfStock && !isEditOutward
                ? 'No available stock for this product'
                : undefined
            }
            className={`w-full border-0 bg-white px-2 text-sm focus:outline-none focus:ring-0 ${
              dispatchInputDisabled ? 'cursor-not-allowed text-gray-400 bg-gray-50' : ''
            }`}
            style={{ height: 34 }}
          />
          {outOfStock && dispatchQty > 0 && (
            <p className="text-xs text-amber-600 mt-1">No on-hand stock</p>
          )}
        </td>
        <td className="px-2 py-1.5 whitespace-nowrap" style={{ color: outOfStock && !isEditOutward ? '#98A2B3' : '#101828', border: '1px solid #E4E7EC', height: 36 }}>
          <div className="text-sm leading-5">
            <span>{onHandStockQty}</span>
            {isEditOutward && inputMaxQty > onHandStockQty && (
              <span className="block text-xs text-gray-500 mt-0.5">
                Max editable: {inputMaxQty}
              </span>
            )}
          </div>
        </td>
        <td className="px-2 py-1.5" style={{ color: '#101828', border: '1px solid #E4E7EC', height: 36 }}>
          {binsList.length ? (
            <div className="text-xs text-gray-700 leading-4">
              {!binsExpanded ? (
                <span className="whitespace-nowrap">
                  {firstBin}
                  {remainingBinsCount > 0 && (
                    <>
                      {" "}
                      <button
                        type="button"
                        onClick={() => setBinsExpanded(true)}
                        className="text-emerald-700 hover:underline"
                      >
                        +{remainingBinsCount} more
                      </button>
                    </>
                  )}
                </span>
              ) : (
                <span>
                  {binsList.join(', ')}
                  {remainingBinsCount > 0 && (
                    <>
                      {" "}
                      <button
                        type="button"
                        onClick={() => setBinsExpanded(false)}
                        className="text-emerald-700 hover:underline"
                      >
                        collapse
                      </button>
                    </>
                  )}
                </span>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-700 leading-4">—</div>
          )}
        </td>
        <td className="px-2 py-1.5 whitespace-nowrap" style={{ border: '1px solid #E4E7EC', height: 36 }}>
          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${isFEFO ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}`}>
            {method}
          </span>
        </td>
        <td className="px-2 py-1.5" style={{ border: '1px solid #E4E7EC', height: 36 }}>
          {useEditInlineAllocation ? (
            <span className="text-gray-400">—</span>
          ) : isFEFO && isApplied ? (
            <button
              type="button"
              onClick={() => onRemoveBatch(id, batches[0]?.batchId || batches[0]?._id)}
              className="text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Remove
            </button>
          ) : (
            <span className="text-gray-400">—</span>
          )}
          {/* LEGACY: Global Apply button per product row (replaced by FEFO auto-apply + per-batch Manual Apply)
          <button onClick={() => (isFEFO ? onApplyFEFO(id) : onApplyManual(id))}>Apply</button>
          */}
        </td>
      </tr>
      {dispatchQtyError && (
        <tr className="bg-red-50/40">
          <td colSpan={7} className="px-3 py-1.5 text-sm text-red-600" style={{ border: '1px solid #E4E7EC' }}>
            {dispatchQtyError}
          </td>
        </tr>
      )}
      {showAllocationSection && (
        <tr className="bg-gray-50/60">
          <td colSpan={7} className="px-3 py-2 align-top" style={{ border: '1px solid #E4E7EC' }}>
            <button
              type="button"
              onClick={() => setAllocationOpen((o) => !o)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-800 hover:text-emerald-700"
            >
              <span className="text-gray-500">{allocationOpen ? '▼' : '▶'}</span>
              Batch Allocation{batchCount > 0 ? ` (${batchCount} Batch${batchCount === 1 ? '' : 'es'})` : ''}
            </button>
            {allocationOpen && (
              <div className="mt-3 ml-1 rounded-xl border border-gray-200 bg-white p-4">
                {state.error && <p className="text-sm font-medium text-red-600 mb-3">{state.error}</p>}
                {isEditOutward && isFEFO && !state.loading && state.fefoLiveAllocated != null && (
                  <p className="text-xs text-gray-600 mb-3">
                    Live FEFO availability for this dispatch request: {Number(state.fefoLiveAllocated) || 0}
                    {Number.isFinite(Number(state.fefoLiveTotalAvailable))
                      ? ` (eligible batch total ${Number(state.fefoLiveTotalAvailable)})`
                      : ''}
                    . Saved line allocations below: {allocatedTotal}.
                  </p>
                )}
                {isFEFO && state.loading && (
                  <p className="text-sm text-gray-500 mb-3">Loading FEFO recommendation…</p>
                )}
                {isFEFO && !state.loading && (state.fefoShortfall ?? 0) > 0 && !state.error && (
                  <p className="text-sm font-medium text-red-600 mb-3">
                    Insufficient FEFO stock across eligible batches. Reduce the Dispatch Quantity or switch to Manual method.
                  </p>
                )}
                {!isFEFO && state.loading && (
                  <p className="text-sm text-gray-500 mb-3">Loading batches…</p>
                )}
                {!state.loading && !batches.length && (
                  <p className="text-xs text-gray-500 mb-3">No batches available.</p>
                )}
                {batches.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm min-w-[720px]">
                      <thead>
                        <tr className="border-b border-gray-200 bg-[#F8FAFC]">
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Batch Number</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Expiry Date</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Manufacturing Date</th>
                          {/* <th className="px-3 py-2 text-left font-semibold text-gray-700">Bin</th> */}
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Available Quantity</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Allocated Quantity</th>
                          {useManualCreateWorkflow ? (
                            <th className="px-3 py-2 text-left font-semibold text-gray-700">Action</th>
                          ) : null}
                        </tr>
                      </thead>
                      <tbody>
                        {allocationRows.map((group) => {
                          const members = group.members;
                          const b = members[0];
                          const bid = b.batchId || b._id;
                          const grouped = useManualCreateWorkflow && members.length > 1;
                          const avail = grouped
                            ? members.reduce((sum, member) => sum + getBatchAvailableQty(member), 0)
                            : getBatchAvailableQty(b);
                          const availDisplay = grouped
                            ? members.reduce((sum, member) => sum + getBatchDisplayAvailableQty(member), 0)
                            : getBatchDisplayAvailableQty(b);
                          const maxSelect = useEditInlineAllocation
                            ? getEditBatchMaxAllocQty(b)
                            : Math.min(avail, dispatchQty);
                          const allocQty = grouped
                            ? members.reduce((sum, member) => sum + getBatchAllocatedQty(member), 0)
                            : getBatchAllocatedQty(b);
                          const appliedRow = useManualCreateWorkflow ? getAppliedBatch(bid) : null;
                          const isRowApplied = grouped
                            ? members.some((member) => {
                                const applied = getAppliedBatch(member.batchId || member._id);
                                return !!applied && Number(applied.quantity) > 0;
                              })
                            : !!appliedRow && Number(appliedRow.quantity) > 0;
                          const currentSel = grouped
                            ? members.reduce(
                                (sum, member) =>
                                  sum + Number(state.manualSelections?.[String(member.batchId || member._id)] ?? 0),
                                0
                              )
                            : useEditInlineAllocation
                              ? Number(b.quantity ?? state.manualSelections?.[bid] ?? 0)
                              : Number(state.manualSelections?.[bid] ?? 0);
                          const isRowEditable = useEditInlineAllocation || (!isFEFO && !isRowApplied);
                          return (
                            <tr key={group.key || `${id}-${bid}`} className="border-b border-gray-100">
                              <td className="px-3 py-2 text-gray-800">{b.batchNumber || '—'}</td>
                              <td className="px-3 py-2 text-gray-700">{formatAllocationDisplayDate(b.expiryDate)}</td>
                              <td className="px-3 py-2 text-gray-700">{formatAllocationDisplayDate(b.manufacturingDate)}</td>
                              {/* <td className="px-3 py-2 text-gray-700">{getBatchBinCode(b, product)}</td> */}
                              <td className="px-3 py-2 text-right text-gray-700">{availDisplay}</td>
                              <td className="px-3 py-2 text-right">
                                {isRowEditable ? (
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    value={currentSel || ''}
                                    onKeyDown={preventOutwardQtyDecimalKeyDown}
                                    onPaste={blockInvalidOutwardQtyPaste}
                                    onChange={(e) => (
                                      useEditInlineAllocation
                                        ? onEditBatchQtyChange(id, bid, e.target.value, maxSelect)
                                        : grouped
                                          ? onManualQtyChange(
                                              id,
                                              bid,
                                              e.target.value,
                                              maxSelect,
                                              distributeGroupedManualQty(members, e.target.value, dispatchQty)
                                            )
                                          : onManualQtyChange(id, bid, e.target.value, maxSelect)
                                    )}
                                    placeholder="0"
                                    className="h-8 w-24 rounded border border-gray-200 px-2 text-sm text-right focus:ring-2 focus:ring-emerald-200"
                                  />
                                ) : (
                                  <span className="text-gray-800 font-medium">{allocQty || 0}</span>
                                )}
                              </td>
                              {useManualCreateWorkflow && (
                                <td className="px-3 py-2">
                                  {isRowApplied ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        onRemoveBatch(
                                          id,
                                          bid,
                                          grouped ? members.map((member) => member.batchId || member._id) : undefined
                                        )
                                      }
                                      className="text-sm text-red-600 hover:text-red-800 font-medium"
                                    >
                                      Remove
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        grouped
                                          ? onApplyManualBatch(
                                              id,
                                              bid,
                                              Object.fromEntries(
                                                members.map((member) => {
                                                  const memberId = String(member.batchId || member._id);
                                                  return [
                                                    memberId,
                                                    Number(state.manualSelections?.[memberId] ?? 0),
                                                  ];
                                                })
                                              )
                                            )
                                          : onApplyManualBatch(id, bid)
                                      }
                                      disabled={!currentSel || outOfStock}
                                      className="rounded-[8px] border border-[#188742] bg-white px-3 py-1 text-xs font-semibold text-[#188742] transition hover:bg-[#188742]/5 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      Apply
                                    </button>
                                  )}
                                </td>
                              )}
                              {/* LEGACY: Edit Outward inline allocation hid Action column (Apply/Remove) */}
                              {/* {!isEditOutward && !isFEFO && ( ... Apply/Remove ... )} */}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-gray-700">
                    Allocated: <span className="font-semibold">{allocatedTotal}</span> / {dispatchQty}
                  </p>
                  {allocationComplete ? (
                    <p className="text-sm font-semibold text-emerald-600">✅ Allocation Complete</p>
                  ) : dispatchQty > 0 ? (
                    <p className="text-sm font-medium text-amber-700">Remaining: {remainingQty}</p>
                  ) : null}
                </div>
                {useManualCreateWorkflow && dispatchQty > 0 && allocatedTotal !== dispatchQty && (
                  <p className="text-sm font-medium text-red-600 mt-2">
                    Allocated total ({allocatedTotal}) must equal dispatch quantity ({dispatchQty})
                  </p>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
      {/* LEGACY: Inline multi-batch rows inside product table (replaced by expandable Batch Allocation panel above)
      {showBatches && batches.slice(1).map(...)}
      {showManualMismatch && (...)}
      */}
    </>
  );
});

export function OutwardManagement() {
  const {
    loading: permissionLoading,
    permissionState,
    pendingRequest,
    approvedPermissions,
    activePermission,
    hasPendingRequest,
    hasActiveAccess,
    refresh: refreshPermissionStatus,
  } = useOutwardBackdatePermission();
  const [permissionRequestModalOpen, setPermissionRequestModalOpen] = useState(false);
  const [permissionModalSelectedDate, setPermissionModalSelectedDate] = useState("");
  const [permissionModalPendingOnly, setPermissionModalPendingOnly] = useState(false);
  const pendingReopenCreateFormRef = useRef(false);
  const permissionPostingDateRef = useRef("");
  const latestEditTransactionRef = useRef(null);
  const warehouseId =
    localStorage.getItem("warehouseId") ||
    sessionStorage.getItem("warehouseId") ||
    "";

  const [page, setPage] = useState(1);
  const limit = 20;
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const filteredCompanies = (companies || []).filter(
    (c) => (c.contract?.onboardStatus || "pending") === "completed"
  );
  // OLD LOGIC (kept for reference):
  // const companyOptions = companies.map((c) => ({
  //   value: c._id,
  //   label: c.name,
  // }));
  const companyOptions = filteredCompanies.map((c) => ({
    value: c._id || c.id,
    label: `${c.companyCode} - ${c.name}`,
  }));
  const bulkCompanyOptions = filteredCompanies.map((c) => ({
    value: c._id || c.id,
    label: `${c.companyCode} - ${c.name}`,
  }));
  const [transactions, setTransactions] = useState([]);
  const [errors, setErrors] = useState([]);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(null);
  const [viewData, setViewData] = useState(null);
  const viewRowRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');
  const [displayMonth, setDisplayMonth] = useState(() => new Date());
  const dateRef = useRef(null);
  const [showForm, setShowForm] = useState(false);
  const [outwardRequisitionNumber, setOutwardRequisitionNumber] = useState("");
  const [ornLoading, setOrnLoading] = useState(false);
  const [ornError, setOrnError] = useState(null);
  const ornFetchInFlightRef = useRef(false);
  const [fefoRecommendation, setFefoRecommendation] = useState(null);
  const [manualBatches, setManualBatches] = useState([]);
  const [availableTotal, setAvailableTotal] = useState(null);
  const [manualBatchSelections, setManualBatchSelections] = useState({});
  const [showManualBatches, setShowManualBatches] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const initialFormData = {
    indentReferenceNumber: '',
    invoiceDate: todayYmdIST(),
    companies: '',
    secondaryCompanies: [],
    productDescription: '',
    productId: '',
    dispatchQuantity: 0,
    vehicleNumber: '',
    reportingDate: '',
    reportingTime: '',
    method: 'FEFO',
    // ❌ OLD
    // location: '',
    vehicleTemperature: '',
    dockNumber: '',
    dockInTime: '',
    dockOutTime: '',
    productTemperature: '',
    allocationMethod: 'FEFO',
    batchDetails: [],
    productTable: {},
  };
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});
  const [formTouched, setFormTouched] = useState({});
  const [loading, setLoading] = useState(false);

  const setFormDataWithValidation = (updater) => {
    setFormData((prev) => {
      let next = typeof updater === 'function' ? updater(prev) : updater;
      // Keep allocationMethod in sync when Method select changes via FormPopup
      if (next?.method != null && next.method !== prev?.method) {
        next = { ...next, allocationMethod: next.method };
      }
      setFormErrors((prevErrs) => {
        if (!prevErrs || Object.keys(prevErrs).length === 0) return prevErrs;
        const nextErrs = { ...prevErrs };
        const fieldNames = [
          ...OUTWARD_REQUIRED_FIELDS.map((f) => f.name),
          'secondaryCompanies',
        ];
        for (const name of fieldNames) {
          const prevVal = prev?.[name];
          const nextVal = next?.[name];
          const changed =
            Array.isArray(prevVal) || Array.isArray(nextVal)
              ? JSON.stringify(prevVal || []) !== JSON.stringify(nextVal || [])
              : prevVal !== nextVal;
          if (!changed) continue;
          setFormTouched((t) => ({ ...(t || {}), [name]: true }));
          if (!isBlankOutwardField(nextVal) && nextErrs[name]) {
            delete nextErrs[name];
          }
        }
        return nextErrs;
      });
      return next;
    });
  };
  const [draftItems, setDraftItems] = useState([]);
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [uploadPreviewOpen, setUploadPreviewOpen] = useState(false);
  const [uploadPreviewSummary, setUploadPreviewSummary] = useState(null);
  const [uploadPreviewFileName, setUploadPreviewFileName] = useState('');
  const [uploadPreviewHeaders, setUploadPreviewHeaders] = useState([]);
  const [uploadPreviewRows, setUploadPreviewRows] = useState([]);
  const [uploadSelectedFile, setUploadSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [productUploadPreviewOpen, setProductUploadPreviewOpen] = useState(false);
  const [productUploadPreviewFileName, setProductUploadPreviewFileName] = useState('');
  const [productUploadPreviewHeaders, setProductUploadPreviewHeaders] = useState([]);
  const [productUploadPreviewRows, setProductUploadPreviewRows] = useState([]);
  const [productUploadSelectedFile, setProductUploadSelectedFile] = useState(null);
  const [productUploadLoading, setProductUploadLoading] = useState(false);
  const [productCsvOrderIds, setProductCsvOrderIds] = useState([]);
  const productTableFileRef = useRef(null);
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [statusConfirmPayload, setStatusConfirmPayload] = useState(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [downloadingDoId, setDownloadingDoId] = useState(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewFileName, setPdfPreviewFileName] = useState("");
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState("PDF Preview");
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const pdfPreviewUrlRef = useRef(null);
  const pdfPreviewRequestIdRef = useRef(0);
  const [picklistGeneratedIds, setPicklistGeneratedIds] = useState(() => {
    try {
      const raw = sessionStorage.getItem("wms_picklist_generated_ids");
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr.map(String) : []);
    } catch {
      return new Set();
    }
  });
  const [failedItems, setFailedItems] = useState([]);
  const [editingTransactionProductIds, setEditingTransactionProductIds] = useState([]);
  const [editingItemStatus, setEditingItemStatus] = useState(null);
  const [outwardEditProducts, setOutwardEditProducts] = useState([]);
  const outwardEditProductsRef = useRef(outwardEditProducts);
  outwardEditProductsRef.current = outwardEditProducts;
  const originalBatchAllocByProductRef = useRef({});
  const [statusChangeModeOpen, setStatusChangeModeOpen] = useState(false);
  const [statusChangeModePayload, setStatusChangeModePayload] = useState(null);
  const [highlightedTransactionId, setHighlightedTransactionId] = useState(null);

  // Stable allocation state per product (avoids blink / re-render reset). Used for create-from-table flow only.
  const [allocationStateByProduct, setAllocationStateByProduct] = useState({});
  const allocationStateByProductRef = useRef(allocationStateByProduct);
  allocationStateByProductRef.current = allocationStateByProduct;
  const globalMethodRef = useRef(String(formData.method || formData.allocationMethod || 'FEFO').toUpperCase());
  useEffect(() => {
    globalMethodRef.current = String(formData.method || formData.allocationMethod || 'FEFO').toUpperCase();
  }, [formData.method, formData.allocationMethod]);
  const productsRef = useRef(products);
  productsRef.current = products;
  const editingTransactionIdRef = useRef(editingTransactionId);
  const editingItemIdRef = useRef(editingItemId);
  editingTransactionIdRef.current = editingTransactionId;
  editingItemIdRef.current = editingItemId;
  const originalDispatchQtyByProductRef = useRef({});
  const getProductForAllocation = useCallback((productId) => {
    const pid = String(productId);
    return (
      (outwardEditProductsRef.current || []).find((p) => String(p._id) === pid) ||
      (productsRef.current || []).find((p) => String(p._id) === pid)
    );
  }, []);
  const fefoDebounceTimersRef = useRef({});
  const fefoRequestIdRef = useRef({});
  const manualRequestIdRef = useRef({});
  const fetchManualBatchesForAllocationRef = useRef(null);
  const skipFefoDebounceRef = useRef({});
const indentFetchRef = useRef(false);

  const {
    data: outwardTransactionsResponse,
    isLoading,
    isFetching,
    refetch: refetchTransactions,
  } = useQuery({
    queryKey: ['outwardTransactions', appliedFrom, appliedTo],
    queryFn: async () => {
      // Do not send page/limit: API skip/limit of 20 hid older PENDING headers
      // (e.g. OUT-81677291-GNN at rank 44) while export has no truncation.
      const params = {};
      if (appliedFrom && appliedTo) {
        params.startDate = appliedFrom;
        params.endDate = appliedTo;
      }
      return outwardService.getOutwardTransactions(params);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
  const tableLoading = isLoading || isFetching;

  const rangeLabel = useMemo(() => {
    const fmt = (v) => {
      if (!v) return '';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return v;
      const day = d.getDate();
      const mon = d.toLocaleString('en-GB', { month: 'short' });
      return `${day} ${mon}`;
    };
    const a = fmt(dateFrom);
    const b = fmt(dateTo);
    if (!a && !b) return 'Select Date';
    if (a && !b) return `${a} - ...`;
    if (!a && b) return `... - ${b}`;
    return `${a} - ${b}`;
  }, [dateFrom, dateTo]);

  const handleApplyDateFilter = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    setPage(1);
  };

  const clearOutwardListFilters = () => {
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
    setAppliedFrom('');
    setAppliedTo('');
    setIsDateOpen(false);
    setPage(1);
  };

  const hasOutwardListFilters =
    !!(searchTerm && String(searchTerm).trim()) ||
    !!(dateFrom && String(dateFrom).trim()) ||
    !!(dateTo && String(dateTo).trim()) ||
    !!(appliedFrom && String(appliedFrom).trim()) ||
    !!(appliedTo && String(appliedTo).trim());

  // Normalize search once for smart, case-insensitive filtering
  const normalizedSearch = useMemo(
    () => (searchTerm ? searchTerm.trim().toLowerCase() : ''),
    [searchTerm]
  );

  // Frontend smart search over common visible fields (transaction, indent, company, product, status)
  const filteredTransactions = useMemo(() => {
    if (!normalizedSearch) return transactions;
    const term = normalizedSearch;
    return (transactions || []).filter((t) => {
      const txId = String(t.transactionId ?? t._id ?? t.id ?? '').toLowerCase();
      const orn = String(t.outwardRequisitionNumber ?? '').toLowerCase();
      const indentRef = String(t.indentReferenceNumber ?? '').toLowerCase();
      const status = String(t.status ?? '').toLowerCase();
      // ❌ OLD
      // const location = String(t.location ?? '').toLowerCase();

      // Company / secondary company visible info
      const primaryCompaniesText = (t.companies || [])
        .map((c) =>
          `${c.name || ''} ${c.companyCode || ''}`.toString().toLowerCase()
        )
        .join(' ');
      const secondaryCompaniesText = (t.secondaryCompanies || [])
        .map((c) =>
          `${c.customerName || ''} ${c.customerCode || ''}`.toString().toLowerCase()
        )
        .join(' ');

      // Product info (sku / name / description) from top-level product or items
      const topProduct = t.productId || (Array.isArray(t.products) ? t.products[0] : null);
      const topSku = String(
        topProduct && typeof topProduct === 'object' ? topProduct.sku ?? '' : topProduct ?? ''
      ).toLowerCase();
      const topName = String(
        topProduct && typeof topProduct === 'object'
          ? topProduct.skuName ?? topProduct.description ?? ''
          : ''
      ).toLowerCase();

      const itemsText = (t.items || [])
        .map((it) => {
          const p = it.productId && typeof it.productId === 'object' ? it.productId : null;
          const sku = String(
            p ? p.sku ?? p.productCode ?? '' : it.productSku ?? it.productCode ?? ''
          ).toLowerCase();
          const name = String(
            p ? p.skuName ?? p.description ?? '' : it.productDescription ?? ''
          ).toLowerCase();
          const ref = String(it.referenceNumber ?? '').toLowerCase();
          return `${sku} ${name} ${ref}`;
        })
        .join(' ');

      return (
        txId.includes(term) ||
        orn.includes(term) ||
        indentRef.includes(term) ||
        status.includes(term) ||
        // ❌ OLD
        // location.includes(term) ||
        primaryCompaniesText.includes(term) ||
        secondaryCompaniesText.includes(term) ||
        topSku.includes(term) ||
        topName.includes(term) ||
        itemsText.includes(term)
      );
    });
  }, [transactions, normalizedSearch]);

  const groupedTransactions = useMemo(
    () => groupOutwardListingRows(filteredTransactions),
    [filteredTransactions]
  );

  const openStatusChangeModeSelection = (transactionId, itemId, currentStatus) => {
    // Only allow status changes from PENDING status
    if (currentStatus !== 'PENDING') {
      toast.error('Status can only be changed from PENDING. CANCELLED and DISPATCHED statuses cannot be changed.', toastOpt);
      return;
    }
    const row = transactions.find(t => (t._id === transactionId || t.id === transactionId) && t.itemId === itemId);
    setStatusChangeModePayload({ transactionId, itemId, currentStatus, rowTransactionId: row?.transactionId });
    setStatusChangeModeOpen(true);
  };

  const handleStatusChangeModeSelection = (mode) => {
    if (!statusChangeModePayload) return;
    const { transactionId, itemId, currentStatus, rowTransactionId } = statusChangeModePayload;
    
    if (mode === 'document') {
      // Highlight all rows with same transactionId
      setHighlightedTransactionId(rowTransactionId);
    } else {
      // Item mode - no highlighting needed
      setHighlightedTransactionId(null);
    }
    // Store mode and show status options for both modes
    setStatusChangeModePayload({ ...statusChangeModePayload, mode, showStatusOptions: true });
  };

  const handleStatusOptionSelect = (newStatus) => {
    if (!statusChangeModePayload) return;
    const { transactionId, itemId, currentStatus, mode, rowTransactionId } = statusChangeModePayload;
    setStatusChangeModeOpen(false);
    // Keep highlighting if document mode, clear if item mode
    if (mode !== 'document') {
      setHighlightedTransactionId(null);
    }
    openStatusConfirm(transactionId, itemId, currentStatus, newStatus);
  };

  const openStatusConfirm = (transactionId, itemId, currentStatus, newStatus) => {
    const mode = statusChangeModePayload?.mode || 'item';
    setStatusConfirmPayload({ 
      id: transactionId, 
      itemId, 
      currentStatus, 
      newStatus, 
      mode,
      transactionIdString: statusChangeModePayload?.rowTransactionId 
    });
    setStatusConfirmOpen(true);
    // Clear mode selection payload
    setStatusChangeModePayload(null);
  };

  const outwardColumns = [
    { key: 'transactionId', label: 'Transaction ID' },
    {
      key: 'outwardRequisitionNumber',
      label: 'ORN',
      render: (v) => v || '—',
    },
    {
      key: 'invoiceDate',
      label: 'Posting Date',
      render: (v) => (v ? formatAllocationDisplayDate(v) : '-'),
    },
    { key: 'indentReferenceNumber', label: 'Indent Ref' },
    {
      key: "deliveryOrderNumber",
      label: "DO Number",
      align: "center",
      render: (_, row) => row.deliveryOrderNumber || "—",
    },
    {
      key: 'companies',
      label: 'Company',
      render: (_, row) => {
        const list = row.companies || [];
        if (!list.length) return '—';
        return list.map((c) => `${c.name}`).join(', ');
      },
    },
    {
      key: 'productId',
      label: 'Product',
      render: (val, row) => {
        const count = row?._productCount ?? 1;
        if (count > 1) return `${count} Products`;
        return val?.sku ?? '—';
      },
    },
    {
      key: 'dispatchQuantity',
      label: 'Dispatch Qty',
      align: 'right',
      render: (val, row) => {
        const count = row?._productCount ?? 1;
        if (count > 1) return '—';
        return val ?? '—';
      },
    },
    {
      key: 'vehicleNumber',
      label: 'Vehicle',
      render: (v) => v || '—',
    },
    // {
    //   key: 'reportingDate',
    //   label: 'Reporting Date',
    //   render: (v) => fmtISTYMD(v),
    // },
    // {
    //   key: 'reportingTime',
    //   label: 'Reporting Time',
    //   render: (v) => fmtISTTimeHM(v),
    // },
    { key: 'allocationMethod', label: 'Method' },
    {
      key: 'status',
      label: 'Status',
      render: (val, row) => (
        <OutwardStatusCell
          status={val}
          transactionId={row?._id}
          itemId={row?.itemId}
          currentStatus={val}
          onSelectNewStatus={(newStatus) => openStatusConfirm(row?._id, row?.itemId, val, newStatus)}
          onOpenModeSelection={() => openStatusChangeModeSelection(row?._id, row?.itemId, val)}
          getStatusVariant={getStatusVariant}
        />
      ),
    },
  ];

  const resetFormState = () => {
    setOutwardRequisitionNumber("");
    setOrnLoading(false);
    setOrnError(null);
    ornFetchInFlightRef.current = false;
    setFormData({ ...initialFormData, productTable: {} });
    setFormErrors({});
    setFormTouched({});
    setAllocationStateByProduct({});
    originalDispatchQtyByProductRef.current = {};
    originalBatchAllocByProductRef.current = {};
    setOutwardEditProducts([]);
    setFefoRecommendation(null);
    setManualBatches([]);
    setAvailableTotal(null);
    setManualBatchSelections({});
    setShowManualBatches(false);
    setErrors([]);
    setFailedItems([]);
    setDraftItems([]);
    setEditingTransactionProductIds([]);
    setEditingItemStatus(null);
    setProductUploadPreviewOpen(false);
    setProductUploadPreviewFileName('');
    setProductUploadPreviewHeaders([]);
    setProductUploadPreviewRows([]);
    setProductUploadSelectedFile(null);
    setProductCsvOrderIds([]);
    latestEditTransactionRef.current = null;
  };

  const openPermissionRequestModal = (selectedYmd, { closeCreateForm = false } = {}) => {
    const fallbackFromForm = resolvePostingYmdForPermissionModal(formData?.invoiceDate);
    const ymd =
      resolvePostingYmdForPermissionModal(selectedYmd) || fallbackFromForm || "";
    permissionPostingDateRef.current = ymd;
    setPermissionModalSelectedDate(ymd);
    setPermissionModalPendingOnly(hasPendingRequest);
    if (closeCreateForm) {
      pendingReopenCreateFormRef.current = true;
      setShowForm(false);
    }
    setPermissionRequestModalOpen(true);
  };

  const closePermissionRequestModal = (opts = {}) => {
    setPermissionRequestModalOpen(false);
    if (pendingReopenCreateFormRef.current) {
      pendingReopenCreateFormRef.current = false;
      setShowForm(true);
    }
    if (opts.pendingDetected) {
      refreshPermissionStatus();
      toast.error("Your access request is already pending admin approval.", toastOpt);
    }
  };

  const onPermissionRequestSuccess = () => {
    closePermissionRequestModal();
    refreshPermissionStatus();
    toast.success("Backdate access request submitted. Please wait for admin approval.", toastOpt);
  };

  // Update allocation state for one product (stable keyed state to prevent blink)
  const updateAllocationState = useCallback((productId, patch) => {
    if (patch.selected === false) {
      setErrors((prev) => (prev || []).filter((e) => String(e?.productId) !== String(productId)));
    } else if (patch.allocationMethod === 'MANUAL') {
      setErrors((prev) =>
        (prev || []).filter(
          (e) =>
            String(e?.productId) !== String(productId) ||
            !isFefoSpecificValidationMessage(e?.message)
        )
      );
    }
    setAllocationStateByProduct((prev) => {
      const next = { ...prev };
      const current = next[productId] || {
        selected: false,
        dispatchQuantity: 0,
        allocationMethod: globalMethodRef.current || 'FEFO',
        productDescription: '',
        batches: [],
        catalogBatches: [],
        manualSelections: {},
        expanded: true,
        loading: false,
        error: null,
        isApplied: false,
      };
      const { releaseAllocationsForMethodChange, ...patchRest } = patch;
      const merged = { ...current, ...patchRest };
      const preserveEditAllocations =
        Boolean(current.isEditOutward) && !releaseAllocationsForMethodChange;
      if (patch.allocationMethod === 'MANUAL' && !preserveEditAllocations) {
        merged.fefoShortfall = undefined;
        merged.fefoManuallyModified = false;
        merged.manualSelections = {};
        merged.batches = [];
        if (isFefoSpecificValidationMessage(merged.error)) {
          merged.error = null;
        }
      }
      if (patch.allocationMethod === 'FEFO' && !preserveEditAllocations) {
        merged.manualSelections = {};
        merged.fefoShortfall = undefined;
        merged.fefoManuallyModified = false;
        merged.batches = [];
      }
      if (
        !preserveEditAllocations &&
        (patch.dispatchQuantity !== undefined || patch.allocationMethod !== undefined)
      ) {
        merged.isApplied = false;
      }
      next[productId] = merged;
      allocationStateByProductRef.current = next;
      return next;
    });
  }, []);

  const applyDispatchStockValidationForProduct = useCallback(
    (productId, productOverride = null, stateOverride = null) => {
      const pid = String(productId);
      const state = {
        ...(allocationStateByProductRef.current?.[pid] || {}),
        ...(stateOverride || {}),
      };
      const requested = Number(state.dispatchQuantity) || 0;
      const prod = productOverride || getProductForAllocation(pid);
      if (!prod) return;

      const original =
        Number(state.originalDispatchQuantity) ||
        Number(originalDispatchQtyByProductRef.current?.[pid]) ||
        0;
      const isEdit = Boolean(
        state.isEditOutward ||
          (editingTransactionIdRef.current && editingItemIdRef.current)
      );
      const { onHand, maxAllowed } = getDispatchAllowance(prod, original);

      if (requested <= 0) {
        setErrors((prev) =>
          (prev || []).filter((e) => String(e?.productId) !== pid)
        );
        return;
      }

      let msg = formatDispatchStockShortageMessage({
        onHand,
        requested,
        maxAllowed,
        isEdit,
      });

      if (state.isEditOutward) {
        const batchErr = getEditBatchAllocationValidationError(state.batches);
        if (batchErr) {
          msg = msg ? `${msg} ${batchErr}` : batchErr;
        }
        const method = String(
          state.allocationMethod || globalMethodRef.current || 'FEFO'
        ).toUpperCase();
        const batchTotal = getSavedBatchAllocationTotal(state.batches);
        if (
          method === 'FEFO' &&
          state.fefoLiveAllocated != null &&
          batchTotal === requested &&
          requested > 0 &&
          Number(state.fefoLiveAllocated) < requested &&
          !state.fefoManuallyModified
        ) {
          const liveMsg = `Saved batch allocations total ${batchTotal}. Current live FEFO availability is ${Number(state.fefoLiveAllocated)} for dispatch ${requested}. Adjust batch quantities or reduce dispatch.`;
          msg = msg ? `${msg} ${liveMsg}` : liveMsg;
        }
      }

      const isStockShortageEntry = (message) =>
        String(message || '').startsWith('Insufficient stock. Available Stock:');

      setErrors((prev) => {
        const next = (prev || []).filter(
          (e) =>
            String(e?.productId) !== pid || !isStockShortageEntry(e?.message)
        );
        if (msg) next.push({ productId: pid, message: msg });
        return next;
      });

      const batchTotal = getSavedBatchAllocationTotal(state.batches);
      if (msg) {
        updateAllocationState(pid, {
          error: msg,
          ...(state.isEditOutward ? { isApplied: false } : { isApplied: false }),
        });
        return;
      }

      if (state.isEditOutward) {
        const applied =
          requested > 0 && batchTotal === requested && !getEditBatchAllocationValidationError(state.batches);
        updateAllocationState(pid, { isApplied: applied });
      }
    },
    [getProductForAllocation, updateAllocationState]
  );

  const refreshEditLiveFefoReference = useCallback(
    async (productId, dispatchQty) => {
      const pid = String(productId);
      const qty = Number(dispatchQty) || 0;
      if (!pid || qty <= 0) return;
      try {
        const result = await outwardService.getFEFORecommendation(pid, qty);
        const data = result?.data || {};
        const livePatch = {
          fefoLiveTotalAvailable: Number(data.totalAvailable),
          fefoLiveAllocated: Number(data.totalAllocated) || 0,
          fefoLiveShortfall: Math.max(0, Number(data.shortfall) || 0),
        };
        updateAllocationState(pid, livePatch);
        applyDispatchStockValidationForProduct(pid, getProductForAllocation(pid), {
          ...(allocationStateByProductRef.current?.[pid] || {}),
          ...livePatch,
        });
      } catch (_) {
        /* ignore live FEFO reference errors */
      }
    },
    [
      applyDispatchStockValidationForProduct,
      getProductForAllocation,
      updateAllocationState,
    ]
  );

  const syncAllocationDispatchWithStock = useCallback(
    async (stockProducts) => {
      const list = Array.isArray(stockProducts) ? stockProducts : productsRef.current || [];
      const isEdit = Boolean(
        editingTransactionIdRef.current && editingItemIdRef.current
      );
      if (isEdit) {
        setOutwardEditProducts((prev) =>
          (prev || []).map((p) => {
            const fresh = list.find((m) => String(m._id) === String(p._id));
            const onHand = fresh ? getProductOnHandStockQty(fresh) : 0;
            return {
              ...p,
              totalQty: onHand,
              onHandStockQty: onHand,
              bins: fresh?.bins ?? p.bins,
            };
          })
        );
      }
      const states = allocationStateByProductRef.current || {};
      for (const [productId, state] of Object.entries(states)) {
        const requested = Number(state.dispatchQuantity) || 0;
        if (requested <= 0) continue;
        const prod =
          list.find((m) => String(m._id) === String(productId)) ||
          getProductForAllocation(productId);
        applyDispatchStockValidationForProduct(productId, prod, state);
        if (state.isEditOutward && requested > 0) {
          await refreshEditLiveFefoReference(productId, requested);
        }
      }
    },
    [
      applyDispatchStockValidationForProduct,
      getProductForAllocation,
      refreshEditLiveFefoReference,
    ]
  );

  const handleAllocationDispatchQtyChange = useCallback((productId, value) => {
    const rawValue = value == null ? '' : String(value);
    const parsed = parseOutwardWholeNumberQtyInput(rawValue);
    if (parsed === null) return;
    const qty = parsed;
    if (qty < 0) return;
    const current = allocationStateByProductRef.current?.[productId] || {};
    const prod = getProductForAllocation(productId);

    updateAllocationState(productId, {
      dispatchQuantity: qty,
      dispatchQuantityInput: undefined,
      selected: qty > 0,
      allocationMethod: globalMethodRef.current || 'FEFO',
    });

    applyDispatchStockValidationForProduct(productId, prod, {
      ...current,
      dispatchQuantity: qty,
    });

    const method = String(globalMethodRef.current || current.allocationMethod || 'FEFO').toUpperCase();
    if (
      current.isEditOutward &&
      method === 'FEFO' &&
      qty > 0 &&
      !current.fefoManuallyModified
    ) {
      refreshEditLiveFefoReference(productId, qty);
      return;
    }

    if (
      method === 'MANUAL' &&
      qty > 0 &&
      isProductAvailableForOutward(prod) &&
      !editingTransactionId &&
      !editingItemId
    ) {
      const latest = allocationStateByProductRef.current?.[productId] || {};
      const hasCatalogBatches =
        Array.isArray(latest.catalogBatches) && latest.catalogBatches.length > 0;
      if (!hasCatalogBatches && !latest.loading) {
        fetchManualBatchesForAllocationRef.current?.(productId);
      }
    }
  }, [
    applyDispatchStockValidationForProduct,
    getProductForAllocation,
    refreshEditLiveFefoReference,
    updateAllocationState,
    editingTransactionId,
    editingItemId,
  ]);

  const handleProductGridPaste = useCallback((e, startRowIndex, startColIndex) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text') || '';
    if (!text) return;
    const rows = text.split(/\r?\n/).filter((r) => r !== '');
    if (!rows.length) return;
    rows.forEach((line, rOffset) => {
      const rowIndex = startRowIndex + rOffset;
      const product = (products || [])[rowIndex];
      if (!product) return;
      const id = product._id;
      const cols = line.split('\t');
      cols.forEach((cell, cOffset) => {
        const colIndex = startColIndex + cOffset;
        if (colIndex === 0) {
          updateAllocationState(id, { skuDraft: cell, selected: true });
        } else if (colIndex === 1) {
          updateAllocationState(id, { selected: true });
          handleAllocationDispatchQtyChange(id, String(cell ?? '').trim());
        }
      });
    });
  }, [products, updateAllocationState, handleAllocationDispatchQtyChange]);

  const resetProductFields = () => {
    setFormData((prev) => ({
      ...prev,
      productId: '',
      dispatchQuantity: 0,
      allocationMethod: 'FEFO',
      batchDetails: [],
      productDescription: '',
    }));
    setFefoRecommendation(null);
    setManualBatches([]);
    setAvailableTotal(null);
    setManualBatchSelections({});
    setShowManualBatches(false);
  };
  useEffect(() => {
   setFormData((prev) => ({ ...prev, secondaryCompanies: [] }));
   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [getSelectedCompanyId(formData.companies)]);

  const setAllocationMethod = (method) => {
    setFormData((prev) => ({ ...prev, allocationMethod: method }));
  };

  const productTable = formData.productTable || {};
  // Create-from-table flow: use allocation state for selection and apply status
  const selectedProductRowsFromAllocation = (products || []).filter((p) => allocationStateByProduct[p._id]?.selected);
  const productsForTable = (editingTransactionId || editingItemId) ? outwardEditProducts : products;
  const productsTableDisplay = useMemo(() => {
    if (editingTransactionId || editingItemId || !productCsvOrderIds.length) return productsForTable || [];
    const orderSet = new Set(productCsvOrderIds.map(String));
    const top = productCsvOrderIds
      .map((id) => (productsForTable || []).find((p) => String(p._id) === String(id)))
      .filter(Boolean);
    const rest = (productsForTable || []).filter((p) => !orderSet.has(String(p._id)));
    return [...top, ...rest];
  }, [editingTransactionId, editingItemId, productsForTable, productCsvOrderIds]);
  const allSelectedApplied = selectedProductRowsFromAllocation.length > 0 && selectedProductRowsFromAllocation.every((p) => allocationStateByProduct[p._id]?.isApplied);
  const selectedProductRows = (editingTransactionId || editingItemId)
    ? (products || []).filter((p) => (productTable[p._id] || {}).selected)
    : selectedProductRowsFromAllocation;
  const fefoDebounceRef = useRef({});
  const setProductTableRow = (productId, patch) => {
    setFormData((prev) => {
      const prevRow = prev.productTable?.[productId] || {};
      const nextRow = { ...prevRow, ...patch };
      if (patch.allocationMethod === 'MANUAL') {
        nextRow.fefoBatchPreview = null;
        nextRow.fefoShortfall = null;
      }
      if (patch.allocationMethod === 'FEFO') {
        nextRow.manualBatches = [];
        nextRow.manualBatchSelections = {};
      }
      return {
        ...prev,
        productTable: { ...prev.productTable, [productId]: nextRow },
      };
    });
  };
  const setProductTableSelectAll = (selected) => {
    setFormData((prev) => {
      const next = { ...prev.productTable };
      (products || []).forEach((p) => {
        const id = p._id;
        next[id] = {
          ...(next[id] || {}),
          selected,
          dispatchQuantity: next[id]?.dispatchQuantity ?? 0,
          allocationMethod: next[id]?.allocationMethod ?? 'FEFO',
          productDescription: next[id]?.productDescription ?? p.description ?? '',
          batchNumber: next[id]?.batchNumber ?? '',
          batchQuantity: next[id]?.batchQuantity ?? '',
          fefoBatchPreview: next[id]?.fefoBatchPreview ?? null,
          fefoShortfall: next[id]?.fefoShortfall ?? null,
          fefoLoading: next[id]?.fefoLoading ?? false,
          manualBatches: next[id]?.manualBatches ?? [],
          manualBatchSelections: next[id]?.manualBatchSelections ?? {},
        };
      });
      return { ...prev, productTable: next };
    });
  };
  const setAllocationSelectAll = useCallback((selected) => {
    setAllocationStateByProduct((prev) => {
      const next = { ...prev };
      (products || []).forEach((p) => {
        const id = p._id;
        const current = next[id] || {
          selected: false,
          dispatchQuantity: 0,
          allocationMethod: 'FEFO',
          productDescription: p.description || '',
          batches: [],
          manualSelections: {},
          expanded: true,
          loading: false,
          error: null,
          isApplied: false,
        };
        next[id] = { ...current, selected };
      });
      return next;
    });
  }, [products]);

  const setManualBatchSelectionForProduct = (productId, batchId, qty, maxAllowed) => {
    const parsed = parseOutwardWholeNumberQtyInput(qty);
    if (parsed === null) return;
    const q = Math.max(0, parsed);
    const finalQty = maxAllowed != null && maxAllowed >= 0 ? Math.min(q, maxAllowed) : q;
    setFormData((prev) => {
      const row = prev.productTable?.[productId] || {};
      const next = { ...row.manualBatchSelections };
      if (finalQty > 0) next[batchId] = finalQty;
      else delete next[batchId];
      return {
        ...prev,
        productTable: { ...prev.productTable, [productId]: { ...row, manualBatchSelections: next } },
      };
    });
  };

  // LEGACY: Auto-fetch FEFO for productTable (edit flow only). Create flow uses allocationStateByProduct + useEffect below.
  const fefoDebounceRefLegacy = useRef({});
  useEffect(() => {
    if (!editingTransactionId && !editingItemId) return;
    const pt = formData.productTable || {};
    (products || []).filter((p) => pt[p._id]?.selected).forEach((p) => {
      const id = p._id;
      const row = pt[id] || {};
      const method = (row.allocationMethod || 'FEFO').toUpperCase();
      const qty = Number(row.dispatchQuantity) || 0;
      if (method !== 'FEFO' || qty <= 0 || !isProductAvailableForOutward(p)) return;
      const t = fefoDebounceRefLegacy.current[id];
      if (t) clearTimeout(t);
      fefoDebounceRefLegacy.current[id] = setTimeout(() => {
        setFormData((prev) => {
          const r = prev.productTable?.[id] || {};
          return {
            ...prev,
            productTable: {
              ...prev.productTable,
              [id]: { ...r, fefoLoading: true, fefoBatchPreview: null, fefoShortfall: null },
            },
          };
        });
        outwardService
          .getFEFORecommendation(id, qty)
          .then(async (result) => {
            const data = result?.data || {};
            const enriched = await enrichFefoBatchesWithCatalogAvailableQty(id, data.batches || []);
            const batches = keepOnlyFirstEligibleFefoBatch(enriched);
            const totalAllocated = Number(data.totalAllocated) || getFefoAllocatedQty(batches);
            const shortfall = Math.max(0, Number(data.shortfall) || (qty - totalAllocated));
            setFormData((prev) => ({
              ...prev,
              productTable: {
                ...prev.productTable,
                [id]: {
                  ...(prev.productTable?.[id] || {}),
                  fefoLoading: false,
                  fefoBatchPreview: batches,
                  fefoShortfall: shortfall,
                },
              },
            }));
          })
          .catch(() => {
            setFormData((prev) => ({
              ...prev,
              productTable: {
                ...prev.productTable,
                [id]: {
                  ...(prev.productTable?.[id] || {}),
                  fefoLoading: false,
                  fefoBatchPreview: null,
                  fefoShortfall: null,
                },
              },
            }));
          });
      }, 300);
    });
    return () => {
      Object.values(fefoDebounceRefLegacy.current || {}).forEach(clearTimeout);
      fefoDebounceRefLegacy.current = {};
    };
  }, [
    formData.productTable,
    editingTransactionId,
    editingItemId,
    products,
  ]);

  // FEFO fetch for allocation state (create-from-table): debounced 300ms, requestId to ignore out-of-order responses
  const fefoTriggerKey = (editingTransactionId || editingItemId)
    ? ''
    : (products || [])
        .filter((p) => {
          const s = allocationStateByProduct[p._id] || {};
          const globalMethod = String(formData.method || formData.allocationMethod || 'FEFO').toUpperCase();
          return s.selected
            && isProductAvailableForOutward(p)
            && globalMethod === 'FEFO'
            && (Number(s.dispatchQuantity) || 0) > 0
            && !s.fefoManuallyModified;
        })
        .map((p) => {
          const s = allocationStateByProduct[p._id] || {};
          const globalMethod = String(formData.method || formData.allocationMethod || 'FEFO').toUpperCase();
          return `${p._id}:${globalMethod}:${s.dispatchQuantity ?? 0}`;
        })
        .sort()
        .join('|');
  const fetchFEFOBatchesForAllocation = useCallback((productId, qtyOverride) => {
    const prod = getProductForAllocation(productId);
    const current = allocationStateByProductRef.current?.[productId] || {};
    const qty =
      qtyOverride != null
        ? Number(qtyOverride) || 0
        : Number(current.dispatchQuantity) || 0;
    if (!isProductAvailableForOutward(prod) || qty <= 0) {
      updateAllocationState(productId, {
        batches: [],
        loading: false,
        fefoShortfall: undefined,
      });
      return;
    }
    if (current.fefoManuallyModified) return;

    const requestIds = fefoRequestIdRef.current;
    requestIds[productId] = (requestIds[productId] || 0) + 1;
    const reqId = requestIds[productId];
    updateAllocationState(productId, {
      loading: true,
      error: null,
      batches: [],
      fefoShortfall: undefined,
    });
    outwardService
      .getFEFORecommendation(productId, qty)
      .then(async (result) => {
        if (requestIds[productId] !== reqId) return;
        const latest = allocationStateByProductRef.current?.[productId] || {};
        if (String(globalMethodRef.current || latest.allocationMethod || 'FEFO').toUpperCase() !== 'FEFO') return;
        const data = result?.data || {};
        const enriched = await enrichFefoBatchesWithCatalogAvailableQty(productId, data.batches || []);
        const batches = keepOnlyFirstEligibleFefoBatch(enriched);
        const totalAllocated = Number(data.totalAllocated) || getFefoAllocatedQty(batches);
        const fefoTotalAvailable = Number(data.totalAvailable);
        const shortfall = Math.max(0, Number(data.shortfall) || (qty - totalAllocated));
        const shortageMsg = resolveFefoAllocationShortageMessage({
          requestedQty: qty,
          totalProductQty: getProductRemainingQty(prod),
          fefoAvailableQty: totalAllocated,
          fefoTotalAvailable: Number.isFinite(fefoTotalAvailable) ? fefoTotalAvailable : undefined,
          batches,
          shortfall,
        });
        updateAllocationState(productId, {
          loading: false,
          batches,
          fefoTotalAvailable: Number.isFinite(fefoTotalAvailable) ? fefoTotalAvailable : undefined,
          fefoShortfall: shortfall,
          error: shortageMsg,
          isApplied: !shortageMsg && totalAllocated === qty,
          ...(latest.isEditOutward ? { isEditOutward: true } : {}),
        });
        if (shortageMsg) {
          setErrors((prev) => {
            const next = Array.isArray(prev) ? [...prev] : [];
            const pid = String(productId);
            const idx = next.findIndex((e) => String(e?.productId) === pid);
            const entry = { productId: pid, message: shortageMsg };
            if (idx >= 0) next[idx] = entry;
            else next.push(entry);
            return next;
          });
        } else {
          setErrors((prev) =>
            (prev || []).filter((e) => String(e?.productId) !== String(productId))
          );
        }
        applyDispatchStockValidationForProduct(productId, prod, {
          ...latest,
          dispatchQuantity: qty,
          batches,
          fefoTotalAvailable: Number.isFinite(fefoTotalAvailable) ? fefoTotalAvailable : undefined,
          fefoShortfall: shortfall,
        });
      })
      .catch(() => {
        if (requestIds[productId] !== reqId) return;
        const latest = allocationStateByProductRef.current?.[productId] || {};
        if (String(globalMethodRef.current || latest.allocationMethod || 'FEFO').toUpperCase() !== 'FEFO') return;
        updateAllocationState(productId, {
          loading: false,
          batches: [],
          error: 'FEFO fetch failed',
        });
      });
  }, [updateAllocationState, getProductForAllocation, applyDispatchStockValidationForProduct]);

  useEffect(() => {
    if (editingTransactionId || editingItemId || !fefoTriggerKey) return;
    const timers = fefoDebounceTimersRef.current;
    const skipDebounce = skipFefoDebounceRef.current;
    const stateByProduct = allocationStateByProductRef.current;
    (products || []).forEach((p) => {
      const id = p._id;
      if (!isProductAvailableForOutward(p)) return;
      const state = stateByProduct[id] || {};
      const method = String(globalMethodRef.current || state.allocationMethod || 'FEFO').toUpperCase();
      const qty = Number(state.dispatchQuantity) || 0;
      if (!state.selected || method !== 'FEFO' || qty <= 0 || state.fefoManuallyModified) return;
      if (skipDebounce[id]) {
        delete skipDebounce[id];
        return;
      }
      if (timers[id]) clearTimeout(timers[id]);
      timers[id] = setTimeout(() => {
        delete timers[id];
        const latest = allocationStateByProductRef.current?.[id] || {};
        if (String(globalMethodRef.current || latest.allocationMethod || 'FEFO').toUpperCase() !== 'FEFO') return;
        fetchFEFOBatchesForAllocation(id, qty);
      }, 300);
    });
    return () => {
      Object.values(timers).forEach(clearTimeout);
      Object.keys(timers).forEach((k) => delete timers[k]);
    };
  }, [
    editingTransactionId,
    editingItemId,
    fefoTriggerKey,
    products,
    fetchFEFOBatchesForAllocation,
  ]);

  // When allocationMethod changes to MANUAL for a row, fetch batches for that product (legacy productTable / edit flow)
  const fetchManualBatchesForProduct = (productId) => {
    const prod = (productsRef.current || []).find((p) => String(p._id) === String(productId));
    if (!isProductAvailableForOutward(prod)) return;
    outwardService
      .getBatchesByProduct(productId)
      .then((res) => {
        const list = filterOutwardAvailableBatches(Array.isArray(res?.data) ? res.data : []);
        setFormData((prev) => ({
          ...prev,
          productTable: {
            ...prev.productTable,
            [productId]: { ...(prev.productTable?.[productId] || {}), manualBatches: list },
          },
        }));
      })
      .catch(() => {
        setFormData((prev) => ({
          ...prev,
          productTable: {
            ...prev.productTable,
            [productId]: { ...(prev.productTable?.[productId] || {}), manualBatches: [] },
          },
        }));
      });
  };

  // Fetch batches for MANUAL mode in allocation state (create-from-table)
  const fetchManualBatchesForAllocation = useCallback((productId) => {
    const prod = getProductForAllocation(productId);
    if (!isProductAvailableForOutward(prod)) {
      updateAllocationState(productId, {
        batches: [],
        manualSelections: {},
        loading: false,
        error: 'No available stock for this product',
      });
      return;
    }
    const requestIds = manualRequestIdRef.current;
    requestIds[productId] = (requestIds[productId] || 0) + 1;
    const reqId = requestIds[productId];
    updateAllocationState(productId, { loading: true, error: null, batches: [] });
    outwardService
      .getBatchesByProduct(productId)
      .then((res) => {
        if (requestIds[productId] !== reqId) return;
        const latest = allocationStateByProductRef.current?.[productId] || {};
        if (String(globalMethodRef.current || latest.allocationMethod || 'FEFO').toUpperCase() !== 'MANUAL') return;
        const batches = mapBatchesFromProductApi(Array.isArray(res?.data) ? res.data : []);
        if (latest.editManualCreateWorkflow) {
          const preservedApplied = (latest.batches || []).filter((b) => Number(b.quantity) > 0);
          const appliedTotal = preservedApplied.reduce(
            (sum, b) => sum + (Number(b.quantity) || 0),
            0
          );
          updateAllocationState(productId, {
            catalogBatches: batches,
            batches: preservedApplied,
            manualSelections: {},
            loading: false,
            isApplied: appliedTotal === Number(latest.dispatchQuantity) && appliedTotal > 0,
            isEditOutward: true,
            editManualCreateWorkflow: true,
          });
        } else if (latest.isEditOutward) {
          // LEGACY: Edit inline manual rows (no per-batch Apply workflow)
          updateAllocationState(productId, {
            catalogBatches: batches,
            batches: batches.map((b) => ({
              ...b,
              quantity: 0,
              originalReservedQty: 0,
            })),
            manualSelections: {},
            loading: false,
            isApplied: false,
            isEditOutward: true,
          });
        } else {
          updateAllocationState(productId, {
            catalogBatches: batches,
            batches: [],
            manualSelections: {},
            loading: false,
            isApplied: false,
          });
        }
      })
      .catch(() => {
        if (requestIds[productId] !== reqId) return;
        const latest = allocationStateByProductRef.current?.[productId] || {};
        if (String(globalMethodRef.current || latest.allocationMethod || 'FEFO').toUpperCase() !== 'MANUAL') return;
        updateAllocationState(productId, { batches: [], loading: false, error: 'Failed to load batches' });
      });
  }, [updateAllocationState, getProductForAllocation]);
  fetchManualBatchesForAllocationRef.current = fetchManualBatchesForAllocation;

  // Global Method applies to all products (FEFO/MANUAL); also supports Edit Outward when item is not dispatched.
  useEffect(() => {
    const m = String(formData.method || 'FEFO').toUpperCase();
    if (m !== 'FEFO' && m !== 'MANUAL') return;

    const isSingleItemEdit = Boolean(editingTransactionId && editingItemId);
  // LEGACY: Edit Outward skipped global Method changes entirely.
  // if (editingTransactionId || editingItemId) return;

    if (isSingleItemEdit) {
      if (isOutwardItemAllocationLocked(editingItemStatus)) return;
      const pid =
        Array.isArray(editingTransactionProductIds) && editingTransactionProductIds.length
          ? String(editingTransactionProductIds[0])
          : '';
      if (!pid) return;
      const s = allocationStateByProductRef.current?.[pid];
      if (!s?.selected) return;
      if ((s.allocationMethod || 'FEFO').toUpperCase() === m) return;

      const timers = fefoDebounceTimersRef.current;
      if (timers[pid]) {
        clearTimeout(timers[pid]);
        delete timers[pid];
      }
      fefoRequestIdRef.current[pid] = (fefoRequestIdRef.current[pid] || 0) + 1;
      manualRequestIdRef.current[pid] = (manualRequestIdRef.current[pid] || 0) + 1;

      const qty = Number(s.dispatchQuantity) || 0;
      const preservedManualBatches =
        m === 'MANUAL'
          ? (s.batches || [])
              .filter((b) => Number(b.quantity) > 0)
              .map((b) => ({
                batchId: b.batchId || b._id,
                batchNumber: b.batchNumber,
                manufacturingDate: b.manufacturingDate,
                expiryDate: b.expiryDate,
                availableQty: b.availableQty ?? b.quantity,
                quantity: Number(b.quantity) || 0,
                binId: b.binId,
              }))
          : [];
      const preservedManualTotal = preservedManualBatches.reduce(
        (sum, b) => sum + (Number(b.quantity) || 0),
        0
      );
      updateAllocationState(pid, {
        allocationMethod: m,
        releaseAllocationsForMethodChange: true,
        batches: preservedManualBatches,
        catalogBatches: [],
        manualSelections: {},
        isApplied: m === 'MANUAL' && preservedManualTotal === qty && qty > 0,
        editManualCreateWorkflow: m === 'MANUAL',
        fefoShortfall: undefined,
        fefoManuallyModified: false,
        error: null,
      });
      if (m === 'MANUAL') {
        fetchManualBatchesForAllocation(pid);
      } else if (qty > 0) {
        skipFefoDebounceRef.current[pid] = true;
        fetchFEFOBatchesForAllocation(pid, qty);
      }
      return;
    }

    if (editingTransactionId || editingItemId) return;
    (products || []).forEach((p) => {
      const id = p._id;
      const s = allocationStateByProductRef.current?.[id] || {};
      if ((s.allocationMethod || '').toUpperCase() === m) return;

      const timers = fefoDebounceTimersRef.current;
      if (timers[id]) {
        clearTimeout(timers[id]);
        delete timers[id];
      }
      fefoRequestIdRef.current[id] = (fefoRequestIdRef.current[id] || 0) + 1;
      manualRequestIdRef.current[id] = (manualRequestIdRef.current[id] || 0) + 1;

      const qty = Number(s.dispatchQuantity) || 0;
      updateAllocationState(id, {
        allocationMethod: m,
        editManualCreateWorkflow: false,
        releaseAllocationsForMethodChange: true,
      });
      if (!isProductAvailableForOutward(p) || qty <= 0) return;
      if (m === 'MANUAL') {
        fetchManualBatchesForAllocation(id);
      } else if (!s.fefoManuallyModified) {
        skipFefoDebounceRef.current[id] = true;
        fetchFEFOBatchesForAllocation(id, qty);
      }
    });
  }, [
    formData.method,
    products,
    editingTransactionId,
    editingItemId,
    editingItemStatus,
    editingTransactionProductIds,
    updateAllocationState,
    fetchManualBatchesForAllocation,
    fetchFEFOBatchesForAllocation,
  ]);

  // Edit popup uses the same sheet/table UI. Keep edit submit payload in-sync
  // with the currently selected/edited row in the sheet (single-item edit flow).
  useEffect(() => {
    if (!editingTransactionId || !editingItemId) return;
    const pid = Array.isArray(editingTransactionProductIds) && editingTransactionProductIds.length
      ? String(editingTransactionProductIds[0])
      : "";
    if (!pid) return;
    const state = allocationStateByProduct?.[pid];
    if (!state) return;

    const nextBatchDetails = (state.batches || []).map((b) => ({
      batchId: b.batchId || b._id,
      batchNumber: b.batchNumber,
      manufacturingDate: b.manufacturingDate,
      expiryDate: b.expiryDate,
      quantity: b.quantity,
      binId: b.binId,
      allocationSource: state.allocationMethod || 'FEFO',
    }));

    setFormData((prev) => ({
      ...prev,
      productId: pid,
      dispatchQuantity: Number(state.dispatchQuantity) || 0,
      method: state.allocationMethod || prev.method || prev.allocationMethod || 'FEFO',
      allocationMethod: state.allocationMethod || prev.allocationMethod || 'FEFO',
      productDescription: state.productDescription ?? prev.productDescription ?? '',
      batchDetails: nextBatchDetails,
    }));

    setDraftItems((prev) => {
      const cur = Array.isArray(prev) && prev.length ? prev[0] : null;
      if (!cur) return prev;
      return [{
        ...cur,
        productId: pid,
        dispatchQuantity: Number(state.dispatchQuantity) || 0,
        allocationMethod: state.allocationMethod || cur.allocationMethod || 'FEFO',
        productDescription: state.productDescription ?? cur.productDescription ?? '',
        batchDetails: nextBatchDetails.map(({ allocationSource, ...b }) => b),
      }];
    });
  }, [editingTransactionId, editingItemId, JSON.stringify(editingTransactionProductIds || []), allocationStateByProduct]);

  const setManualSelectionForAllocation = useCallback((productId, batchId, qty, maxAllowed, groupedAllocations) => {
    if (groupedAllocations && typeof groupedAllocations === 'object') {
      setAllocationStateByProduct((prev) => {
        const cur = prev[productId] || {};
        const next = { ...cur.manualSelections };
        for (const [memberId, memberQty] of Object.entries(groupedAllocations)) {
          const n = Number(memberQty) || 0;
          if (n > 0) next[memberId] = n;
          else delete next[memberId];
        }
        return { ...prev, [productId]: { ...cur, manualSelections: next } };
      });
      return;
    }
    const parsed = parseOutwardWholeNumberQtyInput(qty);
    if (parsed === null) return;
    const q = Math.max(0, parsed);
    const finalQty = maxAllowed != null && maxAllowed >= 0 ? Math.min(q, maxAllowed) : q;
    setAllocationStateByProduct((prev) => {
      const cur = prev[productId] || {};
      const next = { ...cur.manualSelections };
      if (finalQty > 0) next[batchId] = finalQty;
      else delete next[batchId];
      return { ...prev, [productId]: { ...cur, manualSelections: next } };
    });
  }, []);

  const setEditBatchAllocation = useCallback((productId, batchId, qty, maxAllowed) => {
    const parsed = parseOutwardWholeNumberQtyInput(qty);
    if (parsed === null) return;
    const q = Math.max(0, parsed);
    const finalQty = maxAllowed != null && maxAllowed >= 0 ? Math.min(q, maxAllowed) : q;
    setAllocationStateByProduct((prev) => {
      const cur = prev[productId] || {};
      const manualSelections = { ...cur.manualSelections };
      if (finalQty > 0) manualSelections[batchId] = finalQty;
      else delete manualSelections[batchId];
      const batches = (cur.batches || []).map((b) => {
        const bid = String(b.batchId || b._id);
        return bid === String(batchId) ? { ...b, quantity: finalQty } : b;
      });
      const nextState = {
        ...cur,
        manualSelections,
        batches,
        isEditOutward: true,
        fefoManuallyModified: true,
      };
      queueMicrotask(() => {
        applyDispatchStockValidationForProduct(
          productId,
          getProductForAllocation(productId),
          nextState
        );
      });
      return {
        ...prev,
        [productId]: nextState,
      };
    });
  }, [applyDispatchStockValidationForProduct, getProductForAllocation]);

  const applyFEFOForProduct = useCallback((productId) => {
    const state = allocationStateByProduct[productId] || {};
    const product = (products || []).find((p) => String(p._id) === String(productId));
    const fefoBatches = keepOnlyFirstEligibleFefoBatch(state.batches || []);
    const total = getFefoAllocatedQty(fefoBatches);
    const dispatchQty = Number(state.dispatchQuantity) || 0;
    if ((state.fefoShortfall ?? 0) > 0) {
      const fefoMsg =
        state.error ||
        resolveFefoAllocationShortageMessage({
          requestedQty: dispatchQty,
          totalProductQty: getProductRemainingQty(product),
          fefoAvailableQty: total,
          fefoTotalAvailable: state.fefoTotalAvailable,
          batches: fefoBatches,
          shortfall: state.fefoShortfall,
        }) ||
        'FEFO shortfall: cannot apply. Fix dispatch qty or use MANUAL.';
      toast.error(fefoMsg, toastOpt);
      return;
    }
    if (total !== dispatchQty) {
      toast.error(`FEFO total (${total}) must equal dispatch qty (${dispatchQty})`, toastOpt);
      return;
    }
    updateAllocationState(productId, { isApplied: true, error: null });
    toast.success('FEFO allocation applied', toastOpt);
  }, [allocationStateByProduct, products, updateAllocationState]);

  const applyManualForProduct = useCallback((productId) => {
    const state = allocationStateByProductRef.current?.[productId] || allocationStateByProduct[productId] || {};
    const dispatchQty = Number(state.dispatchQuantity) || 0;
    const manualTotal = Object.values(state.manualSelections || {}).reduce((s, q) => s + (Number(q) || 0), 0);
    if (manualTotal !== dispatchQty) {
      toast.error(`Allocated total (${manualTotal}) must equal dispatch qty (${dispatchQty})`, toastOpt);
      setAllocationStateByProduct((prev) => ({ ...prev, [productId]: { ...(prev[productId] || {}), error: `Total ${manualTotal} ≠ ${dispatchQty}` } }));
      return;
    }
    const catalogSource = state.catalogBatches?.length ? state.catalogBatches : (state.batches || []);
    const batches = [];
    for (const [batchId, qty] of Object.entries(state.manualSelections || {})) {
      const q = Number(qty) || 0;
      if (q <= 0) continue;
      const batch = catalogSource.find((b) => String(b.batchId || b._id) === String(batchId));
      if (!batch) {
        toast.error(`Batch ${batchId} not found`, toastOpt);
        return;
      }
      if (q > (batch.availableQty ?? batch.quantity ?? 0)) {
        toast.error(`Batch ${batch.batchNumber || batchId}: allocated exceeds available`, toastOpt);
        return;
      }
      batches.push({
        batchId: batch.batchId || batch._id,
        batchNumber: batch.batchNumber,
        manufacturingDate: batch.manufacturingDate,
        expiryDate: batch.expiryDate,
        availableQty: batch.availableQty ?? batch.quantity,
        quantity: q,
        binId: batch.binId,
      });
    }
    updateAllocationState(productId, {
      batches,
      catalogBatches: catalogSource,
      isApplied: true,
      error: null,
    });
    toast.success('Manual allocation applied', toastOpt);
  }, [allocationStateByProduct, updateAllocationState]);

  const applyManualBatchForAllocation = useCallback((productId, batchId, groupedAllocations) => {
    const state = allocationStateByProductRef.current?.[productId] || {};
    const dispatchQty = Number(state.dispatchQuantity) || 0;
    if (groupedAllocations && typeof groupedAllocations === 'object') {
      const catalogSource = state.catalogBatches?.length
        ? state.catalogBatches
        : (state.batches || []);
      const existingApplied = (state.batches || []).filter(
        (b) => !Object.prototype.hasOwnProperty.call(groupedAllocations, String(b.batchId || b._id))
      );
      const existingTotal = existingApplied.reduce((s, b) => s + (Number(b.quantity) || 0), 0);
      const additions = [];
      let addTotal = 0;
      for (const [memberId, rawQty] of Object.entries(groupedAllocations)) {
        const q = Number(rawQty) || 0;
        if (q <= 0) continue;
        const batch = catalogSource.find((b) => String(b.batchId || b._id) === String(memberId));
        if (!batch) {
          toast.error('Batch not found', toastOpt);
          return;
        }
        if (q > (batch.availableQty ?? batch.quantity ?? 0)) {
          toast.error(`Batch ${batch.batchNumber || memberId}: allocated exceeds available`, toastOpt);
          return;
        }
        additions.push({
          batchId: batch.batchId || batch._id,
          batchNumber: batch.batchNumber,
          manufacturingDate: batch.manufacturingDate,
          expiryDate: batch.expiryDate,
          availableQty: batch.availableQty ?? batch.quantity,
          quantity: q,
          binId: batch.binId,
        });
        addTotal += q;
      }
      if (addTotal <= 0) {
        toast.error('Enter allocated quantity before applying', toastOpt);
        return;
      }
      if (existingTotal + addTotal > dispatchQty) {
        toast.error(`Total allocation would exceed dispatch quantity (${dispatchQty})`, toastOpt);
        return;
      }
      const newBatches = [...existingApplied, ...additions];
      const manualTotal = newBatches.reduce((s, b) => s + (Number(b.quantity) || 0), 0);
      const nextManualSelections = { ...(state.manualSelections || {}) };
      Object.keys(groupedAllocations).forEach((memberId) => {
        delete nextManualSelections[memberId];
      });
      updateAllocationState(productId, {
        batches: newBatches,
        catalogBatches: catalogSource,
        manualSelections: nextManualSelections,
        isApplied: manualTotal === dispatchQty,
        error: manualTotal !== dispatchQty ? `Allocated ${manualTotal} of ${dispatchQty}` : null,
      });
      toast.success(manualTotal === dispatchQty ? 'Manual allocation applied' : 'Batch allocated', toastOpt);
      return;
    }
    const qty = Number(state.manualSelections?.[batchId]) || 0;
    if (qty <= 0) {
      toast.error('Enter allocated quantity before applying', toastOpt);
      return;
    }
    const catalogSource = state.catalogBatches?.length
      ? state.catalogBatches
      : (state.batches || []);
    const batch = catalogSource.find((b) => String(b.batchId || b._id) === String(batchId));
    if (!batch) {
      toast.error('Batch not found', toastOpt);
      return;
    }
    if (qty > (batch.availableQty ?? batch.quantity ?? 0)) {
      toast.error(`Batch ${batch.batchNumber || batchId}: allocated exceeds available`, toastOpt);
      return;
    }
    const existingApplied = (state.batches || []).filter(
      (b) => String(b.batchId || b._id) !== String(batchId)
    );
    const existingTotal = existingApplied.reduce((s, b) => s + (Number(b.quantity) || 0), 0);
    if (existingTotal + qty > dispatchQty) {
      toast.error(`Total allocation would exceed dispatch quantity (${dispatchQty})`, toastOpt);
      return;
    }
    const appliedBatch = {
      batchId: batch.batchId || batch._id,
      batchNumber: batch.batchNumber,
      manufacturingDate: batch.manufacturingDate,
      expiryDate: batch.expiryDate,
      availableQty: batch.availableQty ?? batch.quantity,
      quantity: qty,
      binId: batch.binId,
    };
    const newBatches = [...existingApplied, appliedBatch];
    const manualTotal = newBatches.reduce((s, b) => s + (Number(b.quantity) || 0), 0);
    const nextManualSelections = { ...(state.manualSelections || {}) };
    delete nextManualSelections[String(batchId)];
    updateAllocationState(productId, {
      batches: newBatches,
      catalogBatches: catalogSource,
      manualSelections: nextManualSelections,
      isApplied: manualTotal === dispatchQty,
      error: manualTotal !== dispatchQty ? `Allocated ${manualTotal} of ${dispatchQty}` : null,
    });
    toast.success(manualTotal === dispatchQty ? 'Manual allocation applied' : 'Batch allocated', toastOpt);
  }, [updateAllocationState]);

  const removeBatchForAllocation = useCallback((productId, batchId, groupedBatchIds) => {
    const state = allocationStateByProductRef.current?.[productId] || allocationStateByProduct[productId] || {};
    const method = String(state.allocationMethod || 'FEFO').toUpperCase();
    const idsToRemove = new Set(
      Array.isArray(groupedBatchIds) && groupedBatchIds.length > 1
        ? groupedBatchIds.map((id) => String(id))
        : [String(batchId)]
    );
    const nextBatches = (state.batches || []).filter(
      (b) => !idsToRemove.has(String(b.batchId || b._id))
    );
    const nextManualSelections = { ...(state.manualSelections || {}) };
    idsToRemove.forEach((id) => {
      delete nextManualSelections[id];
    });
    const dispatchQty = Number(state.dispatchQuantity) || 0;
    const appliedTotal = nextBatches.reduce((s, b) => s + (Number(b.quantity) || 0), 0);
    updateAllocationState(productId, {
      batches: nextBatches,
      catalogBatches: state.catalogBatches || [],
      manualSelections: nextManualSelections,
      fefoManuallyModified: false,
      isApplied: appliedTotal === dispatchQty && dispatchQty > 0,
      error: null,
    });
    if (method === 'MANUAL') {
      if (!(state.catalogBatches || []).length) {
        fetchManualBatchesForAllocation(productId);
      }
    } else if (method === 'FEFO') {
      fetchFEFOBatchesForAllocation(productId);
    }
  }, [allocationStateByProduct, updateAllocationState, fetchManualBatchesForAllocation, fetchFEFOBatchesForAllocation]);

  const addCurrentLineToDraft = async (batchDetailsForDraft) => {
    // Indent Reference Number is auto-generated, validation removed
    // if (!formData.indentReferenceNumber?.trim()) {
    //   toast.error('Indent Reference Number is required', toastOpt);
    //   return;
    // }
    if (!formData.productId) {
      toast.error('Product is required', toastOpt);
      return;
    }
    const qty = Number(formData.dispatchQuantity) || 0;
    if (qty <= 0) {
      toast.error('Dispatch Quantity must be greater than 0', toastOpt);
      return;
    }
    
    // Validate stock availability before adding to draft
    const batchTotal = (batchDetailsForDraft || []).reduce((sum, b) => sum + (b.quantity || 0), 0);
    if (batchTotal !== qty) {
      toast.error(`Batch allocation total (${batchTotal}) must equal dispatch quantity (${qty})`, toastOpt);
      return;
    }
    
    // Check if batches have sufficient stock
    const product = products.find((p) => p._id === formData.productId);
    if (!product) {
      toast.error('Product not found', toastOpt);
      return;
    }
    if (!isProductAvailableForOutward(product)) {
      toast.error('No available stock for this product', toastOpt);
      return;
    }
    const remainingQty = getProductRemainingQty(product);
    if (qty > remainingQty) {
      toast.error(`Insufficient stock. Available: ${remainingQty}, Requested: ${qty}`, toastOpt);
      return;
    }
    
    // Validate each batch has sufficient quantity
    for (const batch of batchDetailsForDraft || []) {
      if (batch.batchId) {
        try {
          const batchResult = await outwardService.getBatchesByProduct(formData.productId);
          const batchData = filterOutwardAvailableBatches(batchResult?.data || []).find((b) => b._id === batch.batchId);
          if (!batchData) {
            toast.error(`Batch ${batch.batchNumber || batch.batchId} not found for this product`, toastOpt);
            return;
          }
          if ((batchData.quantity || 0) < (batch.quantity || 0)) {
            const productSku = product.sku || formData.productId;
            toast.error(`Product ${productSku}: Batch ${batch.batchNumber || batch.batchId} has insufficient stock. Available: ${batchData.quantity}, Required: ${batch.quantity}`, toastOpt);
            return;
          }
        } catch (error) {
          console.error('Error validating batch:', error);
          toast.error(`Error validating batch ${batch.batchNumber || batch.batchId}`, toastOpt);
          return;
        }
      }
    }
    
    const selectedCompanyIds = toCompaniesPayload(formData.companies);
    const productCompanyIdSet = new Set(
      (product?.companies || [])
        .map((c) => String(c?._id || c?.id || c))
        .filter(Boolean)
    );
    const filteredCompanyIds = selectedCompanyIds.filter((cid) =>
      productCompanyIdSet.has(String(cid))
    );

    const item = {
     indentReferenceNumber: formData.indentReferenceNumber.trim(),
     invoiceDate: dateInputToIsoUtc(formData.invoiceDate) || undefined,
     vehicleNumber: formData.vehicleNumber?.trim() || '',
     // ❌ OLD
     // location: formData.location?.trim() || '',
     vehicleTemperature: formData.vehicleTemperature !== '' ? Number(formData.vehicleTemperature) : undefined,
     dockNumber: formData.dockNumber?.trim() || '',
     dockInTime: formData.dockInTime || '',
     dockOutTime: formData.dockOutTime || '',
     productTemperature: formData.productTemperature !== '' ? Number(formData.productTemperature) : undefined,
     reportingDate: formData.reportingDate || undefined,
     reportingTime: formData.reportingTime || "",
     companies: filteredCompanyIds,
     secondaryCompanies: Array.isArray(formData.secondaryCompanies) ? formData.secondaryCompanies : [],
     productId: formData.productId,
     dispatchQuantity: qty,
     allocationMethod: (formData.method || formData.allocationMethod || 'FEFO'),
     productDescription: formData.productDescription?.trim() || '',
     batchDetails: (batchDetailsForDraft || []).map(({ allocationSource, ...b }) => b),
   };
    setDraftItems((prev) => [...prev, item]);
    toast.success('Item added. Select another product or click Create to save.', toastOpt);
    resetProductFields();
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    setTransactions(outwardTransactionsResponse?.data || []);
  }, [outwardTransactionsResponse]);

  // Company-bound products:
  // - initial load: products remain EMPTY until company selected
  // - on company change: clear selections and fetch only that company's products
  useEffect(() => {
    const selectedCompanyIds = toCompaniesPayload(formData?.companies);

    // Edit mode uses only products from the selected Outward transaction (not putaway catalog).
    if (editingTransactionId || editingItemId) {
      return;
    }

    // Clear old products + selections when company changes
    setProducts([]);
    setProductCsvOrderIds([]);
    setDraftItems([]);
    setAllocationStateByProduct({});
    setFormData((prev) => ({
      ...prev,
      productId: '',
      productDescription: '',
      dispatchQuantity: 0,
      allocationMethod: 'FEFO',
      method: 'FEFO',
      batchDetails: [],
      productTable: {},
    }));

    fetchProducts(selectedCompanyIds);
  }, [getSelectedCompanyId(formData.companies), editingTransactionId, editingItemId]);

  // useEffect(() => {
  //   const t = setTimeout(() => fetchTransactions(), 300);
  //   return () => clearTimeout(t);
  // }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    const onClick = (e) => {
      if (dateRef.current?.contains(e.target)) return;
      setIsDateOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

 useEffect(() => {
  if (showForm && !editingTransactionId && !editingItemId && !indentFetchRef.current) {
    indentFetchRef.current = true;
    fetchNextIndentReference();
  }

  if (!showForm) {
    indentFetchRef.current = false;
    ornFetchInFlightRef.current = false;
  }
}, [showForm, editingTransactionId, editingItemId]);

  useEffect(() => {
    if (!showForm || editingTransactionId || editingItemId) return;

    let cancelled = false;
    const fetchNextOrn = async () => {
      if (ornFetchInFlightRef.current) return;
      ornFetchInFlightRef.current = true;
      setOrnLoading(true);
      setOrnError(null);
      try {
        const res = await outwardService.getNextOutwardRequisitionNumber();
        if (cancelled) return;
        setOutwardRequisitionNumber(
          res.outwardRequisitionNumber ||
            res.data?.outwardRequisitionNumber ||
            ""
        );
      } catch (err) {
        if (cancelled) return;
        const msg = err?.message || "Failed to load ORN";
        setOrnError(msg);
        toast.error(msg, toastOpt);
      } finally {
        ornFetchInFlightRef.current = false;
        if (!cancelled) setOrnLoading(false);
      }
    };
    fetchNextOrn();
    return () => {
      cancelled = true;
    };
  }, [showForm, editingTransactionId, editingItemId]);
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Auto-generate indent reference number when form opens (only for new transactions, not editing)
  // useEffect(() => {
  //   if (showForm && !editingTransactionId && !editingItemId) {
  //     fetchNextIndentReference();
  //   }
  // }, [showForm, editingTransactionId, editingItemId]);

  const fetchNextIndentReference = async () => {
    try {
      const response = await outwardService.getNextIndentReference();
      if (response.data?.indentReferenceNumber) {
        setFormData(prev => ({
          ...prev,
          indentReferenceNumber: response.data.indentReferenceNumber,
        }));
      }
    } catch (error) {
      console.error('Error fetching next indent reference:', error);
      // Fallback: generate locally if API fails
      const year = new Date().getFullYear();
      setFormData(prev => ({
        ...prev,
        indentReferenceNumber: `IND-001-${year}`,
      }));
    }
  };

  // Fetch batches when product is selected (for availableTotal validation). Don't auto-load batches for Manual.
  useEffect(() => {
    if (!formData.productId) {
      setManualBatches([]);
      setAvailableTotal(null);
      setManualBatchSelections({});
      setShowManualBatches(false);
      return;
    }
    const selectedProduct = (products || []).find((p) => String(p._id) === String(formData.productId));
    if (!isProductAvailableForOutward(selectedProduct)) {
      setManualBatches([]);
      setAvailableTotal(0);
      setManualBatchSelections({});
      setShowManualBatches(false);
      return;
    }
    
    let cancelled = false;
    outwardService
      .getBatchesByProduct(formData.productId)
      .then((res) => {
        if (cancelled || !res?.data) return;
        const existingBatchDetails = formData.batchDetails || [];
        const batchesWithAdjustedQty = filterOutwardAvailableBatches(res.data).map((batch) => {
          const existingBatch = existingBatchDetails.find((b) => String(b.batchId) === String(batch._id));
          if (existingBatch) {
            const alreadyAllocated = existingBatch.quantity || 0;
            return {
              ...batch,
              quantity: (batch.quantity || 0) + alreadyAllocated,
              alreadyAllocated,
            };
          }
          return batch;
        });
        const total = batchesWithAdjustedQty.reduce((sum, b) => sum + (b.quantity || 0), 0);
        setAvailableTotal(total);
        // Don't auto-set manualBatches - user will click "Add Batch" button
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [formData.productId, formData.batchDetails]);

  const fetchProducts = useCallback(async (companyIds) => {
    try {
      const list = Array.isArray(companyIds)
        ? companyIds.map(String).filter(Boolean)
        : (companyIds ? [String(companyIds).trim()] : []);
      if (list.length === 0) {
        setProducts([]);
        return;
      }
      const result = await outwardService.getApprovedPutawayProducts({
        companyIds: list.join(","),
      });
      const companyKey = getOutwardCompanyKey(list);
      const merged = mergeOutwardProductsForCompany(companyKey, result.data || []);
      setProducts(merged);
      await syncAllocationDispatchWithStock(merged);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [syncAllocationDispatchWithStock]);

  // Refresh approved putaway quantities whenever Create/Edit opens so totals match server (after create/delete/upload).
  useEffect(() => {
    if (!showForm) return;
    const selectedCompanyIds = toCompaniesPayload(formData?.companies);
    fetchProducts(selectedCompanyIds);
  }, [showForm, fetchProducts]);

  const fetchCompanies = async () => {
    try {
      const result = await companyService.getCompanies({ isActive: 'true' });
      const list = result.data || [];
      // OLD LOGIC (kept for reference):
      // setCompanies(
      //   Array.isArray(list)
      //     ? list.filter((c) => String(c?.contract?.status || "").toLowerCase() === "completed")
      //     : []
      // );
      setCompanies(
        Array.isArray(list) ? list : []
      );
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchTransactions = async () => {
    await refetchTransactions();
  };

  const toastOpt = {};

  const openView = async (row) => {
    const id = row?._id;
    if (!id) return;
    viewRowRef.current = row;
    setViewOpen(true);
    setViewLoading(true);
    setViewError(null);
    setViewData(null);
    try {
      const res = await outwardService.getOutwardTransactionById(id);
      const tx = res?.data || res;
      setViewData(tx);
    } catch (err) {
      setViewError(err?.message || 'Failed to load outward details');
    } finally {
      setViewLoading(false);
    }
  };

  const fetchFEFORecommendation = async () => {
    if (!formData.productId || !formData.dispatchQuantity) {
      toast.error('Please select product and enter quantity', toastOpt);
      return;
    }
    const qty = Number(formData.dispatchQuantity) || 0;
    const allocatedSoFar = (formData.batchDetails || []).reduce((sum, b) => sum + (b.quantity || 0), 0);
    const remainingToAllocate = Math.max(0, qty - allocatedSoFar);
    if (remainingToAllocate <= 0) {
      toast.error('Dispatch quantity already fulfilled. No more batches needed.', toastOpt);
      return;
    }
    // ❌ OLD (batch available based)
    // if (availableTotal != null && qty > availableTotal) {
    //   const product = products.find((p) => p._id === formData.productId);
    //   const productSku = product?.sku || formData.productId;
    //   toast.error(`Product ${productSku}: Insufficient stock. Available: ${availableTotal}, Required: ${qty}`, toastOpt);
    //   return;
    // }

    // ✅ NEW (TOTAL आधारित)
    {
      const product = products.find((p) => p._id === formData.productId);
      const productSku = product?.sku || formData.productId;
      if (!isProductAvailableForOutward(product)) {
        toast.error(`Product ${productSku} has no available stock`, toastOpt);
        return;
      }
      const maxQty = getProductRemainingQty(product);
      if (qty > maxQty) {
        toast.error(`Product ${productSku}: Dispatch qty cannot exceed available stock (${maxQty})`, toastOpt);
        return;
      }
    }
    try {
      const result = await outwardService.getFEFORecommendation(
        formData.productId,
        remainingToAllocate
      );
      const enriched = await enrichFefoBatchesWithCatalogAvailableQty(
        formData.productId,
        result.data?.batches || []
      );
      const batches = keepOnlyFirstEligibleFefoBatch(enriched);
      const totalAllocated = Number(result.data?.totalAllocated) || getFefoAllocatedQty(batches);
      const shortfall = Math.max(0, Number(result.data?.shortfall) || (remainingToAllocate - totalAllocated));
      if (shortfall > 0) {
        const product = products.find((p) => p._id === formData.productId);
        const productSku = product?.sku || formData.productId;
        toast.error(
          resolveFefoAllocationShortageMessage({
            requestedQty: remainingToAllocate,
            totalProductQty: getProductRemainingQty(product),
            fefoAvailableQty: totalAllocated,
            fefoTotalAvailable: Number(result.data?.totalAvailable),
            batches,
            shortfall,
          }) || `Product ${productSku}: Insufficient stock. Available: ${totalAllocated}, Required: ${qty}, Shortfall: ${shortfall}`,
          toastOpt
        );
        return;
      }
      setFefoRecommendation({
        ...result.data,
        batches,
        totalAllocated,
        shortfall,
      });
    } catch (error) {
      console.error('Error fetching FEFO recommendation:', error);
      const product = products.find((p) => p._id === formData.productId);
      const productSku = product?.sku || formData.productId;
      toast.error(`Error fetching FEFO recommendation for ${productSku}: ${error?.message || 'Unknown error'}`, toastOpt);
    }
  };

  const fetchBatchesForManual = async () => {
    if (!formData.productId) {
      toast.error('Please select a product first', toastOpt);
      return;
    }
    const product = products.find((p) => p._id === formData.productId);
    if (!isProductAvailableForOutward(product)) {
      toast.error('No available stock for this product', toastOpt);
      return;
    }
    if (showManualBatches && manualBatches.length > 0) {
      // If already showing, just toggle it closed
      setShowManualBatches(false);
      return;
    }
    try {
      const result = await outwardService.getBatchesByProduct(formData.productId);
      const list = filterOutwardAvailableBatches(result?.data || []);
      const existingBatchDetails = formData.batchDetails || [];
      const batchesWithAdjustedQty = list.map((batch) => {
        const existingBatch = existingBatchDetails.find((b) => String(b.batchId) === String(batch._id));
        if (existingBatch) {
          const alreadyAllocated = existingBatch.quantity || 0;
          return {
            ...batch,
            quantity: (batch.quantity || 0) + alreadyAllocated,
            alreadyAllocated,
          };
        }
        return batch;
      });
      setManualBatches(batchesWithAdjustedQty);
      const total = batchesWithAdjustedQty.reduce((sum, b) => sum + (b.quantity || 0), 0);
      setAvailableTotal(total);
      setShowManualBatches(true);
    } catch (error) {
      console.error('Error fetching batches:', error);
      toast.error('Failed to load batches', toastOpt);
    }
  };

  const allocatedSoFar = (formData.batchDetails || []).reduce((sum, b) => sum + (b.quantity || 0), 0);
  const dispatchQty = Number(formData.dispatchQuantity) || 0;
  const remainingToAllocate = Math.max(0, dispatchQty - allocatedSoFar);

  const applyFEFORecommendation = async () => {
    if (remainingToAllocate <= 0) {
      toast.error('Dispatch quantity already fulfilled. No more batches to add.', toastOpt);
      return;
    }
    if (fefoRecommendation && fefoRecommendation.batches && fefoRecommendation.batches.length > 0) {
      const existing = formData.batchDetails || [];
      let remaining = remainingToAllocate;
      const toAdd = [];
      for (const b of fefoRecommendation.batches) {
        if (remaining <= 0) break;
        const qty = Math.min(b.quantity || 0, remaining);
        if (qty > 0) {
          toAdd.push({
            ...b,
            quantity: qty,
            allocationSource: 'FEFO',
          });
          remaining -= qty;
        }
      }
      if (toAdd.length === 0) {
        toast.error('No batches to add. Dispatch quantity may already be fulfilled.', toastOpt);
        return;
      }
      const appended = [...existing, ...toAdd];
      const newTotal = appended.reduce((sum, b) => sum + (b.quantity || 0), 0);
      setFefoRecommendation(null);
      if (newTotal === dispatchQty) {
        await addCurrentLineToDraft(appended);
      } else {
        setFormData({ ...formData, batchDetails: appended });
        toast.success('Recommendation applied.', toastOpt);
      }
    }
  };

  const removeAppliedBatch = (index) => {
    const updated = (formData.batchDetails || []).filter((_, i) => i !== index);
    setFormData({ ...formData, batchDetails: updated });
  };

  const updateAppliedBatchQty = (index, newQty) => {
    const parsed = parseOutwardWholeNumberQtyInput(newQty);
    if (parsed === null) return;
    const qty = Math.max(0, parsed);
    const updated = (formData.batchDetails || []).map((b, i) =>
      i === index ? { ...b, quantity: qty } : b
    );
    setFormData({ ...formData, batchDetails: updated });
  };

  const handleManualBatchQtyChange = (batchId, value, maxAllowed) => {
    const parsed = parseOutwardWholeNumberQtyInput(value);
    if (parsed === null) return;
    let qty = Math.max(0, parsed);
    if (maxAllowed != null && maxAllowed >= 0) qty = Math.min(qty, maxAllowed);
    setManualBatchSelections((prev) => {
      const next = { ...prev };
      if (qty > 0) next[batchId] = qty;
      else delete next[batchId];
      return next;
    });
  };

  const applyManualBatches = async () => {
    const product = products.find((p) => p._id === formData.productId);
    const productSku = product?.sku || formData.productId;
    
    // ❌ OLD (batch available based)
    // if (availableTotal != null && dispatchQty > availableTotal) {
    //   toast.error(`Product ${productSku}: Insufficient stock. Available: ${availableTotal}, Required: ${dispatchQty}`, toastOpt);
    //   return;
    // }

    // ✅ NEW (TOTAL आधारित)
    {
      const maxQty = Number(product?.totalQty);
      if (Number.isFinite(maxQty) && dispatchQty > maxQty) {
        toast.error(`Product ${productSku}: Dispatch qty cannot exceed total qty (${maxQty})`, toastOpt);
        return;
      }
    }
    const selected = Object.entries(manualBatchSelections)
      .filter(([, qty]) => qty > 0)
      .map(([batchId, qty]) => {
        const batch = manualBatches.find((b) => b._id === batchId);
        if (!batch) {
          toast.error(`Batch ${batchId} not found`, toastOpt);
          return null;
        }
        if (qty > (batch.quantity || 0)) {
          toast.error(`Product ${productSku}: Batch ${batch.batchNumber || batchId} has insufficient stock. Available: ${batch.quantity}, Required: ${qty}`, toastOpt);
          return null;
        }
        return {
          batchId: batch._id,
          batchNumber: batch.batchNumber,
          manufacturingDate: batch.manufacturingDate,
          expiryDate: batch.expiryDate,
          quantity: Math.min(qty, batch.quantity),
          binId: batch.binId,
          allocationSource: 'MANUAL',
        };
      })
      .filter(Boolean);
    console.log('[Outward][Manual] selectedBatches before merge', {
      selectedBatches: selected,
      existingBatchDetails: formData.batchDetails || [],
    });

    if (selected.length === 0) {
      toast.error('Select quantity for at least one batch', toastOpt);
      return;
    }
    const selectedTotal = selected.reduce((s, b) => s + b.quantity, 0);
    if (selectedTotal > remainingToAllocate) {
      toast.error(`Total selected (${selectedTotal}) cannot exceed remaining to allocate (${remainingToAllocate})`, toastOpt);
      return;
    }
    // ❌ OLD (batch available based)
    // if (availableTotal != null && selectedTotal > availableTotal) {
    //   toast.error(`Product ${productSku}: Insufficient stock. Available: ${availableTotal}, Selected: ${selectedTotal}`, toastOpt);
    //   return;
    // }

    const existing = formData.batchDetails || [];
    const appended = [...existing, ...selected];
    const newTotal = appended.reduce((sum, b) => sum + (b.quantity || 0), 0);
    setManualBatchSelections({});
    if (newTotal === dispatchQty) {
      await addCurrentLineToDraft(appended);
    } else {
      setFormData({ ...formData, batchDetails: appended });
      toast.success('Manual batches applied.', toastOpt);
    }
  };

  const removeDraftItem = (index) => {
    setDraftItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateDraftItemDispatchQty = (index, newQty) => {
    const parsed = parseOutwardWholeNumberQtyInput(newQty);
    if (parsed === null) return;
    const qty = Math.max(0, parsed);
    setDraftItems((prev) => prev.map((item, i) => 
      i === index ? { ...item, dispatchQuantity: qty } : item
    ));
  };

  const updateDraftItemBatchQty = (itemIndex, batchIndex, newQty) => {
    const parsed = parseOutwardWholeNumberQtyInput(newQty);
    if (parsed === null) return;
    const qty = Math.max(0, parsed);
    setDraftItems((prev) => prev.map((item, i) => {
      if (i !== itemIndex) return item;
      const updatedBatches = (item.batchDetails || []).map((b, bi) => 
        bi === batchIndex ? { ...b, quantity: qty } : b
      );
      return { ...item, batchDetails: updatedBatches };
    }));
  };

  const removeDraftItemBatch = (itemIndex, batchIndex) => {
    setDraftItems((prev) => prev.map((item, i) => {
      if (i !== itemIndex) return item;
      const updatedBatches = (item.batchDetails || []).filter((_, bi) => bi !== batchIndex);
      return { ...item, batchDetails: updatedBatches };
    }));
  };

  const buildItemsFromProductTable = async () => {
    const globalMethod = String(formData.method || '').toUpperCase() || 'FEFO';
    if (!['FEFO', 'MANUAL'].includes(globalMethod)) {
      return { items: [], errors: [{ sku: '', error: 'Valid method is required (FEFO or MANUAL)' }] };
    }
    const header = {
      indentReferenceNumber: formData.indentReferenceNumber?.trim() || '',
      invoiceDate: dateInputToIsoUtc(formData.invoiceDate) || undefined,
      vehicleNumber: formData.vehicleNumber?.trim() || '',
      // ❌ OLD
      // location: formData.location?.trim() || '',
      vehicleTemperature: formData.vehicleTemperature !== '' ? Number(formData.vehicleTemperature) : undefined,
      dockNumber: formData.dockNumber?.trim() || '',
      dockInTime: formData.dockInTime || '',
      dockOutTime: formData.dockOutTime || '',
      productTemperature: formData.productTemperature !== '' ? Number(formData.productTemperature) : undefined,
      reportingDate: formData.reportingDate || undefined,
      reportingTime: formData.reportingTime || "",
      secondaryCompanies: Array.isArray(formData.secondaryCompanies) ? formData.secondaryCompanies : [],
      method: globalMethod,
    };
    const items = [];
    const errors = [];

    // Create-from-table: use allocation state (applied batches only)
    if (!editingTransactionId && !editingItemId) {
      for (const p of selectedProductRowsFromAllocation) {
        const state = allocationStateByProduct[p._id] || {};
        if (!state.isApplied) {
          errors.push({ sku: p.sku, error: 'Apply allocation first' });
          continue;
        }
        const dispatchQty = Number(state.dispatchQuantity) || 0;
        if (dispatchQty <= 0) {
          errors.push({ sku: p.sku, error: 'Dispatch quantity required' });
          continue;
        }
        if (!Number.isInteger(dispatchQty)) {
          errors.push({ sku: p.sku, error: 'Dispatch quantity must be a whole number' });
          continue;
        }
        if (!isProductAvailableForOutward(p)) {
          errors.push({ sku: p.sku, error: 'No available stock for this product' });
          continue;
        }
        const remainingQty = getProductRemainingQty(p);
        if (dispatchQty > remainingQty) {
          errors.push({
            sku: p.sku,
            error: formatDispatchStockShortageMessage({
              onHand: remainingQty,
              requested: dispatchQty,
              maxAllowed: remainingQty,
              isEdit: false,
            }),
          });
          continue;
        }
        const method = globalMethod;
        const selectedCompanyIds = toCompaniesPayload(formData.companies);
        const productCompanyIdSet = new Set(
          (p?.companies || [])
            .map((c) => String(c?._id || c?.id || c))
            .filter(Boolean)
        );
        const filteredCompanyIds = selectedCompanyIds.filter((cid) =>
          productCompanyIdSet.has(String(cid))
        );
        const productDescription = (state.productDescription != null && String(state.productDescription).trim()) ? String(state.productDescription).trim() : (p.description || '');
        const batches = state.batches || [];
        const batchDetails = batches.map((b) => ({
          batchId: b.batchId || b._id,
          batchNumber: b.batchNumber,
          manufacturingDate: b.manufacturingDate,
          expiryDate: b.expiryDate,
          quantity: Number(b.quantity) || 0,
          binId: b.binId,
        }));
        const totalBatch = batchDetails.reduce((s, b) => s + b.quantity, 0);
        if (totalBatch !== dispatchQty) {
          errors.push({ sku: p.sku, error: `Batch total (${totalBatch}) must equal dispatch (${dispatchQty})` });
          continue;
        }
        items.push({
          ...header,
          companies: filteredCompanyIds,
          productId: p._id,
          dispatchQuantity: dispatchQty,
          allocationMethod: method,
          productDescription,
          batchDetails,
        });
      }
      return { items, errors };
    }

    // Legacy: edit flow using productTable
    const pt = formData.productTable || {};
    for (const p of selectedProductRows) {
      const row = pt[p._id] || {};
      const dispatchQty = Number(row.dispatchQuantity) || 0;
      if (dispatchQty <= 0) {
        errors.push({ sku: p.sku, error: 'Dispatch quantity required' });
        continue;
      }
      if (!Number.isInteger(dispatchQty)) {
        errors.push({ sku: p.sku, error: 'Dispatch quantity must be a whole number' });
        continue;
      }
      if (!isProductAvailableForOutward(p)) {
        errors.push({ sku: p.sku, error: 'No available stock for this product' });
        continue;
      }
      const remainingQty = getProductRemainingQty(p);
      if (dispatchQty > remainingQty) {
        errors.push({
          sku: p.sku,
          error: formatDispatchStockShortageMessage({
            onHand: remainingQty,
            requested: dispatchQty,
            maxAllowed: remainingQty,
            isEdit: false,
          }),
        });
        continue;
      }
      const method = globalMethod;
      const selectedCompanyIds = toCompaniesPayload(formData.companies);
      const productCompanyIdSet = new Set(
        (p?.companies || [])
          .map((c) => String(c?._id || c?.id || c))
          .filter(Boolean)
      );
      const filteredCompanyIds = selectedCompanyIds.filter((cid) =>
        productCompanyIdSet.has(String(cid))
      );
      const productDescription = (row.productDescription != null && String(row.productDescription).trim()) ? String(row.productDescription).trim() : (p.description || '');
      if (method === 'FEFO') {
        const fefoPreview = row.fefoBatchPreview || [];
        const shortfall = row.fefoShortfall ?? 0;
        if (shortfall > 0) {
          errors.push({ sku: p.sku, error: 'FEFO: insufficient stock / shortfall' });
          continue;
        }
        const fefoTotal = fefoPreview.reduce((s, b) => s + (Number(b.quantity) || 0), 0);
        if (fefoPreview.length === 0 || fefoTotal !== dispatchQty) {
          errors.push({ sku: p.sku, error: 'FEFO: allocation preview must match dispatch qty' });
          continue;
        }
        const batchDetails = fefoPreview.map((b) => ({
          batchId: b.batchId || b._id,
          batchNumber: b.batchNumber,
          manufacturingDate: b.manufacturingDate,
          expiryDate: b.expiryDate,
          quantity: b.quantity || 0,
          binId: b.binId,
        }));
        items.push({
          ...header,
          companies: filteredCompanyIds,
          productId: p._id,
          dispatchQuantity: dispatchQty,
          allocationMethod: 'FEFO',
          productDescription,
          batchDetails,
        });
      } else {
        const manualSelections = row.manualBatchSelections || {};
        const manualBatchesList = row.manualBatches || [];
        const totalManual = Object.values(manualSelections).reduce((s, q) => s + (Number(q) || 0), 0);
        if (totalManual !== dispatchQty) {
          errors.push({ sku: p.sku, error: 'Manual: total batch qty must equal dispatch qty' });
          continue;
        }
        const batchDetails = [];
        for (const [batchId, qty] of Object.entries(manualSelections)) {
          const q = Number(qty) || 0;
          if (q <= 0) continue;
          const batch = manualBatchesList.find((b) => String(b._id) === String(batchId));
          if (!batch) {
            errors.push({ sku: p.sku, error: `Batch ${batchId} not found` });
            break;
          }
          if ((batch.quantity || 0) < q) {
            errors.push({ sku: p.sku, error: `Batch ${batch.batchNumber || batchId} insufficient stock` });
            break;
          }
          batchDetails.push({
            batchId: batch._id,
            batchNumber: batch.batchNumber,
            manufacturingDate: batch.manufacturingDate,
            expiryDate: batch.expiryDate,
            quantity: q,
            binId: batch.binId,
          });
        }
        if (batchDetails.length === Object.keys(manualSelections).filter((k) => (Number(manualSelections[k]) || 0) > 0).length) {
          items.push({
            ...header,
            companies: filteredCompanyIds,
            productId: p._id,
            dispatchQuantity: dispatchQty,
            allocationMethod: 'MANUAL',
            productDescription,
            batchDetails,
          });
        }
      }
    }
    return { items, errors };
  };

  const handleFinalCreate = async () => {
    const requireSecondary =
      Boolean(getSelectedCompanyId(formData.companies)) &&
      (companies || []).some((c) => {
        const cid = String(c._id || c.id);
        if (getSelectedCompanyId(formData.companies) !== cid) return false;
        const sec = Array.isArray(c.secondaryCustomers) ? c.secondaryCustomers : [];
        return sec.some((sc) => sc?.isActive !== false);
      });
    const requiredFieldErrors = validateOutwardRequiredFields(formData, {
      requireSecondary,
    });
    if (Object.keys(requiredFieldErrors).length > 0) {
      const nextTouched = {};
      for (const key of Object.keys(requiredFieldErrors)) {
        nextTouched[key] = true;
      }
      setFormErrors(requiredFieldErrors);
      setFormTouched(nextTouched);
      toast.error(Object.values(requiredFieldErrors)[0], toastOpt);
      return;
    }
    setFormErrors({});

    // Item-row required checks for selected / edit products
    const itemRowErrors = [];
    const isCreateFromTableCheck = !editingTransactionId && !editingItemId;
    const productsToCheck = isCreateFromTableCheck
      ? selectedProductRowsFromAllocation
      : (productsForTable || []).filter((p) =>
          (editingTransactionProductIds || []).map(String).includes(String(p._id))
        );
    for (const p of productsToCheck) {
      const state = allocationStateByProduct[p._id] || {};
      const sku = String(state.skuDraft ?? p.sku ?? '').trim();
      if (!sku) {
        itemRowErrors.push({
          productId: p._id,
          message: 'Item Code is required.',
        });
        continue;
      }
      const qty = Number(state.dispatchQuantity) || 0;
      if (qty <= 0) {
        itemRowErrors.push({
          productId: p._id,
          message: 'Quantity is required.',
        });
        continue;
      }
      if (!Number.isInteger(qty)) {
        itemRowErrors.push({
          productId: p._id,
          message: 'Quantity must be a whole number.',
        });
        continue;
      }
      const batches = (state.batches || []).filter(
        (b) => (Number(b.quantity) || 0) > 0
      );
      if (batches.length === 0) {
        itemRowErrors.push({
          productId: p._id,
          message: 'Batch Number is required.',
        });
        continue;
      }
      const missingBatchNumber = batches.some(
        (b) => !String(b.batchNumber || '').trim()
      );
      if (missingBatchNumber) {
        itemRowErrors.push({
          productId: p._id,
          message: 'Batch Number is required.',
        });
      }
    }
    if (productsToCheck.length === 0 && (draftItems || []).length > 0) {
      for (const item of draftItems) {
        if (isBlankOutwardField(item?.productId)) {
          itemRowErrors.push({
            productId: item?.productId || 'draft',
            message: 'Item Code is required.',
          });
          break;
        }
        if (!(Number(item?.dispatchQuantity) > 0)) {
          itemRowErrors.push({
            productId: item?.productId || 'draft',
            message: 'Quantity is required.',
          });
          break;
        }
        if (!Number.isInteger(Number(item.dispatchQuantity))) {
          itemRowErrors.push({
            productId: item?.productId || 'draft',
            message: 'Quantity must be a whole number.',
          });
          break;
        }
        const batches = (item?.batchDetails || []).filter(
          (b) => (Number(b.quantity) || 0) > 0
        );
        if (
          batches.length === 0 ||
          batches.some((b) => !String(b.batchNumber || '').trim())
        ) {
          itemRowErrors.push({
            productId: item?.productId || 'draft',
            message: 'Batch Number is required.',
          });
          break;
        }
      }
    }
    if (itemRowErrors.length > 0) {
      setErrors((prev) => {
        const others = (prev || []).filter(
          (e) =>
            !itemRowErrors.some(
              (ie) => String(ie.productId) === String(e?.productId)
            )
        );
        return [...others, ...itemRowErrors];
      });
      toast.error(itemRowErrors[0].message, toastOpt);
      return;
    }

    const isCreateFromTable = !editingTransactionId && !editingItemId;
    let itemsToCreate = draftItems;
    if (isCreateFromTable) {
      // Backward-compatible fallback:
      // if table rows are not selected but manual flow already built draft items,
      // allow Save to proceed with draftItems.
      if (selectedProductRowsFromAllocation.length === 0) {
        if ((draftItems || []).length === 0) {
          toast.error('Please select product(s) first.', toastOpt);
          return;
        }
        itemsToCreate = draftItems;
      } else {
        if (!allSelectedApplied) {
          const missingStockSkus = selectedProductRowsFromAllocation
            .filter((p) => !allocationStateByProduct[p._id]?.isApplied)
            .map((p) => {
              const state = allocationStateByProduct[p._id] || {};
              return String(state.skuDraft ?? p.sku ?? '').trim();
            })
            .filter(Boolean);
          toast.error(
            `Please select stock for: ${missingStockSkus.join(', ')} before creating the transaction.`,
            toastOpt
          );
          return;
        }
        const { items, errors } = await buildItemsFromProductTable();
        if (errors.length > 0) {
          const msg = errors.slice(0, 3).map((e) => `${e.sku}: ${e.error}`).join('; ');
          toast.error(msg, toastOpt);
          return;
        }
        if (items.length === 0) {
          toast.error('Dispatch quantity required and allocation must be complete for each selected row.', toastOpt);
          return;
        }
        itemsToCreate = items;
      }
    } else if (draftItems.length === 0) {
      toast.error('Apply FEFO or Manual allocation first to add items, then click Create.', toastOpt);
      return;
    }

    const activeDispatchValidationIds = new Set(
      (
        editingTransactionId || editingItemId
          ? (editingTransactionProductIds || []).map(String).filter(Boolean)
          : selectedProductRowsFromAllocation.map((p) => String(p._id))
      )
    );
    const blockingDispatchErrors = (errors || []).filter(
      (e) =>
        e?.message &&
        (activeDispatchValidationIds.size === 0 ||
          activeDispatchValidationIds.has(String(e.productId)))
    );
    if (blockingDispatchErrors.length > 0) {
      toast.error(blockingDispatchErrors[0].message, toastOpt);
      return;
    }

    const globalMethod = String(formData.method || '').toUpperCase() || 'FEFO';
    if (!['FEFO', 'MANUAL'].includes(globalMethod)) {
      toast.error('Valid method is required (FEFO or MANUAL)', toastOpt);
      return;
    }

    itemsToCreate = (itemsToCreate || []).map((item) => ({
      ...item,
      method: globalMethod,
      allocationMethod: globalMethod, // backward compatible
    }));
    console.log('[Outward] final payload items before API call', {
      totalItems: (itemsToCreate || []).length,
      itemsToCreate,
    });

    const isEditMode = !!(editingTransactionId || editingItemId);
    const postingDateValue = formData.invoiceDate ?? "";
    if (!postingDateValue || String(postingDateValue).trim() === "") {
      toast.error("Please select posting date", toastOpt);
      return;
    }

    const postingDateCheckOptions = isEditMode
      ? {
          isEditMode: true,
          originalDateYmd: normalizeYmdIST(
            latestEditTransactionRef.current?.invoiceDate,
          ),
        }
      : {};

    if (!isPostingDateAllowed(postingDateValue, permissionState, postingDateCheckOptions)) {
      if (isEditMode) {
        toast.error(
          "Posting date is not allowed. Check approved date ranges or select a date within the last 3 days.",
          toastOpt,
        );
        return;
      }
      openPermissionRequestModal(resolvePostingYmdForPermissionModal(postingDateValue), {
        closeCreateForm: true,
      });
      return;
    }

    const invoiceDateIso = dateInputToIsoUtc(formData.invoiceDate);
    if (!invoiceDateIso) {
      toast.error("Please select posting date", toastOpt);
      return;
    }
    itemsToCreate = (itemsToCreate || []).map((item) => ({
      ...item,
      invoiceDate: invoiceDateIso,
    }));

    setLoading(true);
    try {
      if (!editingTransactionId && !editingItemId) {
        if (!outwardRequisitionNumber) {
          toast.error(
            ornError || "ORN is not ready. Please wait or close and reopen the form.",
            toastOpt
          );
          setLoading(false);
          return;
        }
      }
      if (editingTransactionId && editingItemId) {
        const item = draftItems[0];
        if (!item) {
          toast.error('No item data to update', toastOpt);
          setLoading(false);
          return;
        }
        const pid = String(formData.productId || item.productId || '');
        const editState = allocationStateByProduct[pid] || {};
        const dispatchQtyToUse = Number(editState.dispatchQuantity) || Number(formData.dispatchQuantity) || Number(item.dispatchQuantity) || 0;
        const batchDetailsToUse = (editState.batches || [])
          .map((b) => ({
            batchId: b.batchId || b._id,
            batchNumber: b.batchNumber,
            manufacturingDate: b.manufacturingDate,
            expiryDate: b.expiryDate,
            quantity: Number(b.quantity) || 0,
            binId: b.binId,
          }))
          .filter((b) => b.quantity > 0);
        const batchTotal = batchDetailsToUse.reduce((sum, b) => sum + b.quantity, 0);
        if (batchTotal !== dispatchQtyToUse) {
          toast.error(`Batch allocation total (${batchTotal}) must equal dispatch quantity (${dispatchQtyToUse})`, toastOpt);
          setLoading(false);
          return;
        }
        for (const batch of editState.batches || []) {
          const qty = Number(batch.quantity) || 0;
          if (qty <= 0) continue;
          const maxAllowed = getEditBatchMaxAllocQty(batch);
          if (qty > maxAllowed) {
            toast.error(
              `Batch ${batch.batchNumber || batch.batchId}: allocated qty (${qty}) exceeds available (${maxAllowed})`,
              toastOpt
            );
            setLoading(false);
            return;
          }
        }
        try {
          await outwardService.updateOutwardItem(editingTransactionId, editingItemId, {
            productId: pid,
            dispatchQuantity: dispatchQtyToUse,
            allocationMethod:
              editState.allocationMethod ||
              formData.method ||
              formData.allocationMethod ||
              item.allocationMethod ||
              'FEFO',
            productDescription: formData.productDescription?.trim() || '',
            batchDetails: batchDetailsToUse,
          });
          // Also update transaction-level fields (vehicleNumber, indentReferenceNumber) when editing single item
          await outwardService.updateOutwardTransaction(editingTransactionId, {
            indentReferenceNumber: formData.indentReferenceNumber?.trim() || '',
            invoiceDate: invoiceDateIso,
            vehicleNumber: formData.vehicleNumber?.trim() || '',
            // ❌ OLD
            // location: formData.location?.trim() || '',
            vehicleTemperature: formData.vehicleTemperature !== '' ? Number(formData.vehicleTemperature) : undefined,
            dockNumber: formData.dockNumber?.trim() || '',
            dockInTime: formData.dockInTime || '',
            dockOutTime: formData.dockOutTime || '',
            productTemperature: formData.productTemperature !== '' ? Number(formData.productTemperature) : undefined,
            reportingDate: formData.reportingDate || undefined,
            reportingTime: formData.reportingTime || "",
            companies: toCompaniesPayload(formData.companies),
            secondaryCompanies: Array.isArray(formData.secondaryCompanies) ? formData.secondaryCompanies : [],

          });
          toast.success('Outward item updated successfully');
          setEditingTransactionId(null);
          setEditingItemId(null);
          resetFormState();
          setShowForm(false);
          fetchTransactions();
        } catch (error) {
          console.error('Error updating outward item:', error);
          toast.error(error?.response?.data?.error || error?.message || 'Failed to update outward item');
          setLoading(false);
        }
        return;
      } else if (editingTransactionId) {
        const items = draftItems.map((item, index) => ({
          lineNo: index + 1,
          productId: item.productId,
          dispatchQuantity: item.dispatchQuantity,
          allocationMethod: item.allocationMethod || 'FEFO',
          productDescription: item.productDescription || '',
          batchDetails: (item.batchDetails || []).map((b) => ({
            batchId: b.batchId,
            batchNumber: b.batchNumber,
            manufacturingDate: b.manufacturingDate,
            expiryDate: b.expiryDate,
            quantity: b.quantity,
            binId: b.binId,
          })),
          status: item.status || 'PENDING',
        }));
        try {
          await outwardService.updateOutwardTransaction(editingTransactionId, {
            indentReferenceNumber: formData.indentReferenceNumber?.trim() || '',
            invoiceDate: invoiceDateIso,
            vehicleNumber: formData.vehicleNumber?.trim() || '',
            // ❌ OLD
            // location: formData.location?.trim() || '',
            vehicleTemperature: formData.vehicleTemperature !== '' ? Number(formData.vehicleTemperature) : undefined,
            dockNumber: formData.dockNumber?.trim() || '',
            dockInTime: formData.dockInTime || '',
            dockOutTime: formData.dockOutTime || '',
            productTemperature: formData.productTemperature !== '' ? Number(formData.productTemperature) : undefined,
            reportingDate: formData.reportingDate || undefined,
            reportingTime: formData.reportingTime || "",
            companies: toCompaniesPayload(formData.companies),
            items,
            secondaryCompanies: Array.isArray(formData.secondaryCompanies) ? formData.secondaryCompanies : [],
          });
          toast.success('Outward transaction updated successfully');
          setEditingTransactionId(null);
          resetFormState();
          setShowForm(false);
          fetchTransactions();
        } catch (error) {
          console.error('Error updating outward transaction:', error);
          toast.error(error?.response?.data?.error || error?.message || 'Failed to update outward transaction');
          setLoading(false);
        }
        return;
      } else {
        const selectedCompanyIds = toCompaniesPayload(formData?.companies);
        if (selectedCompanyIds.length > 0) {
          const hasNonOnboardedSelection = selectedCompanyIds.some(
            (selectedCompany) => {
              const company = (companies || []).find(
                (c) => String(c._id || c.id) === String(selectedCompany)
              );
              return (
                (company?.contract?.onboardStatus || "pending") !== "completed"
              );
            }
          );
          if (hasNonOnboardedSelection) {
            toast.error("Selected company is not onboarded");
            setLoading(false);
            return;
          }
        }
        // Validate all items first before creating any
        const validationErrors = [];
        let stockProducts = products;
        if (selectedCompanyIds.length > 0) {
          try {
            const freshRes = await outwardService.getApprovedPutawayProducts({
              companyIds: selectedCompanyIds.join(','),
            });
            const companyKey = getOutwardCompanyKey(selectedCompanyIds);
            stockProducts = mergeOutwardProductsForCompany(
              companyKey,
              freshRes?.data || []
            );
            setProducts(stockProducts);
            await syncAllocationDispatchWithStock(stockProducts);
          } catch {
            stockProducts = products || [];
          }
        } else {
          stockProducts = products || [];
        }
        const requestedByProductId = {};
        for (const item of itemsToCreate || []) {
          const pid = String(item.productId || '');
          if (!pid) continue;
          requestedByProductId[pid] =
            (requestedByProductId[pid] || 0) + (Number(item.dispatchQuantity) || 0);
        }
        for (const [pid, reqQty] of Object.entries(requestedByProductId)) {
          const product = stockProducts.find((p) => String(p._id) === pid);
          const productSku = product?.sku || pid;
          if (!product) {
            toast.error(`Product ${productSku} was not found for the selected company.`, toastOpt);
            setLoading(false);
            return;
          }
          const available = getProductRemainingQty(product);
          if (available <= 0) {
            toast.error(`Product ${productSku} has no available stock.`, toastOpt);
            setLoading(false);
            return;
          }
          if (reqQty > available) {
            toast.error(
              `Insufficient stock for SKU ${productSku}. Available: ${available}, Requested: ${reqQty}`,
              toastOpt
            );
            setLoading(false);
            return;
          }
        }
        for (const item of itemsToCreate) {
          const product = stockProducts.find((p) => p._id === item.productId);
          const productSku = product?.sku || item.productId;
          
          if (!product) {
            validationErrors.push({ item, error: `Product ${productSku} not found` });
            continue;
          }
          
          // Check batch availability
          try {
            const batchResult = await outwardService.getBatchesByProduct(item.productId);
            const batchList = filterOutwardAvailableBatches(batchResult?.data || []);
            const totalAvailable = batchList.reduce((sum, b) => sum + (b.quantity || 0), 0);
            
            if (totalAvailable < item.dispatchQuantity) {
              validationErrors.push({ 
                item, 
                error: `Insufficient stock for ${productSku}. Available: ${totalAvailable}, Required: ${item.dispatchQuantity}` 
              });
              continue;
            }
            
            // Validate each batch has sufficient quantity
            for (const batch of item.batchDetails || []) {
              if (batch.batchId) {
                const batchData = batchList.find((b) => b._id === batch.batchId);
                if (!batchData) {
                  validationErrors.push({ 
                    item, 
                    error: `Batch ${batch.batchNumber || batch.batchId} not found for ${productSku}` 
                  });
                  break;
                }
                if ((batchData.quantity || 0) < (batch.quantity || 0)) {
                  validationErrors.push({ 
                    item, 
                    error: `${productSku}: Batch ${batch.batchNumber || batch.batchId} insufficient stock. Available: ${batchData.quantity}, Required: ${batch.quantity}` 
                  });
                  break;
                }
              }
            }
          } catch (error) {
            validationErrors.push({ 
              item, 
              error: `Validation error for ${productSku}: ${error?.message || 'Unknown error'}` 
            });
          }
        }
        
        // If any validation fails, show failed items and don't create any
        if (validationErrors.length > 0) {
          setFailedItems(validationErrors.map((e) => ({ ...e.item, errorMessage: e.error })));
          toast.error(`${validationErrors.length} item(s) failed validation. Please check and fix before creating.`, toastOpt);
          setLoading(false);
          return;
        }
        
        // All validations passed, create all items (all or nothing)
        let firstError = null;
        const createdItems = [];
        
        for (const item of itemsToCreate) {
          try {
            console.log('[Outward] createOutwardTransaction request item', item);
            await outwardService.createOutwardTransaction(item);
            createdItems.push(item);
          } catch (error) {
            firstError = error;
            break; // Stop on first error (all or nothing)
          }
        }
        
        if (firstError) {
          // If any creation fails, mark all as failed
          const product = products.find((p) => p._id === itemsToCreate[createdItems.length]?.productId);
          const productSku = product?.sku || itemsToCreate[createdItems.length]?.productId || 'Unknown';
          const d = firstError?.details;
          let errorMsg = d && (d.sku != null || d.batchNumber != null || d.requiredQty != null || d.availableQty != null || d.shortQty != null)
            ? `SKU ${d.sku ?? '—'} | Batch ${d.batchNumber ?? '—'} | Required ${d.requiredQty ?? '—'} | Available ${d.availableQty ?? '—'} | Short ${d.shortQty ?? '—'}`
            : (firstError?.response?.data?.error || firstError?.message || 'Unknown error');
          errorMsg = replaceBinIdWithBinCodeInErrorMessage(errorMsg, products);
          
          setFailedItems(itemsToCreate.map((item) => ({ 
            ...item, 
            errorMessage: `Failed: ${errorMsg} (Product ${productSku})` 
          })));
          toast.error(`All items failed to create. ${errorMsg}`, toastOpt);
        } else {
          toast.success('Outward transaction created successfully');
          resetFormState();
          setDraftItems([]);
          setFailedItems([]);
          setShowForm(false);
          fetchTransactions();
          fetchProducts(selectedCompanyIds);
          return;
        }
        
        // Reset form after failure
        resetFormState();
        setShowForm(false);
        fetchTransactions();
      }
    } catch (error) {
      console.error('Error creating/updating outward transaction:', error);
      const d = error?.details;
      const errorDisplay = d && (d.sku != null || d.batchNumber != null || d.requiredQty != null || d.availableQty != null || d.shortQty != null)
        ? `SKU ${d.sku ?? '—'} | Batch ${d.batchNumber ?? '—'} | Required ${d.requiredQty ?? '—'} | Available ${d.availableQty ?? '—'} | Short ${d.shortQty ?? '—'}`
        : (error?.response?.data?.error || error?.message || 'Failed to save outward transaction');
      // Mark all items as failed
      const itemsForError = (editingTransactionId || editingItemId) ? draftItems : itemsToCreate;
      setFailedItems((itemsForError || []).map((item) => ({ 
        ...item, 
        errorMessage: error?.response?.data?.error || error?.message || 'Failed to save outward transaction' 
      })));
      // Use default toast container for error so it persists after form closes
      toast.error(errorDisplay);
      resetFormState();
      setShowForm(false);
      fetchTransactions();
    } finally {
      setLoading(false);
    }
  };

  const handleClosePopup = () => {
    resetFormState();
    setDraftItems([]);
    setFailedItems([]);
    setEditingTransactionId(null);
    setEditingItemId(null);
    setShowForm(false);
    if (productUploadPreviewOpen) resetProductUploadPreview();
    if (uploadPreviewOpen) resetUploadPreview();
  };

  const handleSubmit = () => {
    handleFinalCreate();
  };

  const handleExportProductsTemplate = async () => {
    if (!products || products.length === 0) {
      toast.error('No products available to export please choose a company', toastOpt);
      return;
    }
    const allocationMethod = String(formData.method || 'FEFO').toUpperCase();
    let exportProducts = (products || []).map((p) => {
      const state = allocationStateByProduct[p._id] || {};
      return {
        ...p,
        dispatchQuantity: state.dispatchQuantity || '',
        batches: Array.isArray(state.batches) ? state.batches : [],
      };
    });
    if (allocationMethod === 'MANUAL') {
      exportProducts = await Promise.all(
        exportProducts.map(async (p) => {
          if (p.batches.length > 0) return p;
          try {
            const res = await outwardService.getBatchesByProduct(p._id);
            const list = filterOutwardAvailableBatches(Array.isArray(res?.data) ? res.data : []);
            return {
              ...p,
              batches: list.map((b) => ({
                batchId: b.batchId || b._id,
                batchNumber: b.batchNumber,
                availableQty: b.quantity ?? 0,
                quantity: b.quantity ?? 0,
              })),
            };
          } catch {
            return p;
          }
        })
      );
    }
    downloadOutwardProductsTemplate(exportProducts, { allocationMethod });
    toast.success('Products template downloaded. Fill editable columns and upload to pre-fill table.', toastOpt);
  };

  const getProductSku = (productId) => {
    const product = products.find((p) => p._id === productId);
    return product?.sku || productId;
  };

  const handleEdit = async (row) => {
    const transactionId = row._id || row.id;
    const itemId = row.itemId;
    if (!transactionId || !itemId) return;
    try {
      const res = await outwardService.getOutwardTransactionById(transactionId);
      const transaction = res?.data || res;
      latestEditTransactionRef.current = transaction;
      const items = transaction.items || [];
      const itemToEdit = items.find((it) => String(it._id) === String(itemId));
      if (!itemToEdit) {
        toast.error('Item not found', toastOpt);
        return;
      }
      const originalCompanyIds = (transaction.companies || []).map((c) => c._id || c.id);
      const nextCompanyId = getSelectedCompanyId(originalCompanyIds);
      const nextCompanyIds = toCompaniesPayload(nextCompanyId);
      const productIdInItem = String(itemToEdit.productId?._id || itemToEdit.productId);

      let stockRow = null;
      try {
        const stockRes = await outwardService.getApprovedPutawayProducts({
          companyIds: nextCompanyIds.join(','),
        });
        stockRow = (stockRes?.data || []).find((p) => String(p._id) === productIdInItem) || null;
      } catch (_) {
        stockRow = null;
      }

      const editProduct = buildOutwardEditProductFromItem(itemToEdit, stockRow);
      setOutwardEditProducts([editProduct]);

      const originalAlloc = {};
      (itemToEdit.batchDetails || []).forEach((b) => {
        const id = String(b.batchId?._id || b.batchId || '');
        if (id) originalAlloc[id] = Number(b.quantity) || 0;
      });
      originalBatchAllocByProductRef.current = {
        [productIdInItem]: originalAlloc,
      };

      const editBatches = await buildEditModeBatches(productIdInItem, itemToEdit.batchDetails || []);
      const manualSelections = { ...originalAlloc };

      const singleItemDraft = [{
        indentReferenceNumber: transaction.indentReferenceNumber || '',
        invoiceDate: dateInputToIsoUtc(
          transaction.invoiceDate ? toDateInputValue(transaction.invoiceDate) : todayYmdIST(),
        ),
        vehicleNumber: transaction.vehicleNumber || '',
        // ❌ OLD
        // location: transaction.location || '',
        vehicleTemperature: transaction.vehicleTemperature ?? '',
        dockNumber: transaction.dockNumber ?? '',
        dockInTime: transaction.dockInTime ? String(transaction.dockInTime).slice(0, 16) : '',
        dockOutTime: transaction.dockOutTime ? String(transaction.dockOutTime).slice(0, 16) : '',
        productTemperature: transaction.productTemperature ?? '',
        reportingDate: transaction.reportingDate ? new Date(transaction.reportingDate).toISOString().slice(0, 10) : '',
        reportingTime: transaction.reportingTime ? new Date(transaction.reportingTime).toISOString().slice(11, 16) : '',
        companies: nextCompanyIds,
        productId: itemToEdit.productId?._id || itemToEdit.productId,
        dispatchQuantity: itemToEdit.dispatchQuantity || 0,
        allocationMethod: itemToEdit.allocationMethod || 'FEFO',
        batchDetails: (itemToEdit.batchDetails || []).map((b) => ({
          batchId: b.batchId?._id || b.batchId,
          batchNumber: b.batchNumber,
          manufacturingDate: b.manufacturingDate,
          expiryDate: b.expiryDate,
          quantity: b.quantity,
          binId: b.binId,
        })),
      }];
      const existingBatchDetails = (itemToEdit.batchDetails || []).map((b) => ({
        batchId: b.batchId?._id || b.batchId,
        batchNumber: b.batchNumber,
        manufacturingDate: b.manufacturingDate,
        expiryDate: b.expiryDate,
        quantity: b.quantity,
        binId: b.binId,
        allocationSource: itemToEdit.allocationMethod || 'FEFO',
      }));
      setFormData({
       ...initialFormData,
       indentReferenceNumber: transaction.indentReferenceNumber || '',
       invoiceDate: transaction.invoiceDate
         ? toDateInputValue(transaction.invoiceDate)
         : toDateInputValue(new Date()),
       vehicleNumber: transaction.vehicleNumber || '',
       // ❌ OLD
       // location: transaction.location || '',
       vehicleTemperature: transaction.vehicleTemperature ?? '',
       dockNumber: transaction.dockNumber ?? '',
       dockInTime: transaction.dockInTime ? String(transaction.dockInTime).slice(0, 16) : '',
       dockOutTime: transaction.dockOutTime ? String(transaction.dockOutTime).slice(0, 16) : '',
       productTemperature: transaction.productTemperature ?? '',
       reportingDate: transaction.reportingDate ? new Date(transaction.reportingDate).toISOString().slice(0, 10) : '',
       reportingTime: transaction.reportingTime ? new Date(transaction.reportingTime).toISOString().slice(11, 16) : '',
       companies: nextCompanyId,
       secondaryCompanies: Array.isArray(transaction.secondaryCompanies)
       ? transaction.secondaryCompanies.map(String)
       : [],
       productId: itemToEdit.productId?._id || itemToEdit.productId || '',
       dispatchQuantity: Number(itemToEdit.dispatchQuantity) || 0,
       method: itemToEdit.allocationMethod || 'FEFO',
       allocationMethod: itemToEdit.allocationMethod || 'FEFO',
       productDescription: itemToEdit.productDescription || '',
     
       batchDetails: existingBatchDetails,
     });
      setDraftItems(singleItemDraft);
      setEditingTransactionId(transactionId);
      setEditingItemId(itemId);
      setEditingItemStatus(itemToEdit.status || 'PENDING');
      originalDispatchQtyByProductRef.current = {
        [productIdInItem]: Number(itemToEdit.dispatchQuantity) || 0,
      };
      setEditingTransactionProductIds([productIdInItem]);
      setAllocationStateByProduct({
        [productIdInItem]: {
          selected: true,
          dispatchQuantity: Number(itemToEdit.dispatchQuantity) || 0,
          originalDispatchQuantity: Number(itemToEdit.dispatchQuantity) || 0,
          allocationMethod: (itemToEdit.allocationMethod || 'FEFO'),
          productDescription: itemToEdit.productDescription || '',
          batches: editBatches,
          manualSelections,
          expanded: true,
          loading: false,
          error: null,
          isApplied: false,
          isEditOutward: true,
        },
      });
      setFormErrors({});
      setFormTouched({});
      const editDispatch = Number(itemToEdit.dispatchQuantity) || 0;
      const editMethod = String(itemToEdit.allocationMethod || 'FEFO').toUpperCase();
      if (editMethod === 'FEFO' && editDispatch > 0) {
        await refreshEditLiveFefoReference(productIdInItem, editDispatch);
      } else {
        applyDispatchStockValidationForProduct(productIdInItem, editProduct, {
          selected: true,
          dispatchQuantity: editDispatch,
          originalDispatchQuantity: editDispatch,
          allocationMethod: itemToEdit.allocationMethod || 'FEFO',
          batches: editBatches,
          isEditOutward: true,
        });
      }
      setShowForm(true);
    } catch (err) {
      console.error('Error fetching outward transaction:', err);
      toast.error(err?.message || 'Failed to load transaction', toastOpt);
    }
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeletePopUpOpen(true);
  };
  const markPicklistGenerated = (outwardId) => {
    const id = String(outwardId || "");
    if (!id) return;
    setPicklistGeneratedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      try {
        sessionStorage.setItem("wms_picklist_generated_ids", JSON.stringify([...next]));
      } catch {
        /* ignore storage errors */
      }
      return next;
    });
  };

  const revokePdfPreviewUrl = () => {
    if (pdfPreviewUrlRef.current) {
      window.URL.revokeObjectURL(pdfPreviewUrlRef.current);
      pdfPreviewUrlRef.current = null;
    }
    setPdfPreviewUrl(null);
  };

  const closePdfPreview = () => {
    pdfPreviewRequestIdRef.current += 1;
    revokePdfPreviewUrl();
    setPdfPreviewFileName("");
    setPdfPreviewTitle("PDF Preview");
    setPdfPreviewLoading(false);
    setPdfPreviewOpen(false);
  };

  useEffect(() => {
    return () => {
      if (pdfPreviewUrlRef.current) {
        window.URL.revokeObjectURL(pdfPreviewUrlRef.current);
        pdfPreviewUrlRef.current = null;
      }
    };
  }, []);

  const openPdfPreview = ({ title, fileName, requestId, blob }) => {
    if (requestId !== pdfPreviewRequestIdRef.current) return;
    const url = window.URL.createObjectURL(
      new Blob([blob], { type: "application/pdf" })
    );
    pdfPreviewUrlRef.current = url;
    setPdfPreviewUrl(url);
    setPdfPreviewFileName(fileName);
    setPdfPreviewTitle(title);
    setPdfPreviewOpen(true);
    setPdfPreviewLoading(false);
  };

  const handleConfirmPdfDownload = () => {
    if (!pdfPreviewUrl) return;
    const a = document.createElement("a");
    a.href = pdfPreviewUrl;
    a.download = pdfPreviewFileName || "document.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDownloadPicklist = async (outwardId) => {
    const row =
      (transactions || []).find(
        (t) => String(t._id || t.id) === String(outwardId)
      ) || null;
    const rowError = validateOutwardActionRow(row);
    if (rowError) {
      toast.error(rowError, toastOpt);
      return;
    }
    const fileName = `picklist-${outwardId}.pdf`;
    const requestId = ++pdfPreviewRequestIdRef.current;
    revokePdfPreviewUrl();
    setPdfPreviewFileName(fileName);
    setPdfPreviewTitle("Picklist Preview");
    setPdfPreviewOpen(true);
    setPdfPreviewLoading(true);
    try {
      const blob = await outwardService.downloadPicklist(outwardId);
      openPdfPreview({
        title: "Picklist Preview",
        fileName,
        requestId,
        blob,
      });
      if (requestId === pdfPreviewRequestIdRef.current) {
        markPicklistGenerated(outwardId);
      }
    } catch (error) {
      console.error(error.message);
      if (requestId === pdfPreviewRequestIdRef.current) {
        closePdfPreview();
      }
    } finally {
      if (requestId === pdfPreviewRequestIdRef.current) {
        setPdfPreviewLoading(false);
      }
    }
  };

  const handleDownloadDeliveryOrder = async (row) => {
    const outwardId = row?._id || row?.id;
    if (!outwardId || downloadingDoId) return;

    const rowError = validateOutwardActionRow(row);
    if (rowError) {
      toast.error(rowError, toastOpt);
      return;
    }

    const pickListGenerated = picklistGeneratedIds.has(String(outwardId));
    if (!pickListGenerated) {
      toast.error("Please generate Pick List first.", toastOpt);
      return;
    }

    setDownloadingDoId(String(outwardId));
    const fallbackName = `DO_${row?.deliveryOrderNumber || row?.transactionId || outwardId}.pdf`;
    const requestId = ++pdfPreviewRequestIdRef.current;
    revokePdfPreviewUrl();
    setPdfPreviewFileName(fallbackName);
    setPdfPreviewTitle("Delivery Order Preview");
    setPdfPreviewOpen(true);
    setPdfPreviewLoading(true);
    try {
      const { blob, filename } = await outwardService.downloadDeliveryOrder(outwardId);
      openPdfPreview({
        title: "Delivery Order Preview",
        fileName: filename || fallbackName,
        requestId,
        blob,
      });
    } catch (error) {
      toast.error(error?.message || "Failed to download Delivery Order", toastOpt);
      if (requestId === pdfPreviewRequestIdRef.current) {
        closePdfPreview();
      }
    } finally {
      setDownloadingDoId(null);
      if (requestId === pdfPreviewRequestIdRef.current) {
        setPdfPreviewLoading(false);
      }
    }
  };
  const handleDeleteConfirm = async () => {
    if (!rowToDelete) return;

    const transactionId = rowToDelete._id || rowToDelete.id;
    if (!transactionId) return;

    try {
      setDeleteLoading(true);

      await outwardService.deleteOutwardTransaction(transactionId);
      setDeletePopUpOpen(false);
      setRowToDelete(null);
      fetchTransactions();
      fetchProducts();
      toast.success('Outward transaction deleted successfully');
    } catch (err) {
      console.error('Error deleting outward transaction/item:', err);
      // Use default toast container
      toast.error(err?.message || 'Failed to delete');
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetUploadPreview = () => {
    setUploadPreviewOpen(false);
    setUploadPreviewFileName('');
    setUploadPreviewHeaders([]);
    setUploadPreviewRows([]);
    setUploadPreviewSummary(null);
    setUploadSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUploadOutwardClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadFileSelect = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    try {
      const { headers, rows, allRows, fileName } = await parseFileToPreview(file);
      const normalizeKey = (key) =>
        String(key ?? "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "");
      const getCol = (r, keys) => {
        for (const k of keys) {
          const nk = normalizeKey(k);
          if (r && Object.prototype.hasOwnProperty.call(r, nk)) return r[nk];
        }
        return "";
      };
      const normalizedRows = (Array.isArray(allRows) ? allRows : rows || []).map((row) => {
        if (!row || typeof row !== "object") return row;
        const normalized = {};
        Object.entries(row).forEach(([k, v]) => {
          normalized[normalizeKey(k)] = v;
        });
        return normalized;
      });

      const groupMap = new Map();
      for (const row of normalizedRows) {
        const companyName = String(getCol(row, ["Company", "company", "companyname"]) || "").trim();
        if (!companyName) continue;
        const vehicleNumber = String(
          getCol(row, ["Vehicle Number", "vehicleNumber", "vehiclenumber"]) || ""
        ).trim();
        const key = `${companyName.toLowerCase()}||${vehicleNumber.toLowerCase()}`;
        if (!groupMap.has(key)) {
          groupMap.set(key, { companyName, vehicleNumber });
        }
      }

      let ornReservations = [];
      if (groupMap.size > 0) {
        const reserveRes = await outwardService.reserveBulkOutwardOrns(
          Array.from(groupMap.values())
        );
        ornReservations = reserveRes.reservations || [];
      }

      setUploadPreviewFileName(fileName);
      setUploadPreviewHeaders(headers);
      setUploadPreviewRows(rows);
      setUploadPreviewSummary({ ornReservations });
      setUploadSelectedFile(file);
      setUploadPreviewOpen(true);
    } catch (err) {
      toast.error(err?.message || 'Failed to read file');
    }
  };

  const handleDownloadFormat = () => {
    downloadOutwardUploadTemplate();
    toast.success('Outward format downloaded. Fill and upload to add outward transactions.', toastOpt);
  };

  const resetProductUploadPreview = () => {
    setProductUploadPreviewOpen(false);
    setProductUploadPreviewFileName('');
    setProductUploadPreviewHeaders([]);
    setProductUploadPreviewRows([]);
    setProductUploadSelectedFile(null);
    if (productTableFileRef.current) productTableFileRef.current.value = '';
  };

  const handleProductUploadFileSelect = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    try {
      const { headers, rows, fileName } = await parseFileToPreview(file);
      setProductUploadPreviewFileName(fileName);
      setProductUploadPreviewHeaders(headers);
      setProductUploadPreviewRows(rows);
      setProductUploadSelectedFile(file);
      setProductUploadPreviewOpen(true);
    } catch (err) {
      toast.error(err?.message || 'Failed to read file', toastOpt);
    }
  };

  const handleProductUploadApply = async () => {
    if (!productUploadSelectedFile) {
      resetProductUploadPreview();
      return;
    }
    setProductUploadLoading(true);
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(new Uint8Array(ev.target.result));
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(productUploadSelectedFile);
      });
      const wb = XLSX.read(data, { type: 'array' });
      const firstSheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });
      if (rows.length < 2) {
        toast.error('File must have header row and at least one data row', toastOpt);
        return false;
      }
      const headers = (rows[0] || []).map((h) => String(h).trim().toLowerCase().replace(/\s+/g, ''));
      const getCol = (names) => {
        for (const n of names) {
          const i = headers.findIndex((h) => h === n || h === String(n).toLowerCase());
          if (i >= 0) return i;
        }
        return -1;
      };
      const skuCol = getCol(['productsku', 'product_sku', 'sku']);
      if (skuCol < 0) {
        toast.error('File must have productSKU or sku column', toastOpt);
        return false;
      }
      const dispCol = getCol(['dispatchquantity', 'dispatch_quantity']);
      const methodCol = getCol(['allocationmethod', 'allocation_method']);
      const descCol = getCol(['productdescription', 'product_description']);
      // ❌ OLD (user input trusted)
      // const totalQtyCol = getCol(['totalquantity', 'total_quantity', 'totalqty', 'total_qty']);
      const batchNumCol = getCol(['batchnumber', 'batch_number']);
      const batchQtyCol = getCol(['batchquantity', 'batch_quantity']);
      const updates = {};
      const errors = [];
      const csvOrderIds = [];
      const csvOrderSeen = new Set();
      for (let r = 1; r < rows.length; r++) {
        const row = rows[r] || [];
        const rawSku = String(row[skuCol] ?? '').trim();
        if (!rawSku) continue;
        const product = products.find((p) => (p.sku || '').toString().trim() === rawSku);
        if (!product) {
          errors.push({ rowIndex: r + 1, error: `Unknown SKU: ${rawSku}` });
          continue;
        }
        if (!isProductAvailableForOutward(product)) {
          errors.push({ rowIndex: r + 1, error: `SKU ${rawSku} has no available stock` });
          continue;
        }
        const dispatchRaw = dispCol >= 0 ? row[dispCol] : '';
        if (!outwardQtyRawIsValidWholeNumber(dispatchRaw, { allowZero: false })) {
          errors.push({ rowIndex: r + 1, error: `Invalid dispatchQuantity for ${rawSku}` });
          continue;
        }
        const dispatchQty = Number(String(dispatchRaw).trim());
        // ❌ OLD
        // total qty derived internally
        // ✅ NEW: extra FE guard using backend totalQty
        const totalQty = getProductRemainingQty(product);
        if (dispatchQty > totalQty) {
          errors.push({ rowIndex: r + 1, error: `Dispatch qty cannot exceed available stock (${totalQty}) for ${rawSku}` });
          continue;
        }
        const method = (methodCol >= 0 && row[methodCol] != null) ? String(row[methodCol]).trim().toUpperCase() : 'FEFO';
        const validMethod = method === 'MANUAL' ? 'MANUAL' : 'FEFO';
        const productDescription = descCol >= 0 ? String(row[descCol] ?? '').trim() : (product.description || '');
        const batchNumber = batchNumCol >= 0 ? String(row[batchNumCol] ?? '').trim() : '';
        const batchQuantity = batchQtyCol >= 0 ? String(row[batchQtyCol] ?? '').trim() : '';
        if (
          batchQuantity !== '' &&
          !outwardQtyRawIsValidWholeNumber(batchQuantity, { allowZero: false })
        ) {
          errors.push({
            rowIndex: r + 1,
            error: `Invalid batch quantity for ${rawSku}`,
          });
          continue;
        }
        if (!csvOrderSeen.has(product._id)) {
          csvOrderSeen.add(product._id);
          csvOrderIds.push(product._id);
        }
        updates[product._id] = {
          selected: true,
          dispatchQuantity: dispatchQty,
          allocationMethod: validMethod,
          productDescription: productDescription || product.description || '',
          batchNumber,
          batchQuantity,
          // ❌ OLD
          // totalQuantity: row["totalQuantity"]
        };
      }
      if (errors.length > 0) {
        const msg = errors.slice(0, 5).map((e) => `Row ${e.rowIndex}: ${e.error}`).join('; ');
        toast.error(errors.length > 5 ? `${msg}... and ${errors.length - 5} more` : msg);
        return false;
      }
      setFormData((prev) => ({ ...prev, productTable: { ...prev.productTable, ...updates } }));
      setAllocationStateByProduct((prev) => {
        const next = { ...prev };
        for (const [pid, u] of Object.entries(updates)) {
          next[pid] = {
            ...(next[pid] || { batches: [], manualSelections: {}, expanded: true, loading: false, error: null, isApplied: false }),
            selected: u.selected,
            dispatchQuantity: u.dispatchQuantity,
            allocationMethod: u.allocationMethod,
            productDescription: u.productDescription ?? '',
          };
        }
        return next;
      });
      // For MANUAL rows: fetch batches and either apply sheet batch (if it belongs to product) or show "not related" + product batches
      const manualEntries = Object.entries(updates).filter(([, u]) => (u.allocationMethod || '').toUpperCase() === 'MANUAL');
      const manualResolved = {};
      for (const [productId, u] of manualEntries) {
        try {
          const res = await outwardService.getBatchesByProduct(productId);
          const list = filterOutwardAvailableBatches(Array.isArray(res?.data) ? res.data : []);
          const batches = list.map((b) => ({
            batchId: b._id,
            batchNumber: b.batchNumber,
            expiryDate: b.expiryDate,
            manufacturingDate: b.manufacturingDate,
            availableQty: b.quantity ?? 0,
            quantity: b.quantity ?? 0,
            binId: b.binId,
          }));
          const sheetBatchNumber = (u.batchNumber || '').toString().trim();
          const sheetQtyParsed = parseOutwardWholeNumberQtyInput(u.batchQuantity);
          const sheetQty = sheetQtyParsed == null ? 0 : Math.max(0, sheetQtyParsed);
          if (sheetBatchNumber) {
            const matched = batches.find((b) => (b.batchNumber || '').toString().trim().toLowerCase() === sheetBatchNumber.toLowerCase());
            if (matched) {
              const qty = sheetQty <= (matched.availableQty ?? matched.quantity ?? 0) ? sheetQty : (matched.availableQty ?? matched.quantity ?? 0);
              const appliedBatch = {
                batchId: matched.batchId,
                batchNumber: matched.batchNumber,
                manufacturingDate: matched.manufacturingDate,
                expiryDate: matched.expiryDate,
                availableQty: matched.availableQty ?? matched.quantity,
                quantity: qty,
                binId: matched.binId,
              };
              manualResolved[productId] = {
                batches: [appliedBatch],
                catalogBatches: batches,
                manualSelections: { [matched.batchId]: qty },
                isApplied: true,
                error: null,
                loading: false,
              };
            } else {
              manualResolved[productId] = {
                catalogBatches: batches,
                batches: [],
                manualSelections: {},
                loading: false,
                error: 'Manual batch in sheet is not related to this product. Please choose one of the below.',
              };
            }
          } else {
            manualResolved[productId] = { catalogBatches: batches, batches: [], manualSelections: {}, loading: false };
          }
        } catch (err) {
          manualResolved[productId] = { batches: [], loading: false, error: 'Failed to load batches' };
        }
      }
      if (Object.keys(manualResolved).length > 0) {
        setAllocationStateByProduct((prev) => {
          const next = { ...prev };
          for (const [pid, patch] of Object.entries(manualResolved)) {
            next[pid] = { ...(next[pid] || {}), ...patch };
          }
          return next;
        });
      }
      if (Object.keys(updates).length > 0) toast.success(`${Object.keys(updates).length} product row(s) filled and selected`, toastOpt);
      setProductCsvOrderIds(csvOrderIds.filter((id) => updates[id] != null));
      resetProductUploadPreview();
    } catch (err) {
      toast.error(err?.message, toastOpt);
      return false;
    } finally {
      setProductUploadLoading(false);
    }
  };

  const handleUploadApply = async () => {
    if (!uploadSelectedFile) {
      resetUploadPreview();
      return;
    }

    try {
      const { allRows } = await parseFileToPreview(uploadSelectedFile);
      const normalizeKey = (key) =>
        String(key ?? "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "");
      const getCol = (r, keys) => {
        for (const k of keys) {
          const nk = normalizeKey(k);
          if (r && Object.prototype.hasOwnProperty.call(r, nk)) return r[nk];
        }
        return "";
      };
      const normalizedRows = (Array.isArray(allRows) ? allRows : []).map((row) => {
        if (!row || typeof row !== "object") return row;
        const normalized = {};
        Object.entries(row).forEach(([k, v]) => {
          normalized[normalizeKey(k)] = v;
        });
        return normalized;
      });
      const invoiceYmds = extractInvoiceDatesFromUploadRows(normalizedRows, getCol);
      const disallowed = findDisallowedBulkInvoiceDates(invoiceYmds, permissionState);
      if (disallowed.length > 0) {
        openPermissionRequestModal(disallowed[0], { closeCreateForm: false });
        return;
      }
    } catch (previewErr) {
      console.warn("[Outward] bulk permission pre-check skipped:", previewErr?.message);
    }

    setUploadLoading(true);
    try {
      const res = await outwardService.bulkUploadOutwardExcel(uploadSelectedFile);
      const created = res?.data?.created ?? 0;
      const errors = res?.data?.errors || [];
      const errCount = errors.length;

      if (created > 0 && errCount === 0) {
        toast.success(`✅ Success! ${created} outward transaction(s) uploaded successfully.`, {
          autoClose: 4000,
        });
      } else if (created > 0 && errCount > 0) {
        toast.success(`✅ ${created} outward transaction(s) uploaded successfully.`, {
          autoClose: 3000,
        });
        // Show errors separately after a short delay - show exact backend error messages
        setTimeout(() => {
          const errorMessages = errors.slice(0, 5).map((e) => {
            // Show exact backend error message - no modification
            const backendError = e.error || (typeof e === 'string' ? e : '');
            return backendError;
          }).filter(Boolean);
          const errorText = errCount <= 5 
            ? errorMessages.join('. ') 
            : `${errorMessages.join('. ')}. ...and ${errCount - 5} more error(s).`;
          // Show exact backend error message
          toast.error(errorText || 'Some rows had errors');
        }, 500);
      } else if (errCount > 0) {
        // All failed - show exact backend error messages
        const errorMessages = errors.slice(0, 5).map((e) => {
          // Show exact backend error message - no modification
          const backendError = e.error || (typeof e === 'string' ? e : '');
          return backendError;
        }).filter(Boolean);
        const errorText = errCount <= 5 
          ? errorMessages.join('. ') 
          : `${errorMessages.join('. ')}. ...and ${errCount - 5} more error(s).`;
        // Show exact backend error message
        toast.error(errorText || 'Upload failed');
      } else {
        toast.info('ℹ️ No transactions found in file. Please check your file and try again.', {
          autoClose: 4000,
        });
      }
      fetchTransactions();
      fetchProducts();
      // Delay closing popup slightly so user can see toast
      setTimeout(() => {
        resetUploadPreview();
      }, 500);
    } catch (err) {
      // Extract backend error messages from error response data
      let errorMsg = err?.message || 'Upload failed';

      // Map onboarding/company-completion validation to the required toast format
      const onboardingMatch = String(err?.message || '')
        .match(/Company '([^']+)' is not onboarded\. Only completed companies are allowed\./i);
      if (onboardingMatch && onboardingMatch[1]) {
        errorMsg = `Company '${onboardingMatch[1]}' is not onboarded.`;
      }
      
      // Check if error has responseData attached (from API service)
      if (err?.responseData) {
        const errorData = err.responseData;
        // Check for errors array in data.errors or errors
        if (errorData?.data?.errors && Array.isArray(errorData.data.errors) && errorData.data.errors.length > 0) {
          // Extract exact backend error messages
          const backendErrors = errorData.data.errors.slice(0, 5).map((e) => e.error || '').filter(Boolean);
          if (backendErrors.length > 0) {
            errorMsg = backendErrors.join('. ');
          }
        } else if (errorData?.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          const backendErrors = errorData.errors.slice(0, 5).map((e) => e.error || '').filter(Boolean);
          if (backendErrors.length > 0) {
            errorMsg = backendErrors.join('. ');
          }
        }
      }

      // If the backend onboarding validation error came from backendErrors, normalize it too.
      const onboardingMatchFromErrors = String(errorMsg || '')
        .match(/Company '([^']+)' is not onboarded\./i);
      if (onboardingMatchFromErrors && onboardingMatchFromErrors[1]) {
        errorMsg = `Company '${onboardingMatchFromErrors[1]}' is not onboarded.`;
      }
      
      toast.error(errorMsg);
      // Delay closing popup slightly so user can see toast
      setTimeout(() => {
        resetUploadPreview();
      }, 500);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleStatusConfirmYes = async () => {
    if (!statusConfirmPayload) return;
    const { id, itemId, newStatus, mode, transactionIdString } = statusConfirmPayload;
    const row =
      (transactions || []).find(
        (t) =>
          String(t._id || t.id) === String(id) &&
          (itemId == null || String(t.itemId) === String(itemId))
      ) ||
      (transactions || []).find((t) => String(t._id || t.id) === String(id)) ||
      null;
    if (String(newStatus || '').toUpperCase() === 'DISPATCHED') {
      const rowError = validateOutwardActionRow(row);
      if (rowError) {
        toast.error(rowError, toastOpt);
        return;
      }
    }
    try {
      setStatusChangeLoading(true);
      if (mode === 'document' && transactionIdString) {
        // Update all items in the transaction
        await outwardService.updateOutwardTransactionAllItemsStatus(transactionIdString, { status: newStatus });
      } else if (itemId) {
        // Update single item
        await outwardService.updateOutwardItemStatus(id, itemId, { status: newStatus });
      } else {
        // Update transaction-level status (legacy)
        await outwardService.updateOutwardStatus(id, { status: newStatus });
      }
      setStatusConfirmOpen(false);
      setStatusConfirmPayload(null);
      setHighlightedTransactionId(null);
      fetchTransactions();
      toast.success(mode === 'document' ? 'Document status updated successfully' : 'Status updated successfully', toastOpt);
    } catch (err) {
      console.error('Error updating outward status:', err);
      toast.error(err?.message || 'Failed to update status', toastOpt);
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const outwardActions = (row) => {
    const actions = [];
    const isLocked = row.status === 'DISPATCHED' || row.status === 'CANCELLED';
    const rowId = String(row._id || row.id || '');

    if (!isLocked) {
      actions.push({
        label: 'Edit',
        icon: EditIcon,
        tooltip: 'Edit Outward',
        onClick: handleEdit,
      });
      actions.push({
        label: 'Delete',
        icon: DeleteIcon,
        tooltip: 'Delete Outward',
        onClick: handleDelete,
      });
    }
    actions.push({
      label: 'Download Picklist',
      icon: FiDownload,
      tooltip: 'Download Picklist',
      onClick: () => handleDownloadPicklist(row._id),
    });
    actions.push({
      label: 'Download Delivery Order',
      icon: FiDownload,
      tooltip: 'Download Delivery Order',
      disabled: downloadingDoId === rowId,
      onClick: () => handleDownloadDeliveryOrder(row),
    });
    return actions;
  };

  const outwardTableColumns = [
    ...outwardColumns,
    {
      key: '_actions',
      label: 'Actions',
      align: 'right',
      render: (_, row) => (
        <OutwardRowActionsMenu row={row} actions={outwardActions(row)} />
      ),
    },
  ];
  const selectedPrimaryIds = toCompaniesPayload(formData.companies);

  const selectedPrimaryCompanies = (companies || []).filter((c) =>
    selectedPrimaryIds.includes(String(c._id || c.id))
  );
 
  const secondaryCompanyGroups = selectedPrimaryCompanies
    .map((pc) => {
      const sec = Array.isArray(pc.secondaryCustomers) ? pc.secondaryCustomers : [];
      const options = sec
        .filter((sc) => sc?.isActive !== false)
        .map((sc) => ({
          label: `${sc.customerName}${sc.customerCode ? ` (${sc.customerCode})` : ""}`,
          value: sc._id, // secondary customer id
        }));
       return { title: pc.name, options };
    })
    .filter((g) => (g.options || []).length > 0);
  
  const shouldShowSecondary = secondaryCompanyGroups.length > 0;
  const isEditMethodReadOnly = Boolean(
    editingItemId && isOutwardItemAllocationLocked(editingItemStatus)
  );
  const isEditMode = !!(editingTransactionId || editingItemId);
  const originalDateYmd = normalizeYmdIST(latestEditTransactionRef.current?.invoiceDate);

  const postingDatePermissionOptions = useMemo(
    () => ({ isEditMode, originalDateYmd }),
    [isEditMode, originalDateYmd],
  );

  const postingDateSelectable = useMemo(
    () => createPostingDateSelectableChecker(permissionState, postingDatePermissionOptions),
    [permissionState, postingDatePermissionOptions],
  );

  const invoiceDatePickerBounds = getInvoiceDatePickerBoundsIST({
    isEditMode,
    originalDateYmd,
    approvedPermissions,
    activePermission,
  });

  const approvedRangeLabels = formatApprovedPermissionsForDisplay(approvedPermissions);
  const selectedPostingYmd = resolvePostingYmdForPermissionModal(formData?.invoiceDate);
  const showBackdatePermissionBanner =
    !isEditMode &&
    selectedPostingYmd &&
    requiresBackdatePermission(selectedPostingYmd, permissionState);

  const outwardFields = [
    // Indent Reference Number - Auto-generated, field commented out
    // {
    //   name: 'indentReferenceNumber',
    //   label: 'Indent Reference Number',
    //   type: 'text',
    //   required: true,
    //   grid: '1',
    // },
    ...(!editingTransactionId && !editingItemId
      ? [
          {
            type: "custom",
            grid: "1",
            render: () => (
              <div>
                <label className="form-popup-label mb-1.5 block">ORN</label>
                <input
                  type="text"
                  readOnly
                  value={
                    ornLoading
                      ? "Loading..."
                      : ornError
                      ? ornError
                      : outwardRequisitionNumber || "—"
                  }
                  className="w-full cursor-default bg-gray-50"
                  aria-label="ORN"
                />
              </div>
            ),
          },
        ]
      : []),
    {
      name: 'invoiceDate',
      label: 'Posting Date',
      type: 'date',
      required: true,
      grid: '1',
      min: invoiceDatePickerBounds.min,
      max: invoiceDatePickerBounds.max,
      isDateSelectable: postingDateSelectable,
      navigationMin: invoiceDatePickerBounds.min,
      navigationMax: invoiceDatePickerBounds.max,
    },
    {
      name: 'vehicleNumber',
      label: 'Vehicle Number',
      type: 'text',
      required: true,
      grid: '1',
    },
    // ❌ OLD FIELD
    // {
    //   name: 'location',
    //   label: 'Location',
    //   type: 'text',
    //   placeholder: 'Enter location',
    //   grid: '1',
    // },
    {
      name: 'vehicleTemperature',
      label: 'Vehicle Temperature',
      type: 'number',
      required: true,
      placeholder: 'Enter temperature',
      grid: '1',
    },
    {
      name: 'reportingDate',
      label: 'Reporting Date',
      type: 'date',
      required: true,
      grid: '1',
    },
    {
      name: 'reportingTime',
      label: 'Reporting Time',
      type: 'time',
      required: true,
      grid: '1',
    },
    {
      name: 'dockNumber',
      label: 'Dock Number',
      type: 'text',
      required: true,
      placeholder: 'Enter dock number',
      grid: '1',
    },
    {
      name: 'dockInTime',
      label: 'Dock In Time',
      type: 'datetime-local',
      required: true,
      grid: '1',
    },
    {
      name: 'dockOutTime',
      label: 'Dock Out Time',
      type: 'datetime-local',
      required: true,
      grid: '1',
    },
    {
      name: 'productTemperature',
      label: 'Product Temperature (°C)',
      type: 'number',
      required: true,
      placeholder: 'Enter product temperature',
      grid: '1',
    },
    {
      name: 'companies',
      label: 'Company',
      type: 'select',
      required: true,
      placeholder: '-- Select Company --',
      // Create outward + bulk upload company source (same form context)
      options: bulkCompanyOptions.length ? bulkCompanyOptions : companyOptions,
      grid: '1',
    },
   ...(shouldShowSecondary
  ? [{
      name: "secondaryCompanies",
      label: "Secondary Company",
      type: "grouped_multiselect",
      required: true,
      placeholder: "-- Select Secondary Company --",
      groups: secondaryCompanyGroups,
      grid: "1",
    }]
  : []),
    {
      name: 'method',
      label: 'Method',
      type: 'select',
      required: true,
      options: [
        { label: 'FEFO', value: 'FEFO' },
        { label: 'MANUAL', value: 'MANUAL' },
      ],
      disabled: isEditMethodReadOnly,
      grid: '1',
    },
  ];
  const getOutwardExportQueryParams = () => {
    const params = {};
    if (searchTerm && String(searchTerm).trim()) {
      params.search = String(searchTerm).trim();
    }
    if (appliedFrom && appliedTo) {
      params.startDate = appliedFrom;
      params.endDate = appliedTo;
    }
    return params;
  };

  const handleExportOutwardExcel = async () => {
    setExportLoading(true);
    try {
      const params = getOutwardExportQueryParams();
      const blob = await outwardService.downloadOutwardExcel(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `outward_report_${Date.now()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export outward excel");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportOutwardPdf = async () => {
    setExportLoading(true);
    try {
      const params = getOutwardExportQueryParams();
      const blob = await outwardService.downloadOutwardExcel(params);
      await exportOutwardReportPdfFromExcelBlob(
        blob,
        `outward_report_${Date.now()}.pdf`
      );
    } catch (err) {
      toast.error(err?.message || "Failed to export outward pdf");
    } finally {
      setExportLoading(false);
    }
  };
  return (
    <div className="page-container font-nunito">
      <div className="mb-6 w-full">
        <div
          className="flex items-center justify-between w-full flex-wrap gap-4"
          style={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #EEF1F5",
            borderRadius: 28,
            padding: "16px 24px",
            boxShadow: "0px 0px 4px 0px #959DA526",
          }}
        >
          <h1
            style={{
              fontFamily: "Nunito",
              fontSize: 18,
              fontWeight: 600,
              color: "#161616",
              margin: 0,
            }}
          >
            Outward Management
          </h1>

    <div className="flex items-center gap-[14px] flex-wrap">
      <div className="relative" ref={dateRef}>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              const open = !isDateOpen;
              if (open) {
                setDisplayMonth(
                  startOfMonth(dateFrom ? parseISO(dateFrom) : new Date())
                );
              }
              setIsDateOpen(open);
            }}
            className="inline-flex items-center rounded-xl border border-emerald-500/60 bg-white px-5 py-2 text-sm font-semibold text-emerald-700 shadow-sm hover:bg-emerald-50 active:scale-[0.99] transition"
            style={{ minWidth: 180, justifyContent: "center" }}
          >
            {rangeLabel}
          </button>
        </div>

        {isDateOpen && (
          <DashboardDateRangePopup
            dateFrom={dateFrom}
            dateTo={dateTo}
            displayMonth={displayMonth}
            setDisplayMonth={setDisplayMonth}
            onSelectRange={(from, to) => {
              setDateFrom(from);
              setDateTo(to);
            }}
            onApply={() => {
              handleApplyDateFilter();
              setIsDateOpen(false);
            }}
            onClear={() => {
              setDateFrom("");
              setDateTo("");
              setAppliedFrom("");
              setAppliedTo("");
              setPage(1);
            }}
            onClose={() => setIsDateOpen(false)}
          />
        )}
      </div>

      <div className="relative" style={{ width: 320 }}>
        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </span>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search"
          className={`w-full rounded-[12px] border border-[#D0D5DD] bg-white pl-10 text-sm text-gray-800 placeholder:text-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)] ${hasOutwardListFilters ? "pr-10" : "pr-4"}`}
          style={{ height: 44 }}
        />
        {hasOutwardListFilters ? (
          <button
            type="button"
            onClick={clearOutwardListFilters}
            title="Clear search and date filters"
            className="absolute right-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Clear search and date filters"
          >
            <FiX className="h-5 w-5" strokeWidth={2} />
          </button>
        ) : null}
      </div>

      <button
        type="button"
        onClick={() => setShowForm(true)}
        className="shrink-0 rounded-[12px] bg-[#188742] text-white font-semibold transition hover:bg-[#15733a] active:scale-[0.98]"
        style={{ height: 44, paddingLeft: 18, paddingRight: 18 }}
      >
        Create Outward
      </button>

      <div className="relative shrink-0" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          className="flex items-center justify-center rounded-[12px] border-[1.5px] border-[#188742] bg-white text-[#188742] transition hover:bg-[rgba(24,135,66,0.06)]"
          style={{ width: 44, height: 44 }}
          aria-label="More options"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
            <circle cx="12" cy="6" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="18" r="1.5" />
          </svg>
        </button>

        {menuOpen && (
          <div
            className="absolute right-0 top-full z-50 mt-1 rounded-[12px] border border-[#EEF1F5] bg-white shadow-lg overflow-hidden"
            style={{ width: 240, padding: 8 }}
          >
            <button
              type="button"
              onClick={() => {
                handleDownloadFormat();
                setMenuOpen(false);
              }}
              className="w-full text-left rounded-[10px] px-3 text-[#101828] font-medium transition hover:bg-[#F6F7FA]"
              style={{ height: 40 }}
            >
              Download Format
            </button>
            <button
              type="button"
              onClick={() => {
                handleUploadOutwardClick();
                setMenuOpen(false);
              }}
              className="w-full text-left rounded-[10px] px-3 text-[#101828] font-medium transition hover:bg-[#F6F7FA]"
              style={{ height: 40 }}
            >
              Upload Outward
            </button>
            <button
              type="button"
              onClick={() => {
                handleExportOutwardExcel();
                setMenuOpen(false);
              }}
              disabled={exportLoading}
              className="w-full text-left rounded-[10px] px-3 text-[#101828] font-medium transition hover:bg-[#F6F7FA] disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ height: 40 }}
            >
              {exportLoading ? 'Exporting...' : 'Export Excel'}
            </button>
            <button
              type="button"
              onClick={() => {
                handleExportOutwardPdf();
                setMenuOpen(false);
              }}
              disabled={exportLoading}
              className="w-full text-left rounded-[10px] px-3 text-[#101828] font-medium transition hover:bg-[#F6F7FA] disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ height: 40 }}
            >
              {exportLoading ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        )}
      </div>
    </div>
        </div>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        accept=".xlsx,.xls,.csv"
        className="hidden"
        onChange={handleUploadFileSelect}
      />


      <UploadPreviewPopUp
        open={uploadPreviewOpen}
        onClose={resetUploadPreview}
        title="Upload Outward - File overview"
        fileName={uploadPreviewFileName}
        previewHeaders={uploadPreviewHeaders}
        previewRows={uploadPreviewRows}
        summary={uploadPreviewSummary}
        onApply={handleUploadApply}
        onCancel={resetUploadPreview}
        loading={uploadLoading}
      />
      <UploadPreviewPopUp
        open={productUploadPreviewOpen}
        onClose={resetProductUploadPreview}
        title="Upload Products Excel - File overview"
        fileName={productUploadPreviewFileName}
        previewHeaders={productUploadPreviewHeaders}
        previewRows={productUploadPreviewRows}
        onApply={handleProductUploadApply}
        onCancel={resetProductUploadPreview}
        loading={productUploadLoading}
      />
      <DeletePopUp
        open={deletePopUpOpen}
        onClose={() => { setDeletePopUpOpen(false); setRowToDelete(null); }}
        onConfirm={handleDeleteConfirm}
        description="Are you sure you want to delete this outward? This will delete the outward and restore the deducted quantities to their respective batches and bins."
      />
      {pdfPreviewOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={pdfPreviewTitle || "PDF Preview"}
        >
          <div className="flex h-[85vh] w-[90vw] max-w-[900px] flex-col overflow-hidden rounded-[12px] bg-white shadow-lg">
            <div className="flex items-center justify-between border-b border-[#EEF1F5] px-4 py-3">
              <h3 className="m-0 text-base font-semibold text-[#101828]">
                {pdfPreviewFileName || pdfPreviewTitle || "PDF Preview"}
              </h3>
              <button
                type="button"
                onClick={closePdfPreview}
                className="rounded-[8px] p-1.5 text-[#667085] transition hover:bg-[#F6F7FA]"
                aria-label="Close PDF preview"
              >
                <FiX size={18} />
              </button>
            </div>
            <div className="min-h-0 flex-1 bg-[#F6F7FA]">
              {pdfPreviewLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-[#667085]">
                  Loading PDF...
                </div>
              ) : pdfPreviewUrl ? (
                <iframe
                  title={pdfPreviewTitle || "PDF Preview"}
                  src={pdfPreviewUrl}
                  className="h-full w-full border-0"
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-[#667085]">
                  Unable to load PDF.
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-[#EEF1F5] px-4 py-3">
              <button
                type="button"
                onClick={closePdfPreview}
                className="rounded-[10px] border border-[#D0D5DD] bg-white px-4 py-2 text-sm font-semibold text-[#344054] transition hover:bg-[#F6F7FA]"
                style={{ height: 40 }}
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleConfirmPdfDownload}
                disabled={!pdfPreviewUrl || pdfPreviewLoading}
                className="inline-flex items-center gap-2 rounded-[10px] border border-[#188742] bg-[#188742] px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                style={{ height: 40 }}
              >
                <FiDownload size={16} />
                Download
              </button>
            </div>
          </div>
        </div>
      )}
      {statusChangeModeOpen && statusChangeModePayload && !statusChangeModePayload.showStatusOptions && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 99998 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="status-mode-selection-title"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => { setStatusChangeModeOpen(false); setStatusChangeModePayload(null); setHighlightedTransactionId(null); }}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-end pr-4 pt-4">
              <button
                type="button"
                onClick={() => { setStatusChangeModeOpen(false); setStatusChangeModePayload(null); setHighlightedTransactionId(null); }}
                className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 pb-2">
              <p id="status-mode-selection-title" className="text-base font-semibold text-gray-800 mb-4">
                Select Status Change Type
              </p>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => handleStatusChangeModeSelection('item')}
                  className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-emerald-300 transition"
                >
                  <div className="font-medium text-gray-900">Item Status</div>
                  <div className="text-sm text-gray-600 mt-1">Change status for this item only</div>
                </button>
                <button
                  type="button"
                  onClick={() => handleStatusChangeModeSelection('document')}
                  className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-emerald-300 transition"
                >
                  <div className="font-medium text-gray-900">Document Status</div>
                  <div className="text-sm text-gray-600 mt-1">Change status for all items in this transaction</div>
                </button>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => { setStatusChangeModeOpen(false); setStatusChangeModePayload(null); setHighlightedTransactionId(null); }}
                className="h-10 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {statusChangeModeOpen && statusChangeModePayload && statusChangeModePayload.showStatusOptions && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 99998 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="status-options-title"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => { 
              setStatusChangeModeOpen(false); 
              setStatusChangeModePayload(null); 
              setHighlightedTransactionId(null); 
            }}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-end pr-4 pt-4">
              <button
                type="button"
                onClick={() => { 
                  setStatusChangeModeOpen(false); 
                  setStatusChangeModePayload(null); 
                  setHighlightedTransactionId(null); 
                }}
                className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 pb-2">
              <p id="status-options-title" className="text-base font-semibold text-gray-800 mb-4">
                Select New Status {statusChangeModePayload.mode === 'document' ? '(for entire document)' : '(for this item)'}
              </p>
              <div className="space-y-2">
                {OUTWARD_ITEM_STATUS_OPTIONS.filter((s) => s !== statusChangeModePayload.currentStatus).map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => handleStatusOptionSelect(status)}
                    className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-emerald-300 transition"
                  >
                    <div className="font-medium text-gray-900">{status.replace(/_/g, ' ')}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => { 
                  setStatusChangeModeOpen(false); 
                  setStatusChangeModePayload(null); 
                  setHighlightedTransactionId(null); 
                }}
                className="h-10 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {statusConfirmOpen && statusConfirmPayload && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 99999 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="outward-status-confirm-title"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => { setStatusConfirmOpen(false); setStatusConfirmPayload(null); }}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-end pr-4 pt-4">
              <button
                type="button"
                onClick={() => { setStatusConfirmOpen(false); setStatusConfirmPayload(null); }}
                className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 pb-2">
              <p id="outward-status-confirm-title" className="text-base text-gray-800">
                Are you sure you want to change the {statusConfirmPayload.mode === 'document' ? 'document' : 'item'} status from <strong>{statusConfirmPayload.currentStatus?.replace(/_/g, ' ')}</strong> to <strong>{statusConfirmPayload.newStatus?.replace(/_/g, ' ')}</strong>?
                {statusConfirmPayload.mode === 'document' && (
                  <span className="block mt-2 text-sm text-gray-600">This will update all items in this transaction.</span>
                )}
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => { setStatusConfirmOpen(false); setStatusConfirmPayload(null); }}
                className="h-10 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleStatusConfirmYes}
                disabled={statusChangeLoading}
                className="h-10 rounded-full border border-emerald-500 bg-emerald-500 px-5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                {statusChangeLoading ? 'Updating...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      <FormPopup
        open={showForm}
        title={editingItemId ? 'Edit Outward Item' : (editingTransactionId ? 'Edit Outward Transaction' : 'Create Outward Transaction')}
        onClose={handleClosePopup}
        onSubmit={handleSubmit}
        submitText={
          loading
            ? (editingTransactionId || editingItemId ? 'Updating...' : 'Saving...')
            : (editingTransactionId || editingItemId ? 'Update' : 'Save')
        }
        submitDisabled={(() => {
          if (loading) return true;
          if (
            !editingTransactionId &&
            !editingItemId &&
            (ornLoading || ornError || !outwardRequisitionNumber)
          ) {
            return true;
          }
          // Keep Save clickable when header required fields are empty so validation
          // can run and show inline errors (otherwise the disabled button does nothing).
          const headerRequiredErrors = validateOutwardRequiredFields(formData, {
            requireSecondary: shouldShowSecondary,
          });
          if (Object.keys(headerRequiredErrors).length > 0) return false;
          if ((draftItems || []).length > 0 && !editingTransactionId && !editingItemId) return false;
          const editProductIds = new Set(
            (editingTransactionProductIds || []).map(String).filter(Boolean)
          );
          const selected = (productsForTable || []).filter((p) => {
            const state = allocationStateByProduct[p._id];
            if (editingTransactionId || editingItemId) {
              return editProductIds.has(String(p._id)) || !!state?.selected;
            }
            return !!state?.selected;
          });
          if ((editingTransactionId || editingItemId) && selected.length === 0) return false;
          if (!selected.length) return true;
          const selectedIds = new Set(selected.map((p) => String(p._id)));
          // Only block Save for dispatch-qty errors on rows that are still selected (deselecting must not leave stale errors disabling the button).
          const noBlockingDispatchErrors = !(errors || []).some(
            (e) => e?.message && selectedIds.has(String(e.productId))
          );
          const isValid =
            selected.every((p) => {
              const state = allocationStateByProduct[p._id] || {};
              const qty = Number(state.dispatchQuantity) || 0;
              const maxQty = getAllowedDispatchQty(
                p,
                state.originalDispatchQuantity ??
                  originalDispatchQtyByProductRef.current?.[String(p._id)]
              );
              const batchTotal = state.isEditOutward
                ? (state.batches || []).reduce((sum, b) => sum + (Number(b.quantity) || 0), 0)
                : qty;
              const batchesMatch = !state.isEditOutward || batchTotal === qty;
              return qty > 0 && (!Number.isFinite(maxQty) || qty <= maxQty) && batchesMatch;
            }) &&
            noBlockingDispatchErrors;
          return !isValid;
        })()}
        values={formData}
        setValues={setFormDataWithValidation}
        errors={formErrors}
        touched={formTouched}
        fields={outwardFields}
        width="xl"
        popupMaxWidth="1400px"
        popupHeight="94vh"
        formNoValidate
        bodyGridColumns={3}
        afterFooter={
          <>
            {showForm ? (
              <div className="px-2 pb-4">
                <div
                  style={{
                    width: '100%',
                    border: '1px solid #E4E7EC',
                    borderRadius: 14,
                    backgroundColor: '#fff',
                    padding: 18,
                    marginTop: 18,
                  }}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 style={{ fontSize: 16, fontWeight: 600, color: '#101828', margin: 0 }}>Products</h3>
                    <div className="flex flex-wrap items-center gap-[14px]">
                      {!(editingTransactionId || editingItemId) && (
                        <>
                      <button
                        type="button"
                        onClick={handleExportProductsTemplate}
                        className="rounded-[10px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold transition hover:bg-[#188742]/5"
                        style={{ height: 40, paddingLeft: 18, paddingRight: 18 }}
                      >
                        Export CSV
                      </button>
                      <button
                        type="button"
                        onClick={() => productTableFileRef.current?.click()}
                        className="rounded-[10px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold transition hover:bg-[#188742]/5"
                        style={{ height: 40, paddingLeft: 18, paddingRight: 18 }}
                      >
                        Upload Product CSV
                      </button>
                        </>
                      )}
                    </div>
                  </div>
                  <input type="file" ref={productTableFileRef} accept=".xlsx,.xls,.csv" className="hidden" onChange={handleProductUploadFileSelect} />
                  {productUploadPreviewOpen ? (
                    <p className="text-sm text-gray-500 py-4">Previewing upload — use Apply or Cancel in the popup.</p>
                  ) : (
                  <div
                    className="overflow-x-auto overflow-y-auto"
                    style={{ maxHeight: 320, marginTop: 14, border: '1px solid #EEF1F5', borderRadius: 14, padding: 14, backgroundColor: '#fff' }}
                  >
                    <table className="w-full border-collapse text-sm" style={{ minWidth: 920, tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 56 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 150 }} />
                        <col style={{ width: 120 }} />
                        <col style={{ width: 150 }} />
                        <col style={{ width: 100 }} />
                        <col style={{ width: 120 }} />
                        {/* LEGACY: batch columns moved to expandable Batch Allocation panel
                        <col style={{ width: 150 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 120 }} />
                        <col style={{ width: 120 }} />
                        */}
                      </colgroup>
                      <thead className="sticky top-0">
                        <tr>
                          <th className="px-2 text-center font-semibold rounded-l-[8px]" style={{ height: 42, backgroundColor: '#F8FAFC', color: '#667085', border: '1px solid #E4E7EC' }}>#</th>
                          <th className="px-2 text-left font-semibold" style={{ height: 42, backgroundColor: '#F8FAFC', color: '#667085', border: '1px solid #E4E7EC' }}>SKU</th>
                          <th className="px-2 text-left font-semibold" style={{ height: 42, backgroundColor: '#F8FAFC', color: '#667085', border: '1px solid #E4E7EC' }}>Dispatch Quantity</th>
                          <th className="px-2 text-left font-semibold" style={{ height: 42, backgroundColor: '#F8FAFC', color: '#667085', border: '1px solid #E4E7EC' }}>Available Stock</th>
                          <th className="px-2 text-left font-semibold" style={{ height: 42, backgroundColor: '#F8FAFC', color: '#667085', border: '1px solid #E4E7EC' }}>Bins</th>
                          <th className="px-2 text-left font-semibold" style={{ height: 42, backgroundColor: '#F8FAFC', color: '#667085', border: '1px solid #E4E7EC' }}>Method</th>
                          {/* LEGACY: Batch Number, Expiry, Available, Alloc Qty — see expandable Batch Allocation panel per product row
                          <th ...>Batch Number</th>
                          <th ...>Expiry Date</th>
                          <th ...>Available</th>
                          <th ...>Alloc Qty</th>
                          */}
                          <th className="px-2 text-left font-semibold rounded-r-[8px]" style={{ height: 42, backgroundColor: '#F8FAFC', color: '#667085', border: '1px solid #E4E7EC' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(productsTableDisplay || []).map((p, idx) => {
                          const id = p._id;
                          const state = allocationStateByProduct[id] || {
                            selected: false,
                            dispatchQuantity: 0,
                            allocationMethod: formData.method || formData.allocationMethod || 'FEFO',
                            productDescription: p.description || '',
                            batches: [],
                            manualSelections: {},
                            expanded: true,
                            loading: false,
                            error: null,
                            isApplied: false,
                          };
                          return (
                            <ProductRowAllocation
                              key={id}
                              rowNumber={idx + 1}
                              product={p}
                              state={state}
                              globalMethod={formData.method || formData.allocationMethod || 'FEFO'}
                              dispatchQtyError={
                                ((errors || []).find((e) => String(e?.productId) === String(id))?.message) || ''
                              }
                              onDispatchQtyChange={(pid, qty) => {
                                handleAllocationDispatchQtyChange(pid, qty);
                              }}
                              onSkuDraftChange={(pid, value) => updateAllocationState(pid, { skuDraft: value, selected: true })}
                              onGridPaste={handleProductGridPaste}
                              onApplyFEFO={applyFEFOForProduct}
                              onApplyManual={applyManualForProduct}
                              onApplyManualBatch={applyManualBatchForAllocation}
                              onManualQtyChange={setManualSelectionForAllocation}
                              onEditBatchQtyChange={setEditBatchAllocation}
                              onRemoveBatch={removeBatchForAllocation}
                            />
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  )}
                {!productUploadPreviewOpen && (!products || products.length === 0) && (
  <p className="text-sm text-red-500 mt-2">
    {getSelectedCompanyId(formData.companies)
      ? 'No products found for the selected company.'
      : 'No products found. Please select a company to continue.'}
  </p>
)}

                </div>

              </div>
            ) : null}
            {false && (editingTransactionId || editingItemId) && (draftItems.length > 0 || (formData.batchDetails && formData.batchDetails.length > 0)) ? (
              <div className="px-10 pb-10 rounded-3xl border border-emerald-200 bg-emerald-50/30 p-6 mt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Applied Items ({draftItems.length || (formData.batchDetails && formData.batchDetails.length > 0 ? 1 : 0)})</h3>
              <div className="overflow-x-auto">
                {draftItems.length > 0 ? draftItems.map((item, idx) => {
                  // For editing, use formData.batchDetails if available, otherwise use item.batchDetails
                  const batchesToShow = (editingItemId && idx === 0 && formData.batchDetails && formData.batchDetails.length > 0)
                    ? formData.batchDetails
                    : (item.batchDetails || []);
                  const totalBatchQty = batchesToShow.reduce((sum, b) => sum + (b.quantity || 0), 0);
                  // For editing, use formData.dispatchQuantity; for draft items, use item.dispatchQuantity
                  const dispatchQty = (editingItemId && idx === 0) 
                    ? (Number(formData.dispatchQuantity) || 0) 
                    : (item.dispatchQuantity || 0);
                  const batchMismatch = totalBatchQty !== dispatchQty;
                  return (
                    <div key={idx} className="mb-6 rounded-2xl border border-emerald-300 bg-white p-4">
                      <div className="mb-4 flex items-center justify-between">
                        <h4 className="text-base font-semibold text-gray-900">Batch Details</h4>
                        <div className={`text-sm font-medium ${batchMismatch ? 'text-amber-600' : 'text-gray-700'}`}>
                          Total: {totalBatchQty} / Dispatch: {dispatchQty}
                        </div>
                      </div>
                      {batchMismatch && (
                        <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                          <p className="text-sm text-amber-800">
                            Batch total ({totalBatchQty}) doesn't match dispatch qty ({dispatchQty}). 
                            {totalBatchQty < dispatchQty ? (
                              <span className="block mt-1">
                                <strong>Increase batch quantities below</strong> or add {dispatchQty - totalBatchQty} more using FEFO/Manual allocation above.
                              </span>
                            ) : (
                              <span className="block mt-1">
                                <strong>Reduce batch quantities below</strong> to match dispatch qty.
                              </span>
                            )}
                          </p>
                        </div>
                      )}
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Batch Number</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Exp Date</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Quantity</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {batchesToShow.map((batch, batchIdx) => {
                              const batchInManualList = manualBatches.find((mb) => String(mb._id) === String(batch.batchId));
                              const currentQty = batch.quantity || 0;
                              const maxAllowed = batchInManualList ? (batchInManualList.quantity || 0) : (currentQty);
                              const canIncrease = maxAllowed > currentQty;
                              return (
                                <tr key={batchIdx} className="border-b border-gray-100">
                                  <td className="px-4 py-3 text-sm text-gray-700">{batch.batchNumber || '—'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700">
                                    {batch.expiryDate ? (
                                      typeof batch.expiryDate === 'string' 
                                        ? batch.expiryDate.split('T')[0].split(' ')[0] 
                                        : new Date(batch.expiryDate).toISOString().split('T')[0]
                                    ) : '—'}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex flex-col gap-1">
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        value={currentQty}
                                        onKeyDown={preventOutwardQtyDecimalKeyDown}
                                        onPaste={blockInvalidOutwardQtyPaste}
                                        onChange={(e) => {
                                          if (editingItemId && idx === 0 && formData.batchDetails && formData.batchDetails.length > 0) {
                                            updateAppliedBatchQty(batchIdx, e.target.value);
                                          } else {
                                            updateDraftItemBatchQty(idx, batchIdx, e.target.value);
                                          }
                                        }}
                                        className="h-9 w-24 rounded-lg border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                        title={canIncrease ? `Can increase up to ${maxAllowed}` : `Max: ${maxAllowed}`}
                                      />
                                      {canIncrease && (
                                        <div className="text-xs text-blue-600 font-medium">
                                          ↑ Can increase to {maxAllowed}
                                        </div>
                                      )}
                                      {!canIncrease && batchInManualList && (
                                        <div className="text-xs text-gray-500">Max: {maxAllowed}</div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${(batch.allocationSource || item.allocationMethod || '') === 'FEFO' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}`}>
                                      {(batch.allocationSource || item.allocationMethod || 'Manual') === 'FEFO' ? 'FEFO' : 'Manual'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <button
                                      type="button"
                                        onClick={() => {
                                          if (editingItemId && idx === 0 && formData.batchDetails && formData.batchDetails.length > 0) {
                                            removeAppliedBatch(batchIdx);
                                          } else {
                                            removeDraftItemBatch(idx, batchIdx);
                                          }
                                        }}
                                      className="text-sm text-red-600 hover:text-red-800 font-medium"
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {totalBatchQty < dispatchQty && (
                        <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
                          <p className="text-sm text-blue-800">
                            Use FEFO or Manual allocation above to add more batches for this item.
                          </p>
                        </div>
                      )}
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeDraftItem(idx)}
                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                        >
                          Remove Item
                        </button>
                      </div>
                    </div>
                  );
                }) : (
                  // Show formData.batchDetails when no draftItems but batches are applied
                  (() => {
                    const totalBatchQty = (formData.batchDetails || []).reduce((sum, b) => sum + (b.quantity || 0), 0);
                    const dispatchQty = Number(formData.dispatchQuantity) || 0;
                    const batchMismatch = totalBatchQty !== dispatchQty;
                    return (
                      <div className="mb-6 rounded-2xl border border-emerald-300 bg-white p-4">
                        <div className="mb-4 flex items-center justify-between">
                          <h4 className="text-base font-semibold text-gray-900">Batch Details</h4>
                          <div className={`text-sm font-medium ${batchMismatch ? 'text-amber-600' : 'text-gray-700'}`}>
                            Total: {totalBatchQty} / Dispatch: {dispatchQty}
                          </div>
                        </div>
                        {batchMismatch && (
                          <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                            <p className="text-sm text-amber-800">
                              Batch total ({totalBatchQty}) doesn't match dispatch qty ({dispatchQty}). 
                              {totalBatchQty < dispatchQty ? (
                                <span className="block mt-1">
                                  <strong>Increase batch quantities below</strong> or add {dispatchQty - totalBatchQty} more using FEFO/Manual allocation above.
                                </span>
                              ) : (
                                <span className="block mt-1">
                                  <strong>Reduce batch quantities below</strong> to match dispatch qty.
                                </span>
                              )}
                            </p>
                          </div>
                        )}
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="border-b border-gray-200">
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Batch Number</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Exp Date</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Quantity</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Method</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(formData.batchDetails || []).map((batch, batchIdx) => {
                                const batchInManualList = manualBatches.find((mb) => String(mb._id) === String(batch.batchId));
                                const currentQty = batch.quantity || 0;
                                const maxAllowed = batchInManualList ? (batchInManualList.quantity || 0) : (currentQty);
                                const canIncrease = maxAllowed > currentQty;
                                return (
                                  <tr key={batchIdx} className="border-b border-gray-100">
                                    <td className="px-4 py-3 text-sm text-gray-700">{batch.batchNumber || '—'}</td>
                                    <td className="px-4 py-3 text-sm text-gray-700">
                                      {batch.expiryDate ? (
                                        typeof batch.expiryDate === 'string' 
                                          ? batch.expiryDate.split('T')[0].split(' ')[0] 
                                          : new Date(batch.expiryDate).toISOString().split('T')[0]
                                      ) : '—'}
                                    </td>
                                    <td className="px-4 py-3">
                                      <div className="flex flex-col gap-1">
                                        <input
                                          type="text"
                                          inputMode="numeric"
                                          value={currentQty}
                                          onKeyDown={preventOutwardQtyDecimalKeyDown}
                                          onPaste={blockInvalidOutwardQtyPaste}
                                          onChange={(e) => updateAppliedBatchQty(batchIdx, e.target.value)}
                                          className="h-9 w-24 rounded-lg border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                          title={canIncrease ? `Can increase up to ${maxAllowed}` : `Max: ${maxAllowed}`}
                                        />
                                        {canIncrease && (
                                          <div className="text-xs text-blue-600 font-medium">
                                            ↑ Can increase to {maxAllowed}
                                          </div>
                                        )}
                                        {!canIncrease && batchInManualList && (
                                          <div className="text-xs text-gray-500">Max: {maxAllowed}</div>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${(batch.allocationSource || formData.allocationMethod || '') === 'FEFO' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}`}>
                                        {(batch.allocationSource || formData.allocationMethod || 'Manual') === 'FEFO' ? 'FEFO' : 'Manual'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <button
                                        type="button"
                                        onClick={() => removeAppliedBatch(batchIdx)}
                                        className="text-sm text-red-600 hover:text-red-800 font-medium"
                                      >
                                        Remove
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {totalBatchQty < dispatchQty && (
                          <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
                            <p className="text-sm text-blue-800">
                              Use FEFO or Manual allocation above to add more batches for this item.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
            ) : null}
            {failedItems.length > 0 ? (
              <div className="px-10 pb-10 rounded-3xl border border-red-200 bg-red-50/30 p-6 mt-4">
                <h3 className="text-lg font-semibold text-red-900 mb-4">Failed Items ({failedItems.length})</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-red-200">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-red-900">Product SKU</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-red-900">Dispatch Qty</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-red-900">Method</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-red-900">Error</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-red-900">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failedItems.map((item, idx) => (
                        <tr key={idx} className="border-b border-red-100">
                          <td className="px-4 py-3 text-sm text-red-700">{getProductSku(item.productId)}</td>
                          <td className="px-4 py-3 text-sm text-red-700">{item.dispatchQuantity}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${item.allocationMethod === 'FEFO' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}`}>
                              {item.allocationMethod}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-red-600 font-medium">{item.errorMessage || 'Unknown error'}</td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => {
                                // Remove from failed items and add back to draft
                                setFailedItems((prev) => prev.filter((_, i) => i !== idx));
                                setDraftItems((prev) => [...prev, item]);
                              }}
                              className="text-sm text-emerald-600 hover:text-emerald-800 font-medium"
                            >
                              Retry
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-4 text-sm text-red-600">
                  Please fix the errors and try again. Form will reset after you close this popup.
                </p>
              </div>
            ) : null}
          </>
        }
      >
        {/* LEGACY FLOW (single-product allocation) - kept for reference. New create flow uses product-table allocation above. Shown only when editing. */}
        {(editingTransactionId || editingItemId) && false && (
        <div className="mt-6 rounded-3xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Allocation Method</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="allocation-fefo"
                name="allocationMethod"
                value="FEFO"
                checked={formData.allocationMethod === 'FEFO'}
                onChange={() => setAllocationMethod('FEFO')}
                className="h-5 w-5 cursor-pointer border-gray-300 text-emerald-600 focus:ring-2 focus:ring-emerald-200"
              />
              <label htmlFor="allocation-fefo" className="text-sm font-medium text-gray-900 cursor-pointer">
                FEFO (First Expiry, First Out) - Auto Recommended
              </label>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="allocation-manual"
                name="allocationMethod"
                value="MANUAL"
                checked={formData.allocationMethod === 'MANUAL'}
                onChange={() => setAllocationMethod('MANUAL')}
                className="h-5 w-5 cursor-pointer border-gray-300 text-emerald-600 focus:ring-2 focus:ring-emerald-200"
              />
              <label htmlFor="allocation-manual" className="text-sm font-medium text-gray-900 cursor-pointer">
                Manual Selection
              </label>
            </div>

            {formData.allocationMethod === 'FEFO' && (
              <div className="mt-6 space-y-4">
                <button
                  type="button"
                  disabled={remainingToAllocate <= 0}
                  className="h-11 rounded-full border border-emerald-500 bg-white px-6 text-sm font-medium text-emerald-600 transition hover:bg-emerald-50 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={fetchFEFORecommendation}
                >
                  Get FEFO Recommendation {remainingToAllocate <= 0 && '(Dispatch fulfilled)'}
                </button>
                {fefoRecommendation && (
              <div className="mt-4 space-y-4 rounded-2xl border border-gray-200 bg-gray-50 p-6">
                <h4 className="text-base font-semibold text-gray-900">FEFO Recommendation</h4>
                <div className="space-y-2 text-sm text-gray-700">
                  <p>
                    Batches to allocate:{' '}
                    <span className="font-medium">{fefoRecommendation.batches?.length || 0}</span>
                  </p>
                  <p>
                    Total Allocated:{' '}
                    <span className="font-medium">{fefoRecommendation.totalAllocated}</span>
                  </p>
                  {fefoRecommendation.shortfall > 0 && (
                    <p className="text-red-600 font-medium">
                      Shortfall: {fefoRecommendation.shortfall}
                    </p>
                  )}
                </div>
                {fefoRecommendation.batches && fefoRecommendation.batches.length > 0 && (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Batch Number</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Exp Date</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fefoRecommendation.batches.map((b, i) => (
                          <tr key={i} className="border-b border-gray-100">
                            <td className="px-4 py-3 text-sm text-gray-700">{b.batchNumber}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{b.expiryDate}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{b.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <button
                  type="button"
                  disabled={remainingToAllocate <= 0}
                  className="mt-4 h-11 rounded-full border border-emerald-500 bg-emerald-500 px-6 text-sm font-medium text-white transition hover:bg-emerald-600 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={applyFEFORecommendation}
                >
                  Apply Recommendation {remainingToAllocate <= 0 && '(Dispatch fulfilled)'}
                </button>
                {remainingToAllocate <= 0 && (
                  <p className="mt-2 text-sm text-amber-600">Dispatch quantity fulfilled. Remove batches from Applied allocation to add more.</p>
                )}
              </div>
                )}
              </div>
            )}

            {formData.allocationMethod === 'MANUAL' && formData.productId && (
              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    className="h-11 rounded-full border border-emerald-500 bg-emerald-500 px-6 text-sm font-medium text-white transition hover:bg-emerald-600 active:scale-[0.98] flex items-center gap-2"
                    onClick={fetchBatchesForManual}
                  >
                    {showManualBatches ? (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75" />
                        </svg>
                        Close Batches
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                        Add Batch
                      </>
                    )}
                  </button>
                </div>
                {showManualBatches && manualBatches.length === 0 && availableTotal === null && (
                  <p className="text-sm text-gray-500">Loading batches…</p>
                )}
                {showManualBatches && manualBatches.length === 0 && availableTotal === 0 && (
                  <p className="text-sm text-amber-600">No batches available for this product.</p>
                )}
                {showManualBatches && manualBatches.length > 0 && (
                  <div className="mt-4 space-y-4 rounded-2xl border border-gray-200 bg-gray-50 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-base font-semibold text-gray-900">Select Batches</h4>
                      <button
                        type="button"
                        onClick={() => setShowManualBatches(false)}
                        className="text-gray-400 hover:text-gray-600 transition"
                        title="Close"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <p className="text-sm text-gray-600">
                      Available: <strong>{availableTotal}</strong> • Dispatch: <strong>{dispatchQty}</strong> • Remaining to allocate: <strong>{remainingToAllocate}</strong>
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Batch Number</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Exp Date</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Available</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Select Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {manualBatches.map((b, idx) => {
                            const existingBatch = (formData.batchDetails || []).find((bd) => String(bd.batchId) === String(b._id));
                            const alreadyAllocatedInForm = existingBatch ? (existingBatch.quantity || 0) : 0;
                            const othersTotal = manualBatches
                              .filter((_, i) => i !== idx)
                              .reduce((s, x) => {
                                const xExisting = (formData.batchDetails || []).find((bd) => String(bd.batchId) === String(x._id));
                                const xAllocated = xExisting ? (xExisting.quantity || 0) : 0;
                                return s + (manualBatchSelections[x._id] || 0) + xAllocated;
                              }, 0);
                            const remainingByDispatch = Math.max(0, dispatchQty - othersTotal);
                            const batchAvailableQty = b.alreadyAllocated ? (b.quantity - b.alreadyAllocated) : (b.quantity || 0);
                            const maxSelect = Math.min(batchAvailableQty + alreadyAllocatedInForm, remainingByDispatch);
                            const currentSel = manualBatchSelections[b._id] || 0;
                            const isDisabled = remainingToAllocate <= 0 || (availableTotal != null && dispatchQty > availableTotal);
                            return (
                              <tr key={b._id} className="border-b border-gray-100">
                                <td className="px-4 py-3 text-sm text-gray-700">{b.batchNumber}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">
                                  {b.expiryDate ? (typeof b.expiryDate === 'string' ? b.expiryDate : new Date(b.expiryDate).toISOString().slice(0, 10)) : '—'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700">
                                  {batchAvailableQty}
                                  {alreadyAllocatedInForm > 0 && (
                                    <span className="text-xs text-gray-500 ml-1">(+{alreadyAllocatedInForm} allocated)</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    value={currentSel || ''}
                                    onKeyDown={preventOutwardQtyDecimalKeyDown}
                                    onPaste={blockInvalidOutwardQtyPaste}
                                    onChange={(e) => handleManualBatchQtyChange(b._id, e.target.value, maxSelect)}
                                    placeholder="0"
                                    disabled={isDisabled}
                                    className="h-9 w-20 rounded-lg border border-gray-200 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <button
                      type="button"
                      disabled={availableTotal != null && dispatchQty > availableTotal}
                      className="mt-4 h-11 w-full rounded-full border border-emerald-500 bg-emerald-500 px-6 text-sm font-medium text-white transition hover:bg-emerald-600 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={applyManualBatches}
                    >
                      Apply Batch
                    </button>
                    {availableTotal != null && formData.dispatchQuantity > 0 && Number(formData.dispatchQuantity) > availableTotal && (
                      <p className="text-sm font-medium text-red-600">
                        Available in batch <strong>{availableTotal}</strong> dispatch qauntity must be eqaul to {availableTotal} or less.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
        )}
        {!isEditMode && !permissionLoading && hasActiveAccess ? (
          <div
            className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
            role="status"
          >
            <p className="font-medium mb-2 m-0">Approved outward posting date ranges:</p>
            <ul className="list-disc pl-5 space-y-1 m-0">
              {approvedRangeLabels.map((range) => (
                <li key={range.id || `${range.fromYmd}-${range.toYmd}`}>{range.label}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {!isEditMode && !permissionLoading && hasPendingRequest ? (
          <div
            className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            role="status"
          >
            You already have a pending outward backdate access request. Please wait for admin approval.
          </div>
        ) : null}
        {showBackdatePermissionBanner ? (
          <div
            className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex flex-wrap items-center justify-between gap-3"
            role="alert"
          >
            <span>Older outward posting dates require admin approval.</span>
            <button
              type="button"
              onClick={() =>
                openPermissionRequestModal(selectedPostingYmd, { closeCreateForm: true })
              }
              className="form-popup-btn-solid"
            >
              Request Access
            </button>
          </div>
        ) : null}
        {!isEditMode &&
        !permissionLoading &&
        !hasActiveAccess &&
        !hasPendingRequest &&
        !showBackdatePermissionBanner ? (
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={() =>
                openPermissionRequestModal(selectedPostingYmd, { closeCreateForm: true })
              }
              className="form-popup-btn-solid"
            >
              Request Access
            </button>
          </div>
        ) : null}
      </FormPopup>
      <OutwardRequestAccessModal
        open={permissionRequestModalOpen}
        selectedOldDate={
          permissionModalSelectedDate || permissionPostingDateRef.current || ""
        }
        isPending={permissionModalPendingOnly}
        warehouseId={warehouseId}
        onCancel={() => closePermissionRequestModal()}
        onClose={(opts) => closePermissionRequestModal(opts)}
        onSuccess={onPermissionRequestSuccess}
      />
      <OutwardDetailsModal
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewData(null);
          setViewError(null);
        }}
        loading={viewLoading}
        error={viewError}
        data={viewData}
        onRetry={() => viewRowRef.current && openView(viewRowRef.current)}
      />
      {tableLoading ? (
        <SkeletonTable cols={outwardTableColumns.length} rows={8} />
      ) : (
        <Table
          columns={outwardTableColumns}
          data={groupedTransactions}
          loading={false}
          emptyMessage="No outward transactions found"
          onRowClick={(row) => openView(row)}
          getRowClassName={(row) => {
            if (highlightedTransactionId && row.transactionId === highlightedTransactionId) {
              return 'highlight';
            }
            return '';
          }}
        />
      )}
    </div>
  );
}
